import React, { useState, useEffect, useRef, useMemo, useCallback, useLayoutEffect } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import DonationWidgetConfigModal from './components/DonationWidgetConfigModal';
import { createPortal } from 'react-dom';
import DonationBlock from './components/DonationBlock';
import ConfirmStripeDisconnectModal from './components/ConfirmStripeDisconnectModal';
import ProRoute from './components/ProRoute';
import ProLandingPage from './components/ProLandingPage';
import DashboardPro from './components/DashboardPro';
import { NotificationBell, NotificationsPage } from './components/NotificationSystem';
import { BrowserRouter, Routes, Route, Link, Navigate, useNavigate, useParams, Outlet, useLocation, useSearchParams } from 'react-router-dom';
import { auth, db, storage } from './firebase';
import { PayPalScriptProvider } from "@paypal/react-paypal-js";
import { typesenseClient } from './typesenseClient'; 
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendEmailVerification,
  onAuthStateChanged,
  signOut,
  GoogleAuthProvider,
  signInWithPopup,
  sendPasswordResetEmail,
  EmailAuthProvider,
  reauthenticateWithCredential,
  updateEmail,
  deleteUser,
  reauthenticateWithPopup,
} from 'firebase/auth';
import {
  doc,
  getDoc,
  collection,
  query,
  where,
  getDocs,
  addDoc,
  serverTimestamp,
  orderBy,
  limit,
  updateDoc,
  writeBatch,
  Timestamp,
  onSnapshot,
  increment,
  deleteDoc,
  documentId,
  arrayUnion,
  arrayRemove
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { CKEditor } from '@ckeditor/ckeditor5-react';
import BalloonEditor from '@ckeditor/ckeditor5-build-balloon-block';
import DatePicker, { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { enUS } from 'date-fns/locale/en-US';
registerLocale('en-US', enUS);



// ==================================================================================
// 1. HELPER FUNCTION
// ==================================================================================

const useAverageColor = (imageUrl) => {
    // Estado para guardar el color hexadecimal calculado
    const [color, setColor] = useState(null);

    useEffect(() => {
        // Resetea el color si la URL de la imagen cambia
        setColor(null); 
        if (!imageUrl) {
            setColor('#1f2937'); // Fallback si no hay imagen
            return;
        }

        const img = new Image();
        img.crossOrigin = "anonymous"; // ¡Clave! Necesario por el CORS que ya configuramos
        img.src = imageUrl;

        img.onload = () => {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            
            // Reducimos el tamaño para un muestreo más rápido
            const w = canvas.width = 20; // Muestrear en un canvas de 20x20
            const h = canvas.height = 20;
            ctx.drawImage(img, 0, 0, w, h);

            let data;
            try {
                data = ctx.getImageData(0, 0, w, h).data;
            } catch (e) {
                console.error("Error al leer datos de imagen (posiblemente CORS residual):", e);
                setColor('#1f2937'); // Fallback si falla
                return;
            }

            let r = 0, g = 0, b = 0;
            let count = 0;

            // Recorremos los píxeles (de 4 en 4: R, G, B, A)
            for (let i = 0; i < data.length; i += 4) {
                // Ignoramos píxeles casi transparentes (si los hubiera)
                if (data[i + 3] < 255 / 10) continue; 
                
                r += data[i];
                g += data[i + 1];
                b += data[i + 2];
                count++;
            }

            if (count > 0) {
                r = Math.floor(r / count);
                g = Math.floor(g / count);
                b = Math.floor(b / count);

                // Convertir a formato hexadecimal
                const toHex = c => ('0' + c.toString(16)).slice(-2);
                setColor(`#${toHex(r)}${toHex(g)}${toHex(b)}`);
            } else {
                setColor('#1f2937'); // Fallback si no se contaron píxeles
            }
        };

        img.onerror = () => {
            console.error("Error al cargar la imagen para average color.");
            setColor('#1f2937'); // Fallback si la imagen falla al cargar
        };
        
    }, [imageUrl]); // Se ejecuta de nuevo si la URL de la imagen cambia

    return color;
};

const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'hace un momento';

    let date;

    // Verificamos si el timestamp es un objeto de Firestore o un número de TypeSense
    if (timestamp.toDate) {
      // Es un objeto Timestamp de Firestore, usamos el método .toDate()
      date = timestamp.toDate();
    } else if (typeof timestamp === 'number') {
      // Es un número (timestamp de Unix en segundos), lo convertimos a milisegundos
      date = new Date(timestamp * 1000);
    } else {
      // Si llega en otro formato inesperado, evitamos que la app se rompa
      return 'fecha inválida';
    }

    const now = new Date();
    const seconds = Math.floor((now - date) / 1000);

    if (seconds < 60) return `hace ${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `hace ${minutes}m`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `hace ${hours}h`;
    const days = Math.floor(hours / 24);
    if (days < 30) return `hace ${days}d`;
    const months = Math.floor(days / 30);
    if (months < 12) return `hace ${months}mes`;
    const years = Math.floor(months / 12);
    return `hace ${years}a`;
};

const spanishStopWords = [
  'de', 'la', 'el', 'y', 'un', 'una', 'con', 'por', 'en', 'los', 'las', 'a', 'ante', 'bajo', 'cabe', 'contra',
  'desde', 'durante', 'entre', 'hacia', 'hasta', 'mediante', 'para', 'segun', 'sin', 'so', 'sobre', 'tras',
  'versus', 'via', 'mi', 'tu', 'su', 'nuestro', 'vuestro', 'que', 'como', 'cuando', 'donde', 'quien', 'cual',
  'cuyo', 'es', 'son', 'del', 'o', 'e', 'u', 'pero', 'mas', 'sino', 'aunque', 'si'
];

const initialPayPalOptions = {
    "client-id": process.env.REACT_APP_PAYPAL_CLIENT_ID,
    "intent": "capture", // Capturar el pago inmediatamente
    "currency": "USD",   // O la moneda principal que usarás
    "data-merchant-id": "*", // <-- ¡VITAL! Habilita el modo multiparty/marketplace
};

const generateKeywords = (text) => {
  if (!text) return [];
  const words = text.toLowerCase().replace(/[.,¡!¿?]/g, '').split(/\s+/);
  return words.filter(word => !spanishStopWords.includes(word) && word.length > 2);
};

// ==================================================================================
// HELPER: SISTEMA DE RECOMENDACIONES (Fase 1)
// ==================================================================================

const getTopUserTags = (userProfile) => {
    if (!userProfile) return ['romance', 'fantasía', 'aventura']; // Default absoluto

    // 1. Extraer gustos explícitos (de los Likes)
    // tagInterests es un mapa: { "romance": 5, "sci_fi": 2 }
    const explicitTags = userProfile.tagInterests 
        ? Object.entries(userProfile.tagInterests)
            .sort(([, countA], [, countB]) => countB - countA) // Ordenar de mayor a menor
            .map(([tag]) => tag) // Quedarnos solo con el nombre del tag
        : [];

    // 2. Extraer intereses declarados (del Onboarding)
    const onboardingInterests = userProfile.interests || [];

    // 3. Mezclar estrategias (Prioridad: Likes > Onboarding > Defaults)
    // Usamos un Set para evitar duplicados (ej: si le gusta 'Romance' y también lo seleccionó en onboarding)
    const combinedTags = new Set([
        ...explicitTags, 
        ...onboardingInterests, 
        'romance', 'fantasía', 'suspenso' // Fallbacks genéricos por si el usuario es muy nuevo
    ]);

    // Devolvemos solo los Top 3 o 4 para generar los carriles
    return Array.from(combinedTags).slice(0, 4);
};

// ==================================================================================
// 2. LAYOUT & NAVBAR COMPONENTS
// ==================================================================================
const Navbar = ({ userProfile }) => {
    const handleLogout = async () => {
        await signOut(auth);
        navigate('/');
    };
    const [searchTerm, setSearchTerm] = useState('');
    const navigate = useNavigate();

    // --- ¡AÑADIDO! ---
    // 1. Estado para la categoría de búsqueda
    const [searchCategory, setSearchCategory] = useState('stories'); 
    // --- FIN ---

    // --- ¡MODIFICADO! ---
    // 2. handleSearchSubmit ahora envía la categoría
    const handleSearchSubmit = (e) => {
        e.preventDefault();
        if (searchTerm.trim()) {
            navigate(`/search?q=${encodeURIComponent(searchTerm.trim())}&type=${searchCategory}`);
            setSearchTerm('');
        }
    };
    // --- FIN ---

    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const menuRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (menuRef.current && !menuRef.current.contains(event.target)) {
                setIsMenuOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [menuRef]);

    return (
    <nav className="sticky top-0 z-50 bg-gray-800 border-b border-gray-700 shadow-lg pt-4 pb-4 NAVBAR">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 NAVBAR_DIV1">
            <div className="flex items-center justify-between NAVBAR_DIV2">

                <div className="flex-shrink-0 NAVBAR_DIV3">
                    <Link to="/" className="text-2xl font-bold text-white transition-colors LOGO">Type<b>fiction</b></Link>
                </div>
                
                {/* --- ¡CORRECCIÓN DE BÚSQUEDA INCRUSTADA AQUÍ! --- */}
                <div className="flex-1 flex items-center mx-4 sm:mx-6">
                    <div className="w-full">
                        <form onSubmit={handleSearchSubmit} className="flex w-full items-center gap-2 bg-gray-700 rounded-md p-1.5 SEARCH_DIV">
                            <select 
                                value={searchCategory}
                                onChange={(e) => setSearchCategory(e.target.value)}
                                className="py-1 pl-3 pr-7 border border-transparent bg-gray-800 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 hover:bg-gray-900 sm:text-sm SEARCH_CATEGORY"
                                aria-label="Categoría de búsqueda"
                            >
                                <option value="stories">Historias</option>
                                <option value="users">Autores</option>
                                <option value="chapters">Citas</option>
                            </select>

                            <input 
                                id="search" 
                                name="search" 
                                className="block w-full flex-grow bg-transparent border-none py-1 pl-2 text-sm text-white placeholder-gray-400 focus:outline-none focus:ring-0 sm:text-sm SEARCH_BOX" 
                                placeholder={`Buscar en ${searchCategory}...`} 
                                type="search" 
                                value={searchTerm} 
                                onChange={(e) => setSearchTerm(e.target.value)} 
                            />
                        </form>
                    </div>
                </div>

                <div className="flex items-center space-x-4">

                    {/* --- BADGE PRO (ESTILO ETIQUETA) --- */}
                    {userProfile?.isPro ? (
                        // CASO A: USUARIO ES PRO -> Lleva al Dashboard
                        <Link 
                            to="/dashboard" 
                            className="group relative"
                            title="Ir a mi Dashboard PRO"
                        >
                            {/* El Badge Azul #44EFFF */}
                            <span className="bg-gradient-to-r from-[#44EFFF] to-blue-500 text-black text-xs font-black px-3 py-1 rounded-md uppercase tracking-wider transform -rotate-6 hover:rotate-0 transition-transform duration-200 shadow-[0_0_15px_rgba(68,239,255,0.4)] block">
                                PRO
                            </span>
                        </Link>
                    ) : (
                        // CASO B: NO ES PRO -> Lleva a Landing + Tooltip
                        <Link 
                            to="/pro" 
                            className="relative group"
                        >
                            {/* Badge Gris (Desactivado/Invitación) */}
                            <span className="bg-gray-700 text-gray-400 border border-gray-600 text-xs font-black px-3 py-1 rounded-md uppercase tracking-wider transform -rotate-6 group-hover:rotate-0 group-hover:bg-[#44EFFF] group-hover:text-black group-hover:border-[#44EFFF] transition-all duration-300 block">
                                PRO
                            </span>
                            
                            {/* Tooltip Flotante */}
                            <div className="absolute top-full mt-3 left-1/2 -translate-x-1/2 w-max opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none z-50">
                                <div className="bg-[#44EFFF] text-black text-xs font-bold px-2 py-1 rounded shadow-[0_0_20px_rgba(68,239,255,0.6)] relative">
                                    ¡SUSCRÍBETE YA!
                                    {/* Triangulito */}
                                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 border-4 border-transparent border-b-[#44EFFF]"></div>
                                </div>
                            </div>
                        </Link>
                    )}

                    <Link to="/write" className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-gray-700 BOTON_WRITE">Empezar a Escribir</Link>

                    {/* --- INSERTAR AQUÍ EL COMPONENTE DE NOTIFICACIONES --- */}
                        {userProfile && (
                            <div className="mx-2">
                                <NotificationBell userProfile={userProfile} />
                            </div>
                        )}
                        {/* ----------------------------------------------------- */}

                    <div className="relative " ref={menuRef}>
                        {userProfile ? (
                            <button 
                                onClick={() => setIsMenuOpen(!isMenuOpen)} 
                                title="Mi Perfil" 
                                className="block rounded-full transition-opacity hover:opacity-80 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-800 focus:ring-white"
                            >
                                <img 
                                    src={userProfile.avatarURL || `https://placehold.co/40x40/374151/E5E7EB?text=${userProfile.displayName ? userProfile.displayName.charAt(0) : 'U'}`} 
                                    alt="User Avatar" 
                                    className="h-10 w-10 rounded-full object-cover"
                                />
                            </button>
                        ) : (
                            <div className="h-10 w-10 rounded-full bg-gray-700 animate-pulse"></div>
                        )}

                        {isMenuOpen && userProfile && (
                            <div className="origin-top-right absolute right-0 mt-2 w-[212px] rounded-md shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none MENU_CUENTA">
                                <div className="py-1" role="menu" aria-orientation="vertical" aria-labelledby="options-menu">
                                    <div className="px-4 py-3">
                                        <p className="text-sm text-white">Hola de nuevo,</p>
                                        <p className="text-sm font-medium text-white truncate">@{userProfile.username}</p>
                                    </div>
                                    <Link to={`/profile/${userProfile.username}`} onClick={() => setIsMenuOpen(false)} className="block px-4 py-2 text-sm text-gray-300 hover:bg-gray-800 hover:text-white w-full text-left" role="menuitem">
                                        Mi Perfil
                                    </Link>
                                    <Link to="/settings" onClick={() => setIsMenuOpen(false)} className="block px-4 py-2 text-sm text-gray-300 hover:bg-gray-800 hover:text-white w-full text-left" role="menuitem">
                                        Ajustes de la Cuenta
                                    </Link>
                                    <div className="border-t border-gray-600 my-1"></div>
                                    <Link to="/help" onClick={() => setIsMenuOpen(false)} className="block px-4 py-2 text-sm text-gray-300 hover:bg-gray-800 hover:text-white w-full text-left" role="menuitem">
                                        Ayuda
                                    </Link>
                                    <Link to="/feedback" onClick={() => setIsMenuOpen(false)} className="block px-4 py-2 text-sm text-gray-300 hover:bg-gray-800 hover:text-white w-full text-left" role="menuitem">
                                        Enviar Feedback
                                    </Link>
                                    <Link to="/contact" onClick={() => setIsMenuOpen(false)} className="block px-4 py-2 text-sm text-gray-300 hover:bg-gray-800 hover:text-white w-full text-left" role="menuitem">
                                        Contáctanos
                                    </Link>
                                    <div className="border-t border-gray-600 my-1"></div>
                                    <button onClick={handleLogout} className="block w-full text-left px-4 py-2 text-sm text-red-400 hover:bg-gray-800 hover:text-red-300" role="menuitem">
                                        Logout
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    </nav>
);
};

const CollectionPage = ({ loggedInUserProfile }) => {
    const { collectionId } = useParams();
    const navigate = useNavigate();
    
    const [collectionData, setCollectionData] = useState(null);
    const [stories, setStories] = useState([]);
    
    // --- NUEVO ESTADO: Mapa de autores ---
    const [authorsMap, setAuthorsMap] = useState({}); 
    // -------------------------------------

    const [ownerProfile, setOwnerProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    
    // Estados de Edición
    const [isEditing, setIsEditing] = useState(false);
    const [editedTitle, setEditedTitle] = useState('');
    const [editedDescription, setEditedDescription] = useState('');
    
    // Estado para la visibilidad
    const [editedVisibility, setEditedVisibility] = useState('private'); 

    const [isSaving, setIsSaving] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isUploadingImage, setIsUploadingImage] = useState(false);

    useEffect(() => {
        const fetchCollection = async () => {
            setLoading(true);
            try {
                const docRef = doc(db, 'collections', collectionId);
                const unsubscribe = onSnapshot(docRef, async (docSnap) => {
                    if (!docSnap.exists()) {
                        setError("Esta colección no existe.");
                        setLoading(false);
                        return;
                    }

                    const data = { id: docSnap.id, ...docSnap.data() };
                    setCollectionData(data);
                    
                    // Cargar datos iniciales en los estados de edición
                    setEditedTitle(data.title);
                    setEditedDescription(data.description || '');
                    setEditedVisibility(data.visibility || 'private');

                    if (data.ownerId) {
                        const userSnap = await getDoc(doc(db, 'users', data.ownerId));
                        if (userSnap.exists()) setOwnerProfile(userSnap.data());
                    }

                    if (data.storyIds && data.storyIds.length > 0) {
                        const chunks = [];
                        const ids = data.storyIds;
                        for (let i = 0; i < ids.length; i += 10) {
                            chunks.push(ids.slice(i, i + 10));
                        }

                        const storiesPromises = chunks.map(chunk => 
                            getDocs(query(collection(db, 'stories'), where(documentId(), 'in', chunk)))
                        );

                        const snapshots = await Promise.all(storiesPromises);
                        let fetchedStories = [];
                        snapshots.forEach(snap => {
                            snap.forEach(doc => fetchedStories.push({ id: doc.id, ...doc.data() }));
                        });

                        // Ordenar historias según el array de IDs original
                        const orderedStories = data.storyIds.map(id => 
                            fetchedStories.find(s => s.id === id)
                        ).filter(Boolean);

                        setStories(orderedStories);

                        // --- NUEVA LÓGICA: CARGAR PERFILES DE AUTORES ---
                        // Extraemos los IDs únicos de los autores de estas historias
                        const authorIds = [...new Set(orderedStories.map(s => s.authorId))];
                        
                        if (authorIds.length > 0) {
                            const authorChunks = [];
                            for (let i = 0; i < authorIds.length; i += 10) {
                                authorChunks.push(authorIds.slice(i, i + 10));
                            }

                            const authorPromises = authorChunks.map(chunk => 
                                getDocs(query(collection(db, 'users'), where(documentId(), 'in', chunk)))
                            );

                            const authorSnapshots = await Promise.all(authorPromises);
                            const newAuthorsMap = {};
                            
                            authorSnapshots.forEach(snap => {
                                snap.forEach(doc => {
                                    newAuthorsMap[doc.id] = doc.data();
                                });
                            });

                            setAuthorsMap(newAuthorsMap);
                        }
                        // ------------------------------------------------

                    } else {
                        setStories([]);
                    }
                    setLoading(false);
                }, (err) => {
                    console.error(err);
                    setError("Error al cargar la colección.");
                    setLoading(false);
                });

                return () => unsubscribe();
            } catch (err) {
                console.error(err);
                setError(err.message);
                setLoading(false);
            }
        };

        fetchCollection();
    }, [collectionId]);

    // --- LOGICA DE SUBIDA DE IMAGEN ---
    const handleCoverUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        if (!file.type.startsWith('image/')) {
            alert("Por favor sube un archivo de imagen válido.");
            return;
        }
        setIsUploadingImage(true);
        try {
            const imageRef = ref(storage, `covers/collections/${collectionId}/${Date.now()}_${file.name}`);
            const snapshot = await uploadBytes(imageRef, file);
            const downloadURL = await getDownloadURL(snapshot.ref);
            await updateDoc(doc(db, 'collections', collectionId), {
                coverImageURL: downloadURL,
                lastUpdatedAt: serverTimestamp()
            });
        } catch (error) {
            console.error("Error uploading collection cover:", error);
            alert("Error al subir la imagen.");
        } finally {
            setIsUploadingImage(false);
        }
    };

    const handleSaveChanges = async () => {
        if (!editedTitle.trim()) return alert("El título no puede estar vacío");
        setIsSaving(true);
        try {
            await updateDoc(doc(db, 'collections', collectionId), {
                title: editedTitle,
                description: editedDescription,
                visibility: editedVisibility, 
                lastUpdatedAt: serverTimestamp()
            });
            setIsEditing(false);
        } catch (e) {
            console.error(e);
            alert("Error al guardar cambios");
        } finally {
            setIsSaving(false);
        }
    };

    const handleRemoveStory = async (storyId) => {
        if (!window.confirm("¿Quitar esta historia de la colección?")) return;
        try {
            await updateDoc(doc(db, 'collections', collectionId), {
                storyIds: arrayRemove(storyId),
                storyCount: increment(-1)
            });
        } catch (e) {
            console.error(e);
            alert("Error al quitar la historia");
        }
    };

    const handleDeleteCollection = async () => {
        if (!window.confirm("¿Estás seguro de eliminar esta colección permanentemente?")) return;
        setIsDeleting(true);
        try {
            await deleteDoc(doc(db, 'collections', collectionId));
            navigate(`/profile/${loggedInUserProfile?.username}`);
        } catch (e) {
            console.error(e);
            alert("Error al eliminar la colección");
            setIsDeleting(false);
        }
    };

    if (loading) return <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center">Cargando...</div>;
    if (error) return <div className="min-h-screen bg-gray-900 text-red-400 flex items-center justify-center">{error}</div>;
    if (!collectionData) return null;

    const isOwner = loggedInUserProfile && loggedInUserProfile.uid === collectionData.ownerId;
    
    // Validar si puedo verla (Soy dueño O es pública)
    if (collectionData.visibility === 'private' && !isOwner) {
        return (
            <div className="min-h-screen bg-gray-900 text-white flex flex-col items-center justify-center">
                <h1 className="text-4xl mb-4">🔒</h1>
                <p className="text-xl text-gray-400">Esta colección es privada.</p>
                <Link to="/" className="mt-4 text-indigo-400 hover:underline">Volver al inicio</Link>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-900 text-white pb-20">
            <div className="bg-gradient-to-b from-gray-800 to-gray-900 p-8 md:p-12">
                <div className="max-w-6xl mx-auto flex flex-col md:flex-row gap-8 items-end">
                    
                    {/* PORTADA */}
                    <div className="w-52 h-52 md:w-64 md:h-64 bg-gray-700 shadow-2xl rounded-lg overflow-hidden flex-shrink-0 relative group">
                        {collectionData.coverImageURL ? (
                            <img src={collectionData.coverImageURL} alt="Cover" className="w-full h-full object-cover" />
                        ) : (
                            stories.length > 0 && stories[0].coverImageURL ? (
                                <img src={stories[0].coverImageURL} alt="Cover" className="w-full h-full object-cover" />
                            ) : (
                                <div className="w-full h-full flex items-center justify-center text-gray-500">
                                    <svg className="w-20 h-20" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" /></svg>
                                </div>
                            )
                        )}

                        {isEditing && (
                            <label className="absolute inset-0 bg-black/60 flex flex-col items-center justify-center cursor-pointer opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                                {isUploadingImage ? (
                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
                                ) : (
                                    <>
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-white mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                                        <span className="text-white font-bold text-sm uppercase tracking-wider">Cambiar Foto</span>
                                        <input type="file" className="hidden" accept="image/*" onChange={handleCoverUpload} disabled={isUploadingImage}/>
                                    </>
                                )}
                            </label>
                        )}
                    </div>

                    <div className="flex-grow w-full">
                        
                        {/* Selector de visibilidad en Modo Edición */}
                        {isEditing ? (
                            <div className="mb-4 flex items-center gap-3">
                                <span className="text-sm text-gray-400 uppercase tracking-wider font-bold">Visibilidad:</span>
                                <button 
                                    onClick={() => setEditedVisibility(prev => prev === 'public' ? 'private' : 'public')}
                                    className={`px-3 py-1 rounded-full text-xs font-bold border transition-all flex items-center gap-2 ${
                                        editedVisibility === 'public' 
                                            ? 'bg-green-600/20 border-green-500 text-green-400' 
                                            : 'bg-gray-700 border-gray-500 text-gray-300'
                                    }`}
                                >
                                    {editedVisibility === 'public' ? (
                                        <>
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                                            Pública (Visible para todos)
                                        </>
                                    ) : (
                                        <>
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>
                                            Privada (Solo tú)
                                        </>
                                    )}
                                </button>
                            </div>
                        ) : (
                            <div className="uppercase text-xs font-bold text-indigo-400 mb-2 tracking-wider flex items-center gap-2">
                                {collectionData.isDefaultLiked && <span className="text-pink-500">❤️ Favoritos</span>}
                                {collectionData.visibility === 'private' ? (
                                    <span className="flex items-center gap-1 text-gray-400"><svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg> Privada</span>
                                ) : (
                                    <span className="flex items-center gap-1 text-green-400"><svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg> Pública</span>
                                )}
                            </div>
                        )}

                        {/* TITULO */}
                        {isEditing ? (
                            <input 
                                type="text" 
                                value={editedTitle}
                                onChange={(e) => setEditedTitle(e.target.value)}
                                disabled={collectionData.isDefaultLiked} 
                                className={`text-4xl md:text-6xl font-bold bg-transparent border-b border-gray-500 focus:border-indigo-500 focus:outline-none w-full mb-4 text-white placeholder-gray-600 ${collectionData.isDefaultLiked ? 'opacity-50 cursor-not-allowed' : ''}`}
                                placeholder="Título de la colección"
                                title={collectionData.isDefaultLiked ? "No puedes cambiar el nombre de la colección de Favoritos" : ""}
                            />
                        ) : (
                            <h1 className="text-4xl md:text-6xl font-bold mb-4 leading-tight">{collectionData.title}</h1>
                        )}

                        {/* DESCRIPCIÓN */}
                        {isEditing ? (
                            <textarea 
                                value={editedDescription}
                                onChange={(e) => setEditedDescription(e.target.value)}
                                className="w-full bg-gray-800/50 border border-gray-600 rounded p-2 text-gray-300 focus:outline-none focus:border-indigo-500 mb-4"
                                rows="2"
                                placeholder="Añade una descripción..."
                            />
                        ) : (
                            collectionData.description && <p className="text-gray-400 text-lg mb-6 max-w-2xl">{collectionData.description}</p>
                        )}

                        <div className="flex flex-wrap items-center gap-4 text-sm text-gray-400">
                            {ownerProfile && (
                                <div className="flex items-center gap-2">
                                    <img src={ownerProfile.avatarURL || `https://placehold.co/30x30/333/fff?text=${ownerProfile.displayName[0]}`} className="w-6 h-6 rounded-full" alt=""/>
                                    <Link to={`/profile/${ownerProfile.username}`} className="font-bold text-white hover:underline">{ownerProfile.displayName}</Link>
                                </div>
                            )}
                            <span>• {stories.length} historias</span>
                            
                            {/* BOTONES DE ACCIÓN */}
                            {isOwner && (
                                <div className="ml-auto flex gap-3">
                                    {isEditing ? (
                                        <>
                                            {!collectionData.isDefaultLiked && ( 
                                                <button onClick={handleDeleteCollection} disabled={isDeleting} className="px-4 py-2 bg-red-600/20 text-red-400 hover:bg-red-600 hover:text-white rounded font-bold transition-colors">
                                                    {isDeleting ? 'Eliminando...' : 'Eliminar'}
                                                </button>
                                            )}
                                            <button onClick={() => setIsEditing(false)} className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded font-bold text-white transition-colors">
                                                Cancelar
                                            </button>
                                            <button onClick={handleSaveChanges} disabled={isSaving} className="px-6 py-2 bg-indigo-600 hover:bg-indigo-500 rounded font-bold text-white transition-colors shadow-lg">
                                                {isSaving ? 'Guardando...' : 'Guardar'}
                                            </button>
                                        </>
                                    ) : (
                                        <button onClick={() => setIsEditing(true)} className="px-4 py-2 border border-gray-500 hover:border-white text-gray-300 hover:text-white rounded font-bold transition-colors uppercase text-xs tracking-widest">
                                            Editar
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-6xl mx-auto p-4 sm:p-8">
                {stories.length === 0 ? (
                    <div className="text-center py-20 bg-gray-800/50 rounded-xl border border-gray-700 border-dashed">
                        <p className="text-gray-500 text-lg">Esta colección está vacía.</p>
                        {isOwner && <p className="text-gray-600 text-sm mt-2">Ve a leer historias y usa el botón de marcador para agregarlas.</p>}
                    </div>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                        {stories.map((story) => (
                            <div key={story.id} className="relative group">
                                {/* PASAMOS LA INFORMACIÓN DEL AUTOR AL STORY CARD */}
                                <StoryCard 
                                    story={story} 
                                    authorProfile={authorsMap[story.authorId]} // <-- ¡AQUÍ ESTÁ LA MAGIA!
                                />
                                {isEditing && (
                                    <button 
                                        onClick={(e) => {
                                            e.preventDefault(); 
                                            handleRemoveStory(story.id);
                                        }}
                                        className="absolute -top-3 -right-3 bg-red-600 text-white w-8 h-8 rounded-full shadow-lg flex items-center justify-center hover:bg-red-700 transform hover:scale-110 transition-all z-20"
                                        title="Quitar de la colección"
                                    >
                                        ✕
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};



const AppLayout = ({ userProfile }) => {
    return ( <div><Navbar userProfile={userProfile} /><main><Outlet /></main></div> );
};


// ==================================================================================
// 3. SUB-COMPONENTS (Modals, Comments, etc.)
// ==================================================================================

const VerificationGuard = ({ children, user, placement = "top" }) => {
    const [showTooltip, setShowTooltip] = useState(false);
    const [isSending, setIsSending] = useState(false);
    const [feedback, setFeedback] = useState(null); // null, 'success', 'error'

    // Verificamos si el usuario necesita verificación
    // Nota: Google Auth pone emailVerified en true automáticamente, así que esto cubre ambos casos.
    const needsVerification = user && !user.emailVerified;

    const handleResendEmail = async (e) => {
        e.stopPropagation(); // Evitar que el clic se propague
        e.preventDefault();
        if (isSending) return;

        setIsSending(true);
        try {
            await sendEmailVerification(user);
            setFeedback('success');
            setTimeout(() => setShowTooltip(false), 3000); // Cerrar después de 3s
        } catch (error) {
            console.error("Error sending verification:", error);
            setFeedback('error');
        } finally {
            setIsSending(false);
        }
    };

    // Si el usuario está verificado o no hay usuario (visitante), renderizamos el hijo normal
    // (Si es visitante, tu lógica existente de login lo manejará, aquí solo nos importa la verificación)
    if (!needsVerification) {
        return children;
    }

    return (
        <div 
            className="relative inline-block"
            onMouseEnter={() => setShowTooltip(true)}
            onMouseLeave={() => {
                if(!isSending) setShowTooltip(false); // Mantenemos abierto si está enviando
            }}
        >
            {/* Renderizamos el hijo pero desactivamos sus eventos de puntero para que no reciba clics */}
            <div className="pointer-events-none opacity-50 cursor-not-allowed">
                {children}
            </div>

            {/* LA BURBUJA FLOTANTE */}
            {showTooltip && (
                <div className={`absolute z-50 w-64 p-4 bg-gray-800 border border-gray-600 rounded-lg shadow-2xl text-sm text-white transition-opacity duration-200 ${
                    placement === 'top' ? 'bottom-full mb-2 left-1/2 -translate-x-1/2' : 'top-full mt-2 left-1/2 -translate-x-1/2'
                }`}>
                    <div className="text-center">
                        <p className="font-bold text-yellow-400 mb-1">⚠️ Verificación Necesaria</p>
                        <p className="text-gray-300 mb-3 leading-tight">
                            Debes verificar tu correo electrónico para realizar esta acción.
                        </p>
                        
                        {feedback === 'success' ? (
                            <p className="text-green-400 font-bold animate-pulse">¡Correo enviado!</p>
                        ) : feedback === 'error' ? (
                            <p className="text-red-400">Error. Intenta más tarde.</p>
                        ) : (
                            <button 
                                onClick={handleResendEmail}
                                disabled={isSending}
                                className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-500 text-white rounded text-xs font-bold transition-colors w-full"
                            >
                                {isSending ? 'Enviando...' : 'Reenviar correo de verificación'}
                            </button>
                        )}
                    </div>
                    
                    {/* Triangulito decorativo */}
                    <div className={`absolute left-1/2 -translate-x-1/2 border-8 border-transparent ${
                        placement === 'top' ? 'top-full border-t-gray-600' : 'bottom-full border-b-gray-600'
                    }`}></div>
                </div>
            )}
        </div>
    );
};

const ConfirmCommentDeleteModal = ({ isOpen, onConfirm, onCancel }) => {
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-[120]">
            <div className="bg-gray-800 rounded-lg shadow-xl w-full max-w-md p-6 text-white relative">
                <h2 className="text-xl font-bold mb-4">Confirmar Eliminación</h2>
                <p className="text-gray-300 mb-6">¿Estás seguro de que quieres borrar este comentario? Esta acción no se puede deshacer.</p>
                <div className="flex justify-end space-x-4">
                    <button 
                        onClick={onCancel} 
                        className="px-6 py-2 font-bold text-gray-300 bg-gray-600 rounded-md hover:bg-gray-500"
                    >
                        Cancelar
                    </button>
                    <button 
                        onClick={onConfirm} 
                        className="px-6 py-2 font-bold text-white bg-red-600 rounded-md hover:bg-red-700"
                    >
                        Eliminar
                    </button>
                </div>
            </div>
        </div>
    );
};

const ConfirmDiscardModal = ({ isOpen, onConfirm, onCancel, isEditMode }) => {
    if (!isOpen) return null;

    const message = isEditMode
        ? "¿Estás seguro de que quieres descartar los cambios? El contenido volverá a su estado original guardado y se perderán todas las modificaciones locales."
        : "¿Estás seguro de que quieres empezar de nuevo? Todo el progreso no guardado en este borrador se perderá permanentemente.";

    return (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-[120]">
            <div className="bg-gray-800 rounded-lg shadow-xl w-full max-w-md p-6 text-white relative">
                <h2 className="text-2xl font-bold mb-4">Confirmar Acción</h2>
                <p className="text-gray-300 mb-6">{message}</p>
                <div className="flex justify-end space-x-4">
                    <button onClick={onCancel} className="px-6 py-2 font-bold text-gray-300 bg-gray-600 rounded-md hover:bg-gray-500">
                        Cancelar
                    </button>
                    <button onClick={onConfirm} className="px-6 py-2 font-bold text-white bg-red-600 rounded-md hover:bg-red-700">
                        {isEditMode ? 'Descartar Cambios' : 'Descartar Borrador'}
                    </button>
                </div>
            </div>
        </div>
    );
};

const ConfirmDeleteModal = ({ isOpen, onConfirm, onCancel, itemType, itemName }) => {
    if (!isOpen) return null;

    const message = `¿Estás seguro de que quieres eliminar permanentemente ${itemType} "${itemName}"? Esta acción no se puede deshacer y todo su contenido asociado se perderá.`;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-[120]">
            <div className="bg-gray-800 rounded-lg shadow-xl w-full max-w-md p-6 text-white relative">
                <h2 className="text-2xl font-bold mb-4">Confirmar Eliminación</h2>
                <p className="text-gray-300 mb-6">{message}</p>
                <div className="flex justify-end space-x-4">
                    <button onClick={onCancel} className="px-6 py-2 font-bold text-gray-300 bg-gray-600 rounded-md hover:bg-gray-500">
                        Cancelar
                    </button>
                    <button onClick={onConfirm} className="px-6 py-2 font-bold text-white bg-red-600 rounded-md hover:bg-red-700">
                        Eliminar Permanentemente
                    </button>
                </div>
            </div>
        </div>
    );
};

const ProfileComments = ({ profileOwnerId, loggedInUserProfile }) => {
    // 1. NUEVO ESTADO para guardar los perfiles de los comentaristas
    const [userProfilesMap, setUserProfilesMap] = useState({});

    const authUser = auth.currentUser;

    // (Estados existentes)
    const [comments, setComments] = useState([]);
    const [newComment, setNewComment] = useState('');
    const [loading, setLoading] = useState(true);
    const [isPosting, setIsPosting] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [commentToDeleteId, setCommentToDeleteId] = useState(null);

    // 2. useEffect MODIFICADO: Ahora también busca los perfiles
    useEffect(() => {
        const q = query(collection(db, "profileComments"), where("profileOwnerId", "==", profileOwnerId), orderBy("createdAt", "desc"));
        
        const unsubscribe = onSnapshot(q, async (querySnapshot) => {
            const fetchedComments = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setComments(fetchedComments);
            setLoading(false);

            // --- INICIO DE LÓGICA DE PERFILES ---
            if (fetchedComments.length > 0) {
                // 2a. Obtener todos los IDs de comentaristas únicos
                const commenterIds = [...new Set(fetchedComments.map(c => c.commenterId))];

                // 2b. Función para buscar perfiles en lotes (chunks)
                const fetchProfiles = async (ids) => {
                    try {
                        const chunks = [];
                        // Separar en lotes de 30 (límite de Firestore 'in')
                        for (let i = 0; i < ids.length; i += 30) {
                            chunks.push(ids.slice(i, i + 30));
                        }
                        
                        const profilesMap = {};
                        const promises = chunks.map(chunk =>
                            getDocs(query(collection(db, 'users'), where(documentId(), 'in', chunk)))
                        );
                        
                        const snapshots = await Promise.all(promises);
                        
                        snapshots.forEach(snapshot => {
                            snapshot.forEach(doc => {
                                profilesMap[doc.id] = doc.data();
                            });
                        });

                        // 2c. Guardar el mapa de perfiles en el estado
                        setUserProfilesMap(profilesMap);

                    } catch (e) {
                        console.error("Error fetching commenter profiles:", e);
                    }
                };
                
                // 2d. Ejecutar la búsqueda
                await fetchProfiles(commenterIds);

            } else {
                setUserProfilesMap({}); // Limpia el mapa si no hay comentarios
            }
            // --- FIN DE LÓGICA DE PERFILES ---

        });
        return () => unsubscribe();
    }, [profileOwnerId]); // Solo depende del ID del dueño del perfil

    // 3. handlePostComment MODIFICADO: Ya no guarda datos mutables
    const handlePostComment = async (e) => {
        e.preventDefault();
        if (newComment.trim() === '' || !loggedInUserProfile || isPosting) return;
        setIsPosting(true);
        try {
            await addDoc(collection(db, "profileComments"), {
                profileOwnerId,
                commenterId: loggedInUserProfile.uid, // <-- ¡BIEN!
                commentText: newComment,
                createdAt: serverTimestamp(),
                // commenterUsername: loggedInUserProfile.username,     <-- BORRADO 
                // commenterDisplayName: loggedInUserProfile.displayName, <-- BORRADO
                // commenterAvatarURL: loggedInUserProfile.avatarURL || '', <-- BORRADO
            });
            setNewComment('');
        } catch (error) {
            console.error("Error posting comment:", error);
            alert("Failed to post comment. Check Firestore security rules for 'profileComments'.");
        } finally {
            setIsPosting(false);
        }
    };

    // (Lógica de borrado sin cambios)
    const handleOpenDeleteModal = (commentId) => {
        setCommentToDeleteId(commentId);
        setIsDeleteModalOpen(true);
    };

    const confirmDeleteComment = async () => {
        if (!commentToDeleteId) return;
        try {
            await deleteDoc(doc(db, "profileComments", commentToDeleteId));
        } catch (error) {
            console.error("Error deleting comment:", error);
            alert("Failed to delete comment.");
        } finally {
            setIsDeleteModalOpen(false);
            setCommentToDeleteId(null);
        }
    };

    const handleCloseDeleteModal = () => {
        setIsDeleteModalOpen(false);
        setCommentToDeleteId(null);
    };

    // 4. RETURN MODIFICADO: Usa el userProfilesMap para los datos
    return (
        <div className="mt-12 bg-gray-800 rounded-lg shadow-lg p-6 COMMENTS_DIV" id="comments-section">
            <h2 className="text-2xl font-bold mb-6">Comentarios del Perfil</h2> {/* (Texto cambiado a español) */}
            {loggedInUserProfile && (
                <form onSubmit={handlePostComment} className="flex items-start space-x-4 mb-8">
                    <img src={loggedInUserProfile.avatarURL || `https://placehold.co/48x48/374151/E5E7EB?text=${loggedInUserProfile.displayName.charAt(0)}`} alt="Tu avatar" className="w-12 h-12 rounded-full object-cover" />
                    <div className="flex-1">
                        <textarea value={newComment} onChange={(e) => setNewComment(e.target.value)} placeholder="Deja un comentario..." rows="3" className="w-full px-4 py-2 text-white bg-gray-700 border border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"></textarea>
                        <VerificationGuard user={authUser} placement="top">
                        <button type="submit" disabled={isPosting} className="mt-2 px-4 py-2 font-bold text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50">
                            {isPosting ? 'Publicando...' : 'Publicar'}
                        </button>
                        </VerificationGuard>
                    </div>
                </form>
            )}
            <div className="space-y-6">
                {loading ? <p>Cargando comentarios...</p> : comments.length > 0 ? (
                    comments.map(comment => {
                        // 4a. Obtener el perfil actual desde el mapa
                        const profile = userProfilesMap[comment.commenterId];

                        // 4b. Mostrar un placeholder si el perfil aún no carga
                        if (!profile) {
                            return (
                                <div key={comment.id} className="flex items-start space-x-4 animate-pulse">
                                    <div className="w-12 h-12 rounded-full bg-gray-700"></div>
                                    <div className="flex-1 space-y-2">
                                        <div className="h-4 bg-gray-700 rounded w-1/4"></div>
                                        <div className="h-4 bg-gray-700 rounded w-3/4"></div>
                                    </div>
                                </div>
                            );
                        }

                        // 4c. Renderizar el comentario con los datos del perfil ACTUALES
                        return (
                            <div key={comment.id} className="flex items-start space-x-4">
                                <Link to={`/profile/${profile.username}`}> {/* <-- ¡CORREGIDO! */}
                                    <img 
                                        src={profile.avatarURL || `https://placehold.co/48x48/374151/E5E7EB?text=${profile.displayName.charAt(0)}`} 
                                        alt={profile.displayName} // <-- ¡CORREGIDO!
                                        className="w-12 h-12 rounded-full object-cover"
                                    />
                                </Link>
                                <div className="flex-1">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <Link to={`/profile/${profile.username}`} className="font-bold hover:underline"> {/* <-- ¡CORREGIDO! */}
                                                {profile.displayName} {/* <-- ¡CORREGIDO! */}
                                            </Link>
                                            <span className="text-sm text-gray-400 ml-2">{formatTimestamp(comment.createdAt)}</span>
                                        </div>
                                        {loggedInUserProfile && loggedInUserProfile.uid === profileOwnerId && (
                                            <button 
                                                onClick={() => handleOpenDeleteModal(comment.id)} 
                                                title="Eliminar comentario" 
                                                className="text-gray-500 hover:text-red-500"
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm4 0a1 1 0 012 0v6a1 1 0 11-2 0V8z" clipRule="evenodd" /></svg>
                                            </button>
                                        )}
                                    </div>
                                    <p className="text-gray-300 mt-1">{comment.commentText}</p>
                                </div>
                            </div>
                        );
                    })
                ) : <p className="text-gray-500 text-center py-4">Nadie ha comentado todavía. ¡Sé el primero!</p>}
            </div>
            <ConfirmCommentDeleteModal 
                isOpen={isDeleteModalOpen}
                onConfirm={confirmDeleteComment}
                onCancel={handleCloseDeleteModal}
            />
        </div>
    );
};

const ChapterComments = ({ chapterId, chapterAuthorId, loggedInUserProfile }) => {
    const [comments, setComments] = useState([]);
    // 1. NUEVO ESTADO para guardar los perfiles de los comentaristas
    const [userProfilesMap, setUserProfilesMap] = useState({});
    const [newComment, setNewComment] = useState('');
    const [loading, setLoading] = useState(true);
    const [isPosting, setIsPosting] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [commentToDeleteId, setCommentToDeleteId] = useState(null);
    const authUser = auth.currentUser;

    // 2. useEffect MODIFICADO: Ahora también busca los perfiles
    useEffect(() => {
        const q = query(collection(db, "chapterComments"), where("chapterId", "==", chapterId), orderBy("createdAt", "desc"));
        
        const unsubscribe = onSnapshot(q, async (querySnapshot) => {
            const fetchedComments = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setComments(fetchedComments);
            setLoading(false);

            // --- INICIO DE LÓGICA DE PERFILES ---
            if (fetchedComments.length > 0) {
                // 2a. Obtener todos los IDs de comentaristas únicos
                const commenterIds = [...new Set(fetchedComments.map(c => c.commenterId))];

                // 2b. Función para buscar perfiles en lotes (chunks)
                const fetchProfiles = async (ids) => {
                    try {
                        const chunks = [];
                        // Separar en lotes de 30 (límite de Firestore 'in')
                        for (let i = 0; i < ids.length; i += 30) {
                            chunks.push(ids.slice(i, i + 30));
                        }
                        
                        const profilesMap = {};
                        const promises = chunks.map(chunk =>
                            getDocs(query(collection(db, 'users'), where(documentId(), 'in', chunk)))
                        );
                        
                        const snapshots = await Promise.all(promises);
                        
                        snapshots.forEach(snapshot => {
                            snapshot.forEach(doc => {
                                profilesMap[doc.id] = doc.data();
                            });
                        });

                        // 2c. Guardar el mapa de perfiles en el estado
                        setUserProfilesMap(prevMap => ({ ...prevMap, ...profilesMap }));

                    } catch (e) {
                        console.error("Error fetching commenter profiles:", e);
                    }
                };
                
                // 2d. Ejecutar la búsqueda
                await fetchProfiles(commenterIds);

            } else {
                setUserProfilesMap({}); // Limpia el mapa si no hay comentarios
            }
            // --- FIN DE LÓGICA DE PERFILES ---
        });
        return () => unsubscribe();
    }, [chapterId]); // Dependencia correcta

    // 3. handlePostComment MODIFICADO: Ya no guarda datos mutables
    const handlePostComment = async (e) => {
        e.preventDefault();
        if (newComment.trim() === '' || !loggedInUserProfile || isPosting) return;
        setIsPosting(true);
        try {
            await addDoc(collection(db, "chapterComments"), {
                chapterId,
                chapterAuthorId,
                commenterId: loggedInUserProfile.uid, // <-- ¡BIEN!
                commentText: newComment,
                createdAt: serverTimestamp(),
                // commenterUsername: loggedInUserProfile.username,     <-- BORRADO
                // commenterDisplayName: loggedInUserProfile.displayName, <-- BORRADO
                // commenterAvatarURL: loggedInUserProfile.avatarURL || '', <-- BORRADO
            });
            setNewComment('');
        } catch (error) {
            console.error("Error posting chapter comment:", error);
            alert("No se pudo publicar el comentario. Revisa las reglas de seguridad de Firestore.");
        } finally {
            setIsPosting(false);
        }
    };

    // (Lógica de borrado sin cambios)
    const handleOpenDeleteModal = (commentId) => {
        setCommentToDeleteId(commentId);
        setIsDeleteModalOpen(true);
    };

    const confirmDeleteComment = async () => {
        if (!commentToDeleteId) return;
        try {
            await deleteDoc(doc(db, "chapterComments", commentToDeleteId));
        } catch (error) {
            console.error("Error deleting chapter comment:", error);
            alert("No se pudo eliminar el comentario.");
        } finally {
            setIsDeleteModalOpen(false);
            setCommentToDeleteId(null);
        }
    };

    const handleCloseDeleteModal = () => {
        setIsDeleteModalOpen(false);
        setCommentToDeleteId(null);
    };

    // 4. RETURN MODIFICADO: Usa el userProfilesMap para los datos
    return (
        <div className="mt-12 p-6 COMMENT_DIV">
            <h2 className="text-2xl font-bold mb-6 text-white-900 ">Comentarios del Capítulo</h2>
            {loggedInUserProfile && (
                <form onSubmit={handlePostComment} className="flex items-start space-x-4 mb-8">
                    <img src={loggedInUserProfile.avatarURL || `https://placehold.co/48x48/e2e8f0/4a5568?text=${loggedInUserProfile.displayName.charAt(0)}`} alt="Tu avatar" className="w-12 h-12 rounded-full object-cover" />
                    <div className="flex-1">
                        <textarea value={newComment} onChange={(e) => setNewComment(e.target.value)} placeholder="Deja un comentario sobre este capítulo..." rows="3" className="w-full px-4 py-2 text-white bg-gray-100 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"></textarea>
                        <VerificationGuard user={authUser} placement="top">
                        <button type="submit" disabled={isPosting} className="mt-2 px-4 py-2 font-bold text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50">
                            {isPosting ? 'Publicando...' : 'Publicar'}
                        </button>
                        </VerificationGuard>
                    </div>
                </form>
            )}
            <div className="space-y-6">
                {loading ? <p>Cargando comentarios...</p> : comments.length > 0 ? (
                    comments.map(comment => {
                        // 4a. Obtener el perfil actual desde el mapa
                        const profile = userProfilesMap[comment.commenterId];

                        // 4b. Mostrar un placeholder si el perfil aún no carga
                        if (!profile) {
                            return (
                                <div key={comment.id} className="flex items-start space-x-4 animate-pulse">
                                    <div className="w-12 h-12 rounded-full bg-gray-700"></div>
                                    <div className="flex-1 space-y-2">
                                        <div className="h-4 bg-gray-700 rounded w-1/4"></div>
                                        <div className="h-4 bg-gray-700 rounded w-3/4"></div>
                                    </div>
                                </div>
                            );
                        }

                        // 4c. Renderizar el comentario con los datos del perfil ACTUALES
                        return (
                            <div key={comment.id} className="flex items-start space-x-4">
                                <Link to={`/profile/${profile.username}`}> {/* <-- ¡CORREGIDO! */}
                                    <img 
                                        src={profile.avatarURL || `https://placehold.co/48x48/e2e8f0/4a5568?text=${profile.displayName.charAt(0)}`} 
                                        alt={profile.displayName} // <-- ¡CORREGIDO!
                                        className="w-12 h-12 rounded-full object-cover" 
                                    />
                                </Link>
                                <div className="flex-1">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <Link to={`/profile/${profile.username}`} className="font-italic hover:underline text-indigo"> {/* <-- ¡CORREGIDO! */}
                                                {profile.displayName} {/* <-- ¡CORREGIDO! */}
                                            </Link>
                                            <span className="text-sm text-gray-600 ml-2">{formatTimestamp(comment.createdAt)}</span>
                                        </div>
                                        {loggedInUserProfile && (loggedInUserProfile.uid === chapterAuthorId || loggedInUserProfile.uid === comment.commenterId) && (
                                            <button 
                                                onClick={() => handleOpenDeleteModal(comment.id)} 
                                                title="Eliminar comentario" 
                                                className="text-gray-400 hover:text-red-600"
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm4 0a1 1 0 012 0v6a1 1 0 11-2 0V8z" clipRule="evenodd" /></svg>
                                            </button>
                                        )}
                                    </div>
                                    <p className="text-white mt-1">{comment.commentText}</p>
                                </div>
                            </div>
                        );
                    })
                ) : <p className="text-gray-500 text-center py-4">Nadie ha comentado todavía. ¡Sé el primero!</p>}
            </div>
            <ConfirmCommentDeleteModal 
                isOpen={isDeleteModalOpen}
                onConfirm={confirmDeleteComment}
                onCancel={handleCloseDeleteModal}
            />
        </div>
    );
};

const FollowListModal = ({ userId, listType, onClose }) => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchUsers = async () => {
            setLoading(true);
            setUsers([]); // Clear previous results
            try {
                // Step 1: Fetch the list of user IDs from the subcollection.
                const collectionPath = `users/${userId}/${listType}`;
                const orderByField = listType === 'followers' ? 'followedBy' : 'followedAt';
                
                const q = query(collection(db, collectionPath), orderBy(orderByField, 'desc'), limit(200));
                
                const snapshot = await getDocs(q);
                const userIds = snapshot.docs.map(doc => doc.id);

                if (userIds.length > 0) {
                    // Step 2: Firestore 'in' queries are limited to 30 elements. We must "chunk" the userIds array.
                    const chunks = [];
                    for (let i = 0; i < userIds.length; i += 30) {
                        chunks.push(userIds.slice(i, i + 30));
                    }

                    // Step 3: Fetch the user profiles for each chunk in parallel.
                    const userPromises = chunks.map(chunk => {
                        const usersQuery = query(collection(db, 'users'), where(documentId(), 'in', chunk));
                        return getDocs(usersQuery);
                    });

                    const userSnapshots = await Promise.all(userPromises);
                    
                    const usersData = [];
                    userSnapshots.forEach(userSnapshot => {
                        userSnapshot.forEach(doc => {
                            usersData.push({ id: doc.id, ...doc.data() });
                        });
                    });

                    // Re-order the results to match the original sorted list of IDs
                    const sortedUsers = userIds.map(id => usersData.find(user => user.id === id)).filter(Boolean);

                    setUsers(sortedUsers);
                }
            } catch (error) {
                console.error(`Error fetching ${listType}:`, error);
            }
            setLoading(false);
        };

        if (userId) {
            fetchUsers();
        }
    }, [userId, listType]);
    
    return (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-[110] p-4" onClick={onClose}>
            <div className="bg-gray-800 rounded-lg shadow-xl w-full max-w-md p-6 text-white" onClick={e => e.stopPropagation()}>
                <h2 className="text-2xl font-bold mb-4 capitalize">{listType}</h2>
                <div className="max-h-96 overflow-y-auto space-y-3">
                    {loading ? (
                        <p>Cargando...</p>
                    ) : users.length > 0 ? (
                        users.map(user => (
                            <Link to={`/profile/${user.username}`} key={user.id} onClick={onClose} className="flex items-center space-x-4 p-2 rounded-md hover:bg-gray-700">
                                <img src={user.avatarURL || `https://placehold.co/40x40/374151/E5E7EB?text=${user.displayName.charAt(0)}`} alt={user.displayName} className="w-10 h-10 rounded-full object-cover"/>
                                <div>
                                    <p className="font-bold">{user.displayName}</p>
                                    <p className="text-sm text-gray-400">@{user.username}</p>
                                </div>
                            </Link>
                        ))
                    ) : (
                        <p className="text-gray-500">No hay usuarios que mostrar.</p>
                    )}
                </div>
            </div>
        </div>
    );
};

const EditProfileModal = ({ currentUserProfile, onClose, onProfileUpdate }) => {
    const navigate = useNavigate(); const [displayName, setDisplayName] = useState(currentUserProfile.displayName); const [username, setUsername] = useState(currentUserProfile.username); const [description, setDescription] = useState(currentUserProfile.description || ''); const [avatarFile, setAvatarFile] = useState(null); const [avatarPreview, setAvatarPreview] = useState(currentUserProfile.avatarURL); const [isSaving, setIsSaving] = useState(false); const [error, setError] = useState('');
    const thirtyDaysInMs = 30 * 24 * 60 * 60 * 1000; const lastChange = currentUserProfile.lastUsernameChange?.toDate(); const canChangeUsername = !lastChange || (Date.now() - lastChange.getTime()) > thirtyDaysInMs; const timeUntilChange = lastChange ? Math.ceil((thirtyDaysInMs - (Date.now() - lastChange.getTime())) / (1000 * 60 * 60 * 24)) : 0;
    const handleFileChange = (e) => { const file = e.target.files[0]; if (file && file.type.startsWith("image/")) { setAvatarFile(file); setAvatarPreview(URL.createObjectURL(file)); } };
    const handleSave = async () => { setIsSaving(true); setError(''); const newUsername = username.toLowerCase().trim(); const oldUsername = currentUserProfile.username; const usernameChanged = newUsername !== oldUsername; if (usernameChanged) { if (!canChangeUsername) { setError(`Puedes cambiar tu nombre de usuario de nuevo en ${timeUntilChange} días.`); setIsSaving(false); return; } if (newUsername.length < 3) { setError("El nombre de usuario debe tener al menos 3 caracteres."); setIsSaving(false); return; } const usernameDoc = await getDoc(doc(db, "usernames", newUsername)); if (usernameDoc.exists()) { setError("Ese nombre de usuario ya está en uso."); setIsSaving(false); return; } } try { let avatarURL = currentUserProfile.avatarURL; if (avatarFile) { const avatarRef = ref(storage, `avatars/${currentUserProfile.uid}/${avatarFile.name}`); const snapshot = await uploadBytes(avatarRef, avatarFile); avatarURL = await getDownloadURL(snapshot.ref); } const userDataToUpdate = { displayName, description, avatarURL, }; if (usernameChanged) { const batch = writeBatch(db); const oldUsernameRef = doc(db, "usernames", oldUsername); batch.delete(oldUsernameRef); const newUsernameRef = doc(db, "usernames", newUsername); batch.set(newUsernameRef, { userId: currentUserProfile.uid }); userDataToUpdate.username = newUsername; userDataToUpdate.lastUsernameChange = Timestamp.now(); await batch.commit(); } const userDocRef = doc(db, "users", currentUserProfile.uid); await updateDoc(userDocRef, userDataToUpdate); onProfileUpdate(userDataToUpdate); onClose(); if (usernameChanged) { navigate(`/profile/${newUsername}`); } } catch (err) { console.error("Error al actualizar el perfil: ", err); setError("No se pudo guardar el perfil. Inténtalo de nuevo."); } finally { setIsSaving(false); } };
    return ( <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4"><div className="bg-gray-800 rounded-lg shadow-xl w-full max-w-lg p-6 text-white relative"><h2 className="text-2xl font-bold mb-6">Editar Perfil</h2><div className="space-y-4"><div className="flex items-center space-x-4"><img src={avatarPreview || `https://placehold.co/100x100/374151/E5E7EB?text=${displayName.charAt(0)}`} alt="Avatar preview" className="w-24 h-24 rounded-full object-cover"/><label className="cursor-pointer bg-gray-700 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded-md"><span>Cambiar Imagen</span><input type="file" onChange={handleFileChange} accept="image/png, image/jpeg" className="hidden"/></label></div><div><label className="block text-sm font-medium text-gray-300">Nombre de Perfil</label><input type="text" value={displayName} onChange={(e) => setDisplayName(e.target.value)} className="mt-1 w-full px-4 py-2 text-white bg-gray-700 border border-gray-600 rounded-md"/></div><div><label className="block text-sm font-medium text-gray-300">Nombre de Usuario (@)</label><input type="text" value={username} onChange={(e) => setUsername(e.target.value)} disabled={!canChangeUsername} className="mt-1 w-full px-4 py-2 text-white bg-gray-700 border border-gray-600 rounded-md disabled:bg-gray-900 disabled:opacity-50"/>{!canChangeUsername && <p className="text-xs text-gray-400 mt-1">Puedes cambiar tu nombre de usuario de nuevo en {timeUntilChange} días.</p>}</div><div><label className="block text-sm font-medium text-gray-300">Descripción</label><textarea value={description} onChange={(e) => setDescription(e.target.value)} maxLength="200" rows="3" className="mt-1 w-full px-4 py-2 text-white bg-gray-700 border border-gray-600 rounded-md"></textarea><p className="text-xs text-right text-gray-400">{description?.length || 0}/200</p></div></div>{error && <p className="text-red-500 text-sm mt-4">{error}</p>}<div className="flex justify-end space-x-4 pt-6 mt-4 border-t border-gray-700"><button onClick={onClose} disabled={isSaving} className="px-6 py-2 font-bold text-gray-300 bg-gray-600 rounded-md hover:bg-gray-700">Cancelar</button><button onClick={handleSave} disabled={isSaving} className="px-6 py-2 font-bold text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50">{isSaving ? 'Guardando...' : 'Guardar Cambios'}</button></div></div></div> );
};

const PublishModal = ({ 
    story, 
    chapterTitle, 
    onTitleChange, 
    acceptDonations, 
    onDonationsChange, 
    onClose, 
    onPublish, 
    isPublishing,
    userProfile 
}) => {
    const storyName = story ? story.title : "your story";
    const isPro = userProfile?.isPro;

    const [releaseType, setReleaseType] = useState('now'); 
    const [scheduleDate, setScheduleDate] = useState(null);

    const minDate = new Date();
    // Detectar zona horaria del usuario para mostrársela
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    const handleConfirm = () => {
        if (releaseType !== 'now' && !scheduleDate) {
            alert("Please select a release date and time."); // Mensaje en Inglés
            return;
        }
        
        onPublish(
            releaseType === 'now' ? null : scheduleDate, 
            releaseType
        );
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            {/* Estilos Dark Mode (Igual que antes) */}
            <style>{`
                .react-datepicker {
                    font-family: inherit;
                    background-color: #1f2937;
                    border-color: #374151;
                    color: #e5e7eb;
                }
                .react-datepicker__header {
                    background-color: #111827;
                    border-bottom-color: #374151;
                }
                .react-datepicker__current-month, .react-datepicker-time__header, .react-datepicker-year-header {
                    color: #f3f4f6;
                }
                .react-datepicker__day-name { color: #9ca3af; }
                .react-datepicker__day { color: #e5e7eb; }
                .react-datepicker__day:hover { background-color: #374151; }
                .react-datepicker__day--selected, .react-datepicker__day--keyboard-selected {
                    background-color: #4f46e5 !important;
                    color: white !important;
                }
                .react-datepicker__time-container {
                    border-left-color: #374151;
                }
                .react-datepicker__time-container .react-datepicker__time {
                    background-color: #1f2937;
                    color: #e5e7eb;
                }
                .react-datepicker__time-container .react-datepicker__time .react-datepicker__time-box ul.react-datepicker__time-list li.react-datepicker__time-list-item:hover {
                    background-color: #374151;
                }
                .react-datepicker__time-container .react-datepicker__time .react-datepicker__time-box ul.react-datepicker__time-list li.react-datepicker__time-list-item--selected {
                    background-color: #4f46e5 !important;
                }
                .react-datepicker__input-container input {
                    width: 100%;
                    padding: 0.75rem;
                    background-color: #000;
                    border: 1px solid #4b5563;
                    border-radius: 0.5rem;
                    color: white;
                    outline: none;
                }
                .react-datepicker__input-container input:focus {
                    border-color: #6366f1;
                    ring: 2px;
                }
            `}</style>

            <div className="bg-gray-800 rounded-xl shadow-2xl w-full max-w-lg p-6 text-white relative border border-gray-700">
                
                <div className="flex justify-between items-start mb-6">
                    <h2 className="text-2xl font-bold">Publishing Options</h2>
                    <button onClick={onClose} className="text-gray-400 hover:text-white">✕</button>
                </div>
                
                <div className="space-y-6 max-h-[70vh] overflow-y-auto custom-scrollbar pr-2">
                    
                    {/* 1. TÍTULO */}
                    <div>
                        <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Chapter Title</label>
                        <input 
                            type="text" 
                            value={chapterTitle} 
                            onChange={(e) => onTitleChange(e.target.value)} 
                            placeholder="e.g. The Awakening" 
                            className="w-full px-4 py-3 text-white bg-gray-900 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-lg font-medium"
                            disabled={isPublishing}
                        />
                    </div>

                    {/* 2. ESTRATEGIA DE LANZAMIENTO (Solo PRO) */}
                    <div className="bg-gray-900/50 rounded-lg p-4 border border-gray-700">
                        <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-4 flex justify-between">
                            <span>Release Strategy</span>
                            {!isPro && <span className="text-yellow-500 text-[10px] bg-yellow-500/10 px-2 py-0.5 rounded border border-yellow-500/20">PRO ONLY 👑</span>}
                        </label>

                        <div className="space-y-3">
                            {/* Opción A: Publicar Ahora */}
                            <label className={`flex items-start p-3 rounded-lg border cursor-pointer transition-all ${releaseType === 'now' ? 'bg-indigo-900/20 border-indigo-500' : 'border-gray-700 hover:bg-gray-800'}`}>
                                <input 
                                    type="radio" 
                                    name="releaseType" 
                                    value="now" 
                                    checked={releaseType === 'now'} 
                                    onChange={() => setReleaseType('now')}
                                    className="mt-1 text-indigo-600 focus:ring-indigo-500 bg-gray-900 border-gray-600"
                                />
                                <div className="ml-3">
                                    <span className="block text-sm font-medium text-white">Publish Immediately</span>
                                    <span className="block text-xs text-gray-400">The chapter will be available to everyone instantly.</span>
                                </div>
                            </label>

                            {/* Opción B: Programar (Fantasma) */}
                            <label className={`flex items-start p-3 rounded-lg border cursor-pointer transition-all ${!isPro ? 'opacity-50 cursor-not-allowed' : ''} ${releaseType === 'scheduled' ? 'bg-indigo-900/20 border-indigo-500' : 'border-gray-700 hover:bg-gray-800'}`}>
                                <input 
                                    type="radio" 
                                    name="releaseType" 
                                    value="scheduled" 
                                    checked={releaseType === 'scheduled'} 
                                    onChange={() => isPro && setReleaseType('scheduled')}
                                    disabled={!isPro}
                                    className="mt-1 text-indigo-600 focus:ring-indigo-500 bg-gray-900 border-gray-600"
                                />
                                <div className="ml-3">
                                    <span className="block text-sm font-medium text-white flex items-center gap-2">
                                        Silent Schedule
                                        {!isPro && <span className="text-[10px] text-yellow-500">PRO</span>}
                                    </span>
                                    <span className="block text-xs text-gray-400">Automatically publishes on the selected date. No one sees it before then.</span>
                                </div>
                            </label>

                            {/* Opción C: Pre-Estreno (Hype) */}
                            <label className={`flex items-start p-3 rounded-lg border cursor-pointer transition-all ${!isPro ? 'opacity-50 cursor-not-allowed' : ''} ${releaseType === 'hype' ? 'bg-pink-900/20 border-pink-500' : 'border-gray-700 hover:bg-gray-800'}`}>
                                <input 
                                    type="radio" 
                                    name="releaseType" 
                                    value="hype" 
                                    checked={releaseType === 'hype'} 
                                    onChange={() => isPro && setReleaseType('hype')}
                                    disabled={!isPro}
                                    className="mt-1 text-pink-600 focus:ring-pink-500 bg-gray-900 border-gray-600"
                                />
                                <div className="ml-3">
                                    <span className="block text-sm font-medium text-white flex items-center gap-2">
                                        Hype Mode (Pre-Save) 🔥
                                        {!isPro && <span className="text-[10px] text-yellow-500">PRO</span>}
                                    </span>
                                    <span className="block text-xs text-gray-400">Appears NOW with a lock and countdown. Fans can pre-save it.</span>
                                </div>
                            </label>
                        </div>

                        {/* SELECTOR DE FECHA */}
                        {releaseType !== 'now' && (
                            <div className="mt-4 pl-8 animate-fadeIn">
                                <label className="block text-xs text-gray-400 mb-1">
                                    Release Date & Time <span className="text-indigo-400">({userTimezone})</span>
                                </label>
                                <div className="relative">
                                    <DatePicker
                                        selected={scheduleDate}
                                        onChange={(date) => setScheduleDate(date)}
                                        showTimeSelect
                                        timeFormat="HH:mm"
                                        timeIntervals={15}
                                        timeCaption="Time"
                                        dateFormat="MMMM d, yyyy h:mm aa" // Formato inglés
                                        minDate={minDate}
                                        locale="en-US" // Locale Inglés
                                        placeholderText="Select date and time..."
                                        className="w-full" 
                                        calendarClassName="shadow-xl border border-gray-600"
                                        popperPlacement="top-start" 
                                    />
                                    <div className="absolute right-3 top-3 pointer-events-none text-gray-400">
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                    
                    {/* 3. DONACIONES */}
                    <div className="pt-2">
                         <label className="flex items-center cursor-pointer p-3 rounded-lg hover:bg-gray-700/50 transition-colors">
                            <input 
                                type="checkbox" 
                                checked={acceptDonations} 
                                onChange={(e) => onDonationsChange(e.target.checked)} 
                                disabled={isPublishing}
                                className="h-5 w-5 rounded text-indigo-500 bg-gray-900 border-gray-600 focus:ring-indigo-600" 
                            /> 
                            <div className="ml-3">
                                <span className="block text-sm font-medium text-gray-200">Accept Donations</span>
                                <span className="block text-xs text-gray-500">Allow readers to support this chapter.</span>
                            </div>
                        </label>
                    </div>
                </div>

                <p className="text-gray-400 mt-4">
                    You are about to publish a chapter in the story: 
                    <span className="font-semibold text-indigo-400"> "{storyName}"</span>.
                </p>
                
                
                {/* FOOTER */}
                <div className="flex justify-end space-x-3 pt-6 mt-4 border-t border-gray-700">
                    <button 
                        onClick={onClose} 
                        disabled={isPublishing} 
                        className="px-4 py-2 font-bold text-gray-400 hover:text-white hover:bg-gray-700 rounded-md transition-colors"
                    >
                        Cancel
                    </button>
                    
                    <button 
                        onClick={handleConfirm} 
                        disabled={isPublishing || !chapterTitle.trim()} 
                        className={`px-6 py-2 font-bold text-white rounded-md shadow-lg transition-all transform hover:scale-105 disabled:opacity-50 disabled:scale-100 ${
                            releaseType === 'hype' ? 'bg-gradient-to-r from-pink-600 to-purple-600 hover:from-pink-500 hover:to-purple-500' :
                            releaseType === 'scheduled' ? 'bg-blue-600 hover:bg-blue-500' :
                            'bg-indigo-600 hover:bg-indigo-500'
                        }`}
                    >
                        {isPublishing ? 'Processing...' : (
                            releaseType === 'now' ? 'Publish Now' : 
                            releaseType === 'hype' ? 'Schedule Hype' : 'Schedule'
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

const AddToCollectionModal = ({ isOpen, onClose, story, userProfile }) => {
    const [collections, setCollections] = useState([]);
    const [loading, setLoading] = useState(true);
    
    // Estados para crear
    const [isCreating, setIsCreating] = useState(false);
    const [newTitle, setNewTitle] = useState('');
    const [isPrivate, setIsPrivate] = useState(true); 
    const [feedbackMsg, setFeedbackMsg] = useState(''); // Feedback visual

    useEffect(() => {
        if (!isOpen || !userProfile) return;

        const q = query(
            collection(db, 'collections'), 
            where('ownerId', '==', userProfile.uid),
            orderBy('lastUpdatedAt', 'desc')
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const fetchedCollections = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            const manualCollections = fetchedCollections.filter(c => !c.isDefaultLiked);
            setCollections(manualCollections);
            setLoading(false);
        }, (error) => {
            console.error(error);
            setLoading(false);
        });

        return () => unsubscribe();
    }, [isOpen, userProfile]);

    const showFeedback = (msg) => {
        setFeedbackMsg(msg);
        setTimeout(() => setFeedbackMsg(''), 2000);
    };

    const handleToggleCollection = async (collectionId, isAlreadyIn) => {
        const collRef = doc(db, 'collections', collectionId);
        const batch = writeBatch(db);

        if (isAlreadyIn) {
            batch.update(collRef, {
                storyIds: arrayRemove(story.id),
                storyCount: increment(-1),
                lastUpdatedAt: serverTimestamp()
            });
            showFeedback("Removido");
        } else {
            batch.update(collRef, {
                storyIds: arrayUnion(story.id),
                storyCount: increment(1),
                lastUpdatedAt: serverTimestamp()
            });
            showFeedback("Guardado");
        }

        try {
            await batch.commit();
        } catch (error) {
            console.error("Error updating collection:", error);
        }
    };

    const handleCreateCollection = async () => {
        if (!newTitle.trim()) return;
        setIsCreating(true);

        try {
            await addDoc(collection(db, 'collections'), {
                ownerId: userProfile.uid,
                title: newTitle.trim(),
                description: '', 
                coverImageURL: story.coverImageURL || null, 
                visibility: isPrivate ? 'private' : 'public',
                isDefaultLiked: false,
                storyIds: [story.id], 
                storyCount: 1,
                createdAt: serverTimestamp(),
                lastUpdatedAt: serverTimestamp()
            });
            
            setNewTitle('');
            setIsPrivate(true);
            showFeedback("¡Colección Creada!");
        } catch (error) {
            console.error("Error creating collection:", error);
        } finally {
            setIsCreating(false);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleCreateCollection();
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-[130]" onClick={onClose}>
            <div className="bg-gray-800 rounded-lg shadow-xl w-full max-w-md p-6 text-white flex flex-col max-h-[80vh]" onClick={e => e.stopPropagation()}>
                
                {/* Header con Feedback Flotante */}
                <div className="flex justify-between items-center mb-4 relative">
                    <h2 className="text-xl font-bold">Guardar en...</h2>
                    {feedbackMsg && (
                        <span className="absolute left-1/2 transform -translate-x-1/2 bg-green-600 text-xs font-bold px-2 py-1 rounded-full animate-pulse">
                            {feedbackMsg}
                        </span>
                    )}
                    <button onClick={onClose} className="text-gray-400 hover:text-white">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                </div>

                {/* LISTA DE COLECCIONES */}
                <div className="flex-grow overflow-y-auto custom-scrollbar mb-4 pr-2 space-y-2">
                    {loading ? (
                        <p className="text-center text-gray-400 py-4">Cargando...</p>
                    ) : collections.length === 0 ? (
                        <p className="text-center text-gray-500 py-4 text-sm">Crea tu primera colección abajo.</p>
                    ) : (
                        collections.map(coll => {
                            const isAdded = coll.storyIds?.includes(story.id);
                            return (
                                <button 
                                    key={coll.id}
                                    onClick={() => handleToggleCollection(coll.id, isAdded)}
                                    className="w-full flex items-center justify-between p-3 rounded-md hover:bg-gray-700 transition-colors group text-left"
                                >
                                    <div className="flex items-center space-x-3 overflow-hidden">
                                        <div className="w-10 h-10 rounded bg-gray-600 flex-shrink-0 flex items-center justify-center overflow-hidden">
                                            {coll.coverImageURL ? (
                                                <img src={coll.coverImageURL} alt="" className="w-full h-full object-cover"/>
                                            ) : (
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 19a2 2 0 01-2-2V7a2 2 0 012-2h4l2 2h4a2 2 0 012 2v1M5 19h14a2 2 0 002-2v-5a2 2 0 00-2-2H9a2 2 0 00-2 2v5a2 2 0 01-2 2z" /></svg>
                                            )}
                                        </div>
                                        <div className="truncate">
                                            <p className={`font-semibold truncate ${isAdded ? 'text-indigo-400' : 'text-gray-200'}`}>{coll.title}</p>
                                            <p className="text-xs text-gray-500 flex items-center gap-1">
                                                {coll.storyCount} historias • {coll.visibility === 'private' ? 'Privada' : 'Pública'}
                                            </p>
                                        </div>
                                    </div>
                                    <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${isAdded ? 'bg-indigo-600 border-indigo-600' : 'border-gray-500 group-hover:border-gray-300'}`}>
                                        {isAdded && <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-white" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>}
                                    </div>
                                </button>
                            );
                        })
                    )}
                </div>

                {/* CREAR NUEVA (Diseño Compacto) */}
                <div className="pt-4 border-t border-gray-700">
                    <div className="relative flex items-center">
                        {/* Input + Botón Incrustado */}
                        <input 
                            type="text" 
                            placeholder="Nombre nueva colección..." 
                            value={newTitle}
                            onChange={(e) => setNewTitle(e.target.value)}
                            onKeyDown={handleKeyDown}
                            className="w-full pl-4 pr-24 py-3 bg-gray-900 border border-gray-600 rounded-md text-white focus:ring-2 focus:ring-indigo-500 focus:outline-none text-sm"
                        />
                        
                        {/* Botón de Crear dentro del input */}
                        <button 
                            onClick={handleCreateCollection}
                            disabled={!newTitle.trim() || isCreating}
                            className="absolute right-2 top-1.5 bottom-1.5 px-3 bg-indigo-600 text-white text-xs font-bold rounded hover:bg-indigo-500 disabled:opacity-50 transition-colors flex items-center"
                        >
                            {isCreating ? '...' : 'CREAR'}
                        </button>
                    </div>

                    {/* Toggle de Privacidad debajo */}
                    <div className="mt-2 flex justify-between items-center">
                        <label className="flex items-center cursor-pointer space-x-2">
                            <span className="text-xs text-gray-400">Visibilidad:</span>
                            <div className={`w-8 h-4 rounded-full p-0.5 transition-colors ${isPrivate ? 'bg-gray-600' : 'bg-green-600'}`} onClick={() => setIsPrivate(!isPrivate)}>
                                <div className={`w-3 h-3 bg-white rounded-full shadow-md transform transition-transform ${isPrivate ? 'translate-x-0' : 'translate-x-4'}`}></div>
                            </div>
                            <span className="text-xs text-gray-300">{isPrivate ? 'Privada' : 'Pública'}</span>
                        </label>
                    </div>
                </div>

                {/* BOTÓN LISTO (Para cerrar) */}
                <button 
                    onClick={onClose}
                    className="mt-6 w-full py-3 bg-indigo-600 text-white-900 font-bold rounded-md hover:bg-gray-200  text-grey-900 transition-colors BOTON_CONFIRMACION"
                >
                    ¡LISTO!
                </button>
            </div>
        </div>
    );
};

// ==================================================================================
// COMPONENTE: AUTOCOMPLETADO DE TAGS/PERSONAJES (FASE 4 - PREMIUM UX)
// ==================================================================================
const TagAutocomplete = ({ 
    label, 
    placeholder, 
    collectionName, // 'tags' o 'characters'
    selectedItems = [], 
    onItemsChange, 
    maxItems = 15 
}) => {
    const [input, setInput] = useState('');
    const [suggestions, setSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [newCharWarning, setNewCharWarning] = useState(null); // null, 'format', 'confirm'
    const wrapperRef = useRef(null);

    // Determinar si se alcanzó el límite
    const isLimitReached = selectedItems.length >= maxItems;

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setShowSuggestions(false);
                setNewCharWarning(null);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // Búsqueda en Typesense
    useEffect(() => {
        if (isLimitReached) return; // No buscar si ya llegamos al límite

        const timer = setTimeout(async () => {
            if (input.length < 2) {
                setSuggestions([]);
                return;
            }
            try {
                const searchResults = await typesenseClient.collections(collectionName)
                    .documents()
                    .search({
                        q: input,
                        query_by: 'name',
                        sort_by: '_text_match:desc,usageCount:desc',
                        per_page: 6
                    });
                if (searchResults.hits) {
                    setSuggestions(searchResults.hits.map(h => h.document));
                }
            } catch (error) {
                console.error("Error autocomplete:", error);
            }
        }, 300);
        return () => clearTimeout(timer);
    }, [input, collectionName, isLimitReached]);

    const handleInputChange = (e) => {
        let val = e.target.value;
        if (collectionName === 'tags') val = val.replace(/\s/g, ''); 
        setInput(val);
        setNewCharWarning(null);
    };

    const attemptAddItem = (nameOverride = null) => {
        if (isLimitReached) return; // Seguridad extra

        const valueToAdd = nameOverride || input.trim();
        if (!valueToAdd) return;

        if (selectedItems.some(item => item.toLowerCase() === valueToAdd.toLowerCase())) {
            setInput('');
            setNewCharWarning(null);
            return;
        }
        
        // --- LÓGICA DE PERSONAJES ---
        if (collectionName === 'characters') {
            const match = suggestions.find(s => s.name.toLowerCase() === valueToAdd.toLowerCase());
            if (match) {
                addItem(match.name);
                return;
            }
            const formatRegex = /^.+ \(.+\)$/; 
            if (!formatRegex.test(valueToAdd)) {
                setNewCharWarning('format'); 
                return;
            }
            if (newCharWarning !== 'confirm') {
                setNewCharWarning('confirm');
                return;
            }
        }
        
        addItem(valueToAdd);
    };

    const addItem = (name) => {
        onItemsChange([...selectedItems, name]);
        setInput('');
        setSuggestions([]);
        setShowSuggestions(false);
        setNewCharWarning(null);
    };

    const removeItem = (indexToRemove) => {
        onItemsChange(selectedItems.filter((_, index) => index !== indexToRemove));
        // Al borrar, automáticamente se reactiva el input porque isLimitReached cambia a false
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            attemptAddItem();
        }
    };

    return (
        <div className="mb-2 relative" ref={wrapperRef}>
            <div className="flex justify-between items-end mb-2">
                <label className="block text-sm font-medium text-gray-300">{label}</label>
                <span className={`text-xs font-mono ${isLimitReached ? 'text-red-400 font-bold' : 'text-gray-500'}`}>
                    {selectedItems.length}/{maxItems}
                </span>
            </div>
            
            <div className={`border rounded-lg p-4 transition-colors input_label ${
                newCharWarning ? 'border-yellow-500 ring-1 ring-yellow-500' : 
                isLimitReached ? 'border-red-900/50 bg-red-900/10' : // Borde rojo sutil si está lleno
                'border-gray-700 '
            }`}>
                
                {/* Lista de Items Seleccionados */}
                <div className="flex flex-wrap gap-2 mb-2">
                    {selectedItems.map((item, index) => (
                        <span key={index} className={`text-white text-xs font-bold px-3 py-1 rounded-full flex items-center gap-2  ${collectionName === 'characters' ? 'bg-blue-600/20 border border-blue-500/50 text-blue-200' : 'bg-indigo-600/20 border border-indigo-500/50 text-indigo-200'}`}>
                            {collectionName === 'characters' && <span className="w-2 h-2 rounded-full bg-current"></span>}
                            {collectionName === 'tags' && <span className="">#</span>}
                            {item}
                            <button type="button" onClick={() => removeItem(index)} className="hover:text-white focus:outline-none ml-1">&times;</button>
                        </span>
                    ))}
                </div>

                {/* INPUT O MENSAJE DE LÍMITE */}
                <div className="relative">
                    {!isLimitReached ? (
                        <>
                            <input
                                type="text"
                                value={input}
                                onChange={handleInputChange}
                                onKeyDown={handleKeyDown}
                                onFocus={() => setShowSuggestions(true)}
                                placeholder={selectedItems.length === 0 ? placeholder : ""}
                                className="w-full bg-transparent text-white placeholder-gray-500 focus:outline-none text-sm py-1"
                            />
                            
                            {/* DROPDOWN DE SUGERENCIAS */}
                            {showSuggestions && suggestions.length > 0 && !newCharWarning && (
                                <div className="absolute top-full left-0 w-full bg-gray-800 border border-gray-600 rounded-md shadow-2xl z-50 mt-2 max-h-60 overflow-y-auto">
                                    {suggestions.map((s, idx) => (
                                        <button
                                            key={idx}
                                            type="button"
                                            onClick={() => attemptAddItem(s.name)}
                                            className="w-full text-left px-4 py-3 text-sm text-gray-200 hover:bg-gray-700 flex items-center gap-3 border-b border-gray-700/50 last:border-0"
                                        >
                                            {collectionName === 'characters' && (
                                                <img 
                                                    src={s.avatarURL || `https://placehold.co/40x40/1f2937/FFFFFF?text=${s.name.charAt(0)}`} 
                                                    alt="" 
                                                    className="w-8 h-8 rounded-full object-cover flex-shrink-0"
                                                />
                                            )}
                                            <div className="flex-grow">
                                                <div className="font-semibold text-white">
                                                    {collectionName === 'tags' && <span className="text-indigo-400 mr-0.5">#</span>}
                                                    {s.name}
                                                </div>
                                                <div className="text-xs text-gray-500 flex items-center gap-2">
                                                    <span>{s.usageCount || 0} historias</span>
                                                    {s.status === 'approved' && <span className="text-green-400 flex items-center gap-0.5">✓ Verificado</span>}
                                                </div>
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </>
                    ) : (
                        // MENSAJE VISUAL DE LÍMITE ALCANZADO (Solo para Personajes o cuando se alcance el max)
                        <div className="py-2 px-1 text-sm flex items-center gap-2 text-yellow-200/80 italic animate-fadeIn">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" /></svg>
                            <span>
                                Has alcanzado el límite de {maxItems} personajes principales. 
                                <span className="opacity-70 ml-1 not-italic">Usa las etiquetas normales para añadir secundarios.</span>
                            </span>
                        </div>
                    )}
                </div>
            </div>

            {/* ADVERTENCIAS EN UI */}
            {newCharWarning === 'format' && (
                <div className="mt-2 p-3 bg-red-900/30 border border-red-500/50 rounded-md text-red-200 text-sm flex items-start gap-3 animate-fadeIn">
                    <span className="text-xl">⚠️</span>
                    <div>
                        <p className="font-bold mb-1">Formato Incorrecto</p>
                        <p className="text-xs opacity-80 mb-2">Para evitar confusiones, los personajes nuevos deben incluir el show o fandom entre paréntesis.</p>
                        <p className="font-mono text-xs bg-black/30 p-1 rounded inline-block">Ejemplo: {input} (Nombre Show)</p>
                    </div>
                </div>
            )}

            {newCharWarning === 'confirm' && (
                <div className="mt-2 p-3 bg-yellow-900/30 border border-yellow-500/50 rounded-md text-yellow-200 text-sm flex flex-col gap-2 animate-fadeIn">
                    <div className="flex items-start gap-3">
                        <span className="text-xl">🤔</span>
                        <div>
                            <p className="font-bold">¿Crear personaje nuevo?</p>
                            <p className="text-xs opacity-80">"{input}" no existe en nuestra base de datos. Se creará como pendiente de moderación.</p>
                        </div>
                    </div>
                    <div className="flex justify-end gap-2 mt-1">
                        <button onClick={() => setNewCharWarning(null)} className="px-3 py-1 rounded hover:bg-white/10 text-xs">Cancelar</button>
                        <button onClick={() => addItem(input)} className="px-3 py-1 bg-yellow-600 hover:bg-yellow-500 text-black font-bold rounded text-xs">Sí, Crear</button>
                    </div>
                </div>
            )}
        </div>
    );
};

// Agregamos 'onClose' a las props recibidas
const CreateStoryModal = ({ userProfile, onStorySelected, onClose }) => { 
    const [stories, setStories] = useState([]); 
    const [loading, setLoading] = useState(true); 
    const [view, setView] = useState('choose'); 
    
    // Estados del formulario
    const [title, setTitle] = useState(''); 
    
    const [tags, setTags] = useState([]); 
    const [mainCharacters, setMainCharacters] = useState([]); 
    const [description, setDescription] = useState('');

    const [error, setError] = useState(''); 
    
    const [coverImageFile, setCoverImageFile] = useState(null); 
    const [imagePreview, setImagePreview] = useState('');
    const [isSensitive, setIsSensitive] = useState(false);
    const [isMature, setIsMature] = useState(false);
    
    // Cargar historias existentes
    useEffect(() => { 
        const fetchStories = async () => { 
            if (view !== 'choose') return; 
            setLoading(true); 
            try { 
                const q = query(collection(db, "stories"), where("authorId", "==", userProfile.uid)); 
                const querySnapshot = await getDocs(q); 
                setStories(querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))); 
            } catch (err) { 
                console.error("Error al buscar historias: ", err); 
            } finally { 
                setLoading(false); 
            } 
        }; 
        fetchStories(); 
    }, [userProfile.uid, view]); 
    
    const handleFileChange = (e) => { 
        const file = e.target.files[0]; 
        if (file && file.type.startsWith("image/")) { 
            setCoverImageFile(file); 
            setImagePreview(URL.createObjectURL(file)); 
        } else { 
            setCoverImageFile(null); 
            setImagePreview(''); 
        } 
    }; 
    
    const handleCreateStory = async (e) => { 
        e.preventDefault(); 
        if (!title) { setError("El Título es obligatorio."); return; }
        setLoading(true); 
        setError('');
        
        let coverImageURL = ''; 
        if (coverImageFile) { 
            const imageRef = ref(storage, `covers/${userProfile.uid}/${Date.now()}_${coverImageFile.name}`); 
            try { 
                const snapshot = await uploadBytes(imageRef, coverImageFile); 
                coverImageURL = await getDownloadURL(snapshot.ref); 
            } catch (uploadError) { 
                console.error("Error al subir imagen: ", uploadError); 
                setError("Error al subir la imagen de portada. Inténtalo de nuevo."); 
                setLoading(false); 
                return; 
            } 
        } 
        
        try { 
            const combinedText = `${title} ${description} ${tags.join(' ')} ${mainCharacters.join(' ')}`;
            const keywords = [...new Set(generateKeywords(combinedText))];

            const newStoryData = { 
                title, 
                description, 
                tags: tags, 
                authorId: userProfile.uid, 
                createdAt: serverTimestamp(), 
                lastUpdatedAt: serverTimestamp(), 
                coverImageURL, 
                searchKeywords: keywords, 
                isSensitive, 
                isMature, 
                heartCount: 0, 
                mainCharacters: mainCharacters, 
                extraCharacters: [] 
            }; 
            
            const newStoryRef = await addDoc(collection(db, "stories"), newStoryData); 
            onStorySelected({ id: newStoryRef.id, ...newStoryData }); 
        } catch (err) {
            setError("Error al crear la historia. Inténtalo de nuevo."); 
            console.error("Error al crear la historia: ", err); 
        } finally { 
            setLoading(false); 
        } 
    }; 
    
    return ( 
        <div className="fixed inset-0 bg-black/80 backdrop-blur-md flex items-center justify-center z-50 p-4">
            <div className="w-full text-white relative PopUp_Modal">
                
                {/* --- AQUÍ ESTÁ EL BOTÓN DE CERRAR (X) --- */}
                <button 
                    onClick={onClose}
                    className="absolute top-4 right-4 text-gray-400 hover:text-white hover:bg-gray-700 rounded-full p-2 transition-all z-50"
                    title="Cerrar y volver"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>
                {/* ---------------------------------------- */}

                {view === 'choose' ? ( 
                    <> 
                        <h2 className="text-2xl font-bold mb-4">Elige una Historia</h2> 
                        <p className="text-gray-400 mb-6">Crea una nueva historia o añade un capítulo a una existente.</p> 
                        
                        <button onClick={() => setView('create')} className="w-full mb-6 px-4 py-3 font-bold text-white bg-indigo-600 rounded-lg hover:bg-indigo-500 transition-colors shadow-lg shadow-indigo-900/20">
                            + Crear Nueva Historia
                        </button> 
                        
                        <div className="border-t border-gray-700 pt-4">
                            <h3 className="text-lg font-semibold mb-3 text-gray-300">O continúa una existente:</h3> 
                            {loading ? (
                                <p className="text-gray-500 italic">Cargando tus historias...</p>
                            ) : stories.length > 0 ? ( 
                                <ul className="space-y-2 max-h-48 overflow-y-auto custom-scrollbar pr-1">
                                    {stories.map(story => ( 
                                        <li key={story.id}>
                                            <button onClick={() => onStorySelected(story)} className="w-full text-left p-3 bg-gray-700/50 hover:bg-gray-700 rounded-md transition-colors border border-gray-600 hover:border-gray-500 flex justify-between items-center group">
                                                <span className="font-medium truncate">{story.title}</span>
                                                <span className="text-xs text-gray-500 group-hover:text-gray-300">Seleccionar →</span>
                                            </button>
                                        </li> 
                                    ))}
                                </ul> 
                            ) : (
                                <p className="text-gray-500">Aún no has creado ninguna historia.</p>
                            )} 
                        </div> 
                    </> 
                ) : ( 
                    <> 
                        <h2 className="text-2xl font-bold mb-6">Crear Nueva Historia</h2> 
                        <div className="Create_modal_flex">
                            
                            <form onSubmit={handleCreateStory} className="space-y-4 custom-scrollbar"> 
                                <div className="EACH_MODAL_FLEX">
                                {/* Título */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-2">Título</label>
                                    <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} className="w-full px-4 py-2 text-white input_label"/> 
                                </div>

                                {/* Descripción */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mt-2 mb-2">Descripción</label>
                                    <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows="3" className="w-full px-4 py-2 text-white input_label" placeholder="¿De qué trata tu historia?"></textarea>
                                </div>

                                <TagAutocomplete
                                    label="Personajes Principales"
                                    placeholder="Ej. Jax, Pomni, Ragatha..."
                                    collectionName="characters"
                                    
                                    selectedItems={mainCharacters}
                                    onItemsChange={setMainCharacters}
                                    maxItems={5}
                                />

                                <TagAutocomplete
                                    label="Etiquetas"
                                    placeholder="Ej. slow burn, enemies to lovers, magia..."
                                    collectionName="tags"
                                    
                                    selectedItems={tags}
                                    onItemsChange={setTags}
                                    maxItems={20} 
                                />

                                </div>

                                <div className="EACH_MODAL_FLEX">

                                {/* Portada */}
                                <div> 
                                    <label className="block text-sm font-medium text-gray-300 mb-2">Imagen de Portada</label> 
                                    <div className="mt-1 flex items-center bg-gray-700/50 p-3 rounded-lg border border-gray-600 border-dashed"> 
                                        {imagePreview && <img src={imagePreview} alt="Cover preview" className="h-24 w-16 object-cover rounded-md mr-4 shadow-md"/>} 
                                        <label className="cursor-pointer bg-gray-600 hover:bg-gray-500 text-white font-bold py-2 px-4 rounded-md transition-colors text-sm"> 
                                            <span>Subir Archivo</span> 
                                            <input type="file" onChange={handleFileChange} accept="image/png, image/jpeg" className="hidden"/> 
                                        </label> 
                                    </div> 
                                </div>
                            
                                </div>
                            
                            
                                {error && <p className="text-red-400 text-sm bg-red-900/20 p-2 rounded border border-red-900">{error}</p>} 
                            
                                {/* Botones Footer */}
                                <div className="flex justify-between space-x-3 pt-4 border-t border-gray-700"> 
                                     {/* Checkboxes */}
                                    <div className="flex items-center justify-start space-x-6 pt-2">
                                        <label className="flex items-center cursor-pointer group">
                                            <input type="checkbox" checked={isSensitive} onChange={() => setIsSensitive(!isSensitive)} className="h-5 w-5 rounded text-indigo-500 bg-gray-700 border-gray-600 focus:ring-indigo-600 cursor-pointer" /> 
                                            <span className="ml-2 text-gray-300 group-hover:text-white transition-colors">Sensible</span>
                                        </label>
                                        <label className="flex items-center cursor-pointer group">
                                            <input type="checkbox" checked={isMature} onChange={() => setIsMature(!isMature)} className="h-5 w-5 rounded text-indigo-500 bg-gray-700 border-gray-600 focus:ring-indigo-600 cursor-pointer" /> 
                                            <span className="ml-2 text-gray-300 group-hover:text-white transition-colors">Adulto (18+)</span>
                                        </label>
                                    </div> 

                                    <div className='flex gap-2'>
                                    <button type="button" onClick={() => { setView('choose'); setError(''); }} className="px-4 py-2 font-bold text-gray-300 hover:text-white bg-gray-700 hover:bg-gray-600 rounded-md transition-colors" disabled={loading}>
                                        Atrás
                                    </button> 
                                    <button type="submit" className="px-6 py-2 font-bold text-white bg-indigo-600 hover:bg-indigo-500 rounded-md disabled:opacity-50 transition-colors shadow-lg shadow-sky-500/5" disabled={loading}> 
                                        {loading ? 'Creando...' : 'Crear y Continuar'} 
                                    </button> 
                                    </div>
                                </div> 
                            </form> 
                        </div>
                    </> 
                )}
            </div>
        </div> 
    ); 
};

const EditStoryModal = ({ story, onClose, onDeleteClick }) => {
    const [title, setTitle] = useState(story.title);
    // const [genre, setGenre] = useState(story.genre);

    const [tags, setTags] = useState(story.tags || []); 
    const [mainCharacters, setMainCharacters] = useState(story.mainCharacters || []); // <-- NUEVO

    const [description, setDescription] = useState(story.description || '');

    const [isSensitive, setIsSensitive] = useState(story.isSensitive);
    const [isMature, setIsMature] = useState(story.isMature);
    const [coverImageFile, setCoverImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState(story.coverImageURL);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState('');

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file && file.type.startsWith("image/")) {
            setCoverImageFile(file);
            setImagePreview(URL.createObjectURL(file));
        }
    };

    const handleSaveChanges = async () => {
        if (!title) { setError("El Título es obligatorio."); return; }
        setIsSaving(true);
        setError('');

        try {
            let coverImageURL = story.coverImageURL;
            if (coverImageFile) {
                const imageRef = ref(storage, `covers/${story.authorId}/${Date.now()}_${coverImageFile.name}`);
                const snapshot = await uploadBytes(imageRef, coverImageFile);
                coverImageURL = await getDownloadURL(snapshot.ref);
            }
            
            const combinedText = `${title} ${description} ${tags.join(' ')} ${mainCharacters.join(' ')}`;
            const keywords = [...new Set(generateKeywords(combinedText))];

            const storyDataToUpdate = {
                title,
                tags, // <-- Array directo
                description,
                isSensitive,
                isMature,
                coverImageURL,
                searchKeywords: keywords,
                lastUpdatedAt: serverTimestamp(),
                // --- CAMBIO 3: Guardamos personajes ---
                mainCharacters: mainCharacters,
                extraCharacters: story.extraCharacters || [] // Mantenemos extra si existen
            };

            const storyRef = doc(db, "stories", story.id);
            await updateDoc(storyRef, storyDataToUpdate);
            
            // Recargamos para ver los cambios (o puedes actualizar el estado localmente si prefieres)
            window.location.reload(); 

        } catch (err) {
            setError("Error al actualizar la historia. Inténtalo de nuevo.");
            console.error("Error updating story: ", err);
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-gray-800 rounded-xl shadow-2xl w-full max-w-lg p-6 text-white relative border border-gray-700">
                <h2 className="text-2xl font-bold mb-6">Editar Historia</h2>
                <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-2 custom-scrollbar">
                    {/* Título */}
                    <div>
                        <label className="block text-sm font-medium text-gray-300">Título</label>
                        <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} className="mt-1 w-full px-4 py-2 text-white bg-gray-700 border border-gray-600 rounded-md focus:border-indigo-500 focus:outline-none"/>
                    </div>
                    
                    {/* ELIMINADO: Bloque de Color de Acento */}

                    {/* Descripción */}
                     <div>
                        <label className="block text-sm font-medium text-gray-300">Descripción</label>
                        <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows="4" className="mt-1 w-full px-4 py-2 text-white bg-gray-700 border border-gray-600 rounded-md focus:border-indigo-500 focus:outline-none"></textarea>
                    </div>

                    {/* --- CAMBIO 4: Autocompletados --- */}
                    <TagAutocomplete
                        label="Personajes Principales"
                        placeholder="Ej. Jax, Pomni, Ragatha..."
                        collectionName="characters"
                        selectedItems={mainCharacters}
                        onItemsChange={setMainCharacters}
                        maxItems={5} // <-- LÍMITE DE 5 AQUI
                    />

                    <TagAutocomplete
                        label="Etiquetas"
                        placeholder="Ej. slow burn, enemies to lovers, magia..."
                        collectionName="tags"
                        selectedItems={tags}
                        onItemsChange={setTags}
                        maxItems={20} // <-- Las etiquetas pueden ser más
                    />

                    {/* Portada */}
                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">Portada</label>
                        <div className="mt-1 flex items-center bg-gray-700/50 p-3 rounded-lg border border-gray-600 border-dashed">
                            {imagePreview && <img src={imagePreview} alt="Cover preview" className="h-24 w-16 object-cover rounded-md mr-4 shadow-md"/>}
                            <label className="cursor-pointer bg-gray-600 hover:bg-gray-500 text-white font-bold py-2 px-4 rounded-md transition-colors text-sm">
                                <span>Cambiar Imagen</span>
                                <input type="file" onChange={handleFileChange} accept="image/png, image/jpeg" className="hidden"/>
                            </label>
                        </div>
                    </div>

                    {/* Checkboxes */}
                    <div className="flex items-center justify-start space-x-6 pt-2">
                        <label className="flex items-center cursor-pointer group">
                            <input type="checkbox" checked={isSensitive} onChange={() => setIsSensitive(!isSensitive)} className="h-5 w-5 rounded text-indigo-500 bg-gray-700 border-gray-600 focus:ring-indigo-600 cursor-pointer" />
                            <span className="ml-2 text-gray-300 group-hover:text-white">Sensible</span>
                        </label>
                        <label className="flex items-center cursor-pointer group">
                            <input type="checkbox" checked={isMature} onChange={() => setIsMature(!isMature)} className="h-5 w-5 rounded text-indigo-500 bg-gray-700 border-gray-600 focus:ring-indigo-600 cursor-pointer" />
                            <span className="ml-2 text-gray-300 group-hover:text-white">Adulto (18+)</span>
                        </label>
                    </div>
                </div>

                {error && <p className="text-red-400 text-sm mt-4 bg-red-900/20 p-2 rounded border border-red-900">{error}</p>}
                
                <div className="flex justify-between items-center pt-6 mt-4 border-t border-gray-700">
                     <button onClick={onDeleteClick} disabled={isSaving} className="px-4 py-2 font-bold text-red-400 hover:bg-red-900/30 rounded-md transition-colors text-sm disabled:opacity-50">
                        Eliminar Historia
                    </button>
                    <div className="flex space-x-3">
                        <button onClick={onClose} disabled={isSaving} className="px-4 py-2 font-bold text-gray-300 hover:text-white bg-gray-700 hover:bg-gray-600 rounded-md transition-colors">Cancelar</button>
                        <button onClick={handleSaveChanges} disabled={isSaving} className="px-6 py-2 font-bold text-white bg-indigo-600 hover:bg-indigo-500 rounded-md disabled:opacity-50 transition-colors shadow-lg shadow-indigo-900/20">
                            {isSaving ? 'Guardando...' : 'Guardar'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

const QuoteSelectorModal = ({ isOpen, onClose, chapter, story, authorProfile, onQuoteSelected, preselectedText }) => {
    // 1. Estados para la selección y validación (HOOKS SIEMPRE PRIMERO)
    const [selectedLineIndices, setSelectedLineIndices] = useState([]); 
    const [currentQuote, setCurrentQuote] = useState(''); 
    const [isValidQuote, setIsValidQuote] = useState(false); 

    // 2. Procesamiento del texto con useMemo (HOOK)
    const plainText = useMemo(() => {
        if (!chapter?.content) return '';
        return chapter.content
            .replace(/<\/p>/gi, '\n') 
            .replace(/<br\s*\/?>/gi, '\n') 
            .replace(/<[^>]+>/g, '') 
            .trim();
    }, [chapter?.content]);
    
    // Otro useMemo (HOOK)
    const lines = useMemo(() => plainText.split('\n').filter(line => line.trim() !== ''), [plainText]);
    useEffect(() => {
        // Solo ejecuta si hay texto preseleccionado y *aún no* hay índices seleccionados
        if (preselectedText && selectedLineIndices.length === 0 && lines.length > 0) {
            // Limpia el texto preseleccionado (quita espacios extra, etc.)
            const cleanPreselected = preselectedText.trim().replace(/\s+/g, ' '); 
            
            // Busca los índices de las líneas que contienen (o son iguales a) partes del texto preseleccionado
            const initialIndices = [];
            let remainingText = cleanPreselected;

            for (let i = 0; i < lines.length; i++) {
                const cleanLine = lines[i].trim().replace(/\s+/g, ' ');
                // Si la línea actual está contenida en el texto restante
                if (remainingText.includes(cleanLine) && cleanLine.length > 0) {
                    initialIndices.push(i);
                    // "Consume" la parte encontrada del texto restante (simplificado)
                    // Esto ayuda si la selección abarca varias líneas, aunque no es perfecto
                    remainingText = remainingText.replace(cleanLine, '').trim(); 
                    // Si ya no queda texto por encontrar, salimos
                    if (remainingText.length === 0) break; 
                } 
                // Si ya encontramos algunas líneas y la actual no coincide, paramos (asume selección contigua)
                else if (initialIndices.length > 0) {
                     break;
                }
            }

            // Si encontramos alguna línea coincidente, la establecemos como selección inicial
            if (initialIndices.length > 0) {
                setSelectedLineIndices(initialIndices);
            }
        }
        // Queremos que esto se ejecute solo cuando el modal se abre O el texto preseleccionado cambia *antes* de interactuar
        // eslint-disable-next-line react-hooks/exhaustive-deps 
    }, [isOpen, preselectedText, lines]); // Depende de isOpen, preselectedText y lines
    // -- FIN: useEffect PARA PRESELECCIÓN --

    // 4. useEffect para recalcular la cita y validar (HOOK) - CORREGIDO
    useEffect(() => {
        if (selectedLineIndices.length === 0) {
            setCurrentQuote('');
            setIsValidQuote(false);
            return;
        }

        // 1. Ordena los índices seleccionados numéricamente
        const sortedIndices = [...selectedLineIndices].sort((a, b) => a - b);
        
        // 2. Construye la cita con elipsis en los saltos
        const quoteParts = [];
        let lastIndex = -2; // Inicializa a un valor que garantice que el primero no sea consecutivo

        sortedIndices.forEach(index => {
            // ++ CAMBIO CLAVE: ++
            // Solo añade elipsis si NO es la primera línea Y no es consecutiva
            if (index > lastIndex + 1 && quoteParts.length > 0) { 
                quoteParts.push('(...)'); // Tu nueva elipsis
            }
            // Añade la línea de texto correspondiente al índice
            quoteParts.push(lines[index]);
            lastIndex = index; // Actualiza el último índice procesado
        });

        // 3. Une las partes. Ya no necesitamos .replace() ni .substring()
        // El 'join' añade el espacio entre la línea y la elipsis.
        let finalQuoteText = quoteParts.join(' ').trim();

        setCurrentQuote(finalQuoteText);

        // 4. Validamos (la lógica de validación no cambia)
        const wordCount = finalQuoteText.split(/\s+/).filter(Boolean).length;
        const charCount = finalQuoteText.length;
        setIsValidQuote(wordCount >= 4 && charCount <= 500 && charCount > 0);

    }, [selectedLineIndices, lines]); // Dependencias sin cambios

    // ++ ESTA ES LA LÍNEA MOVIDA ++
    // Comprobación de visibilidad DESPUÉS de todos los hooks
    if (!isOpen) return null; 
    // -- FIN DE LA LÍNEA MOVIDA --

    // 3. Lógica para manejar el clic en una línea (Función normal, va después de hooks)
    const handleLineClick = (index) => {
        setSelectedLineIndices(prevIndices => {
            if (prevIndices.includes(index)) {
                return prevIndices.filter(i => i !== index);
            } else {
                return [...prevIndices, index]; 
            }
        });
    };
    
    // 5. Función para el botón "Siguiente" (Función normal)
    const handleNext = () => {
        if (isValidQuote) {
            onQuoteSelected(currentQuote); 
        }
    };

    // (Opcional) Calcular palabras/caracteres (Variables normales)
    const wordCount = currentQuote.split(/\s+/).filter(Boolean).length;
    const charCount = currentQuote.length;

    // El return createPortal con el JSX va al final (sin cambios en su contenido interno)
    return createPortal( 
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-[120]" onClick={onClose}>
            <div className="bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl p-6 text-white flex flex-col" onClick={e => e.stopPropagation()}>
                {/* ... El resto del JSX del modal (igual que antes) ... */}
                 <h2 className="text-2xl font-bold mb-2">Selecciona tu Cita</h2>
                 <p className="text-gray-400 mb-4 text-sm">Haz clic en las líneas que quieres compartir.</p>
                 
                 <div className="flex-grow max-h-[60vh] overflow-y-auto space-y-1 p-4 bg-gray-900 rounded-md mb-4 custom-scrollbar">
                     {lines.map((line, index) => {
                        const isSelected = selectedLineIndices.includes(index);
                        // const selectionOrder = isSelected ? selectedLineIndices.indexOf(index) + 1 : null; // YA NO SE NECESITA

                        return (
                            <div 
                                key={index} 
                                onClick={() => handleLineClick(index)}
                                // Quitamos el 'gap-3' y el span vacío para que el texto ocupe todo
                                className={`flex items-start p-2 rounded transition-colors cursor-pointer ${isSelected ? 'bg-indigo-700/50' : 'hover:bg-gray-700/80'}`} 
                            >
                                {/* Párrafo de la línea (ahora ocupa todo el ancho) */}
                                <p className={`flex-grow ${isSelected ? 'text-white' : 'text-gray-300'}`}>
                                    {line}
                                </p>
                            </div>
                        );
                    })}
                 </div>

                 <div className="text-sm text-gray-400 mb-4 flex justify-between items-center px-1">
                      <span>
                          Palabras: {wordCount} | Caracteres: {charCount} / 500
                      </span>
                      {!isValidQuote && currentQuote.length > 0 && ( 
                          <span className="text-red-400 font-semibold">
                              {wordCount < 4 ? 'Mínimo 4 palabras' : (charCount > 500 ? 'Máximo 500 caracteres' : '')}
                          </span>
                      )}
                      {isValidQuote && ( 
                          <span className="text-green-400 font-semibold flex items-center gap-1">
                              ¡Válido!
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                              </svg>
                          </span>
                      )}
                 </div>

                 <div className="flex justify-end space-x-4 pt-4 border-t border-gray-700">
                      <button onClick={onClose} className="px-6 py-2 font-bold text-gray-300 bg-gray-600 rounded-md hover:bg-gray-500">
                          Cancelar
                      </button>
                      <button 
                          onClick={handleNext} 
                          disabled={!isValidQuote} 
                          className="px-6 py-2 font-bold text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                          Siguiente
                      </button>
                 </div>
            </div>
        </div>,
        document.body 
    );
};

// ++ INICIO: NUEVO COMPONENTE PARA LA PLANTILLA DE IMAGEN ++
const ShareableImageTemplate = ({ 
    story, 
    chapter, 
    authorProfile, 
    quoteText, 
    templateId, 
    showBgImage, 
    bgColor, 
    textColor 
}) => {
    
    const coverUrl = story?.coverImageURL || 'https://placehold.co/600x600/1f2937/ffffff?text=No+Image';
    const storyTitle = story?.title || 'Título Desconocido';
    const chapterTitle = chapter?.chapterTitle || 'Capítulo Desconocido';
    const authorName = authorProfile?.displayName || 'Autor Desconocido';
    const quote = quoteText || 'Aquí va la cita seleccionada...';
    const authorColor = textColor === '#FFFFFF' ? '#818cf8' : '#4f46e5';

    return (
        <div 
            id={templateId} // El ID sigue aquí, lo usaremos en el gemelo oculto
            className="w-[600px] h-[600px] bg-gray-800 p-8 flex flex-col justify-between text-white relative" 
            style={{ 
                fontFamily: '"Inria Serif", serif', 
                backgroundColor: showBgImage ? '#1f2937' : bgColor, 
                color: textColor 
            }} 
        >
            {showBgImage && (
                <img 
                    src={coverUrl} 
                    alt="" 
                    className="absolute inset-0 w-full h-full object-cover opacity-15 z-0"
                    crossOrigin="anonymous" 
                />
            )}
            
            <div className="relative z-10 flex flex-col h-full">
            
                <header className="flex items-start gap-4 mb-6"> 
                    <img 
                        src={coverUrl}
                        alt={`${storyTitle} cover`} 
                        className="w-20 h-20 object-cover rounded-lg shadow-md flex-shrink-0"
                        crossOrigin="anonymous" 
                    />
                    <div className="pt-1 pb-1" style={{ marginTop: -8,}}> 
                        <h2 
                            className="text-xl font-bold leading-normal mb-0" 
                            style={{ fontFamily: '"Josefin Slab", serif', color: textColor, opacity: 0.7, marginBottom: -6,}}> 
                            Story:
                        </h2>
                        <h2 
                            className="text-xl font-bold leading-normal mb-0" 
                            style={{ fontFamily: '"Josefin Slab", serif', color: textColor, marginBottom: -3}}> 
                            {storyTitle}
                        </h2>
                        <p 
                            className="text-sm leading-normal mt-0" 
                            style={{ color: textColor, opacity: 0.7, marginBottom: -30, }}> 
                            Chapter: {chapterTitle}
                        </p> 
                    </div>
                </header>

                {/* Cita Principal */}
                <div className="flex-grow flex items-center justify-center text-center px-4">
                    <p 
                        className={`font-semibold ${quote.length > 200 ? 'text-2xl' : (quote.length > 100 ? 'text-3xl' : 'text-4xl')}`} 
                        style={{ lineHeight: '1.4', color: textColor }} 
                    >
                        "{quote}"
                    </p>
                </div>

                {/* Pie: Autor y Logo */}
                <footer className="mt-6 flex justify-between items-end"> 
                    <div 
                        className="text-lg font-bold" 
                        style={{ fontFamily: '"Josefin Slab", serif', color: textColor, opacity: 0.6 }}> 
                        Type<b style={{ color: authorColor, fontStyle: 'italic' }}>fiction</b> 
                    </div>
                    <p 
                        className="text-lg font-medium" 
                        style={{ color: authorColor }}> 
                        — {authorName}
                    </p>
                </footer>
            </div>
        </div>
    );
};
// -- FIN PLANTILLA --

// ++ INICIO: MODAL DE VISTA PREVIA (CORREGIDO OTRA VEZ) ++
const QuotePreviewModal = ({ isOpen, onClose, story, chapter, authorProfile, finalQuote }) => {
    const [isLoading, setIsLoading] = useState(false); 
    const [feedbackMsg, setFeedbackMsg] = useState(''); 
    const templateId = "shareable-quote-image"; 
    const [showBgImage, setShowBgImage] = useState(true); 
    const [bgColor, setBgColor] = useState('#1f2937'); 
    const [textColor, setTextColor] = useState('#FFFFFF'); 
    const [activeSwatchId, setActiveSwatchId] = useState('image'); 
    
    const previewRef = useRef(null); // Ref para el elemento que ROTA y ESCALA
    const colorPickerRef = useRef(null); 
    const [previewScale, setPreviewScale] = useState(1); 
    const wrapperRef = useRef(null); // Ref para el contenedor que MIDE

    const magicColor = useAverageColor(story?.coverImageURL); 

    // showFeedback (sin cambios)
    const showFeedback = (msg) => {
        setFeedbackMsg(msg);
        setTimeout(() => setFeedbackMsg(''), 3000);
    };

    // handleAction (sin cambios)
    const handleAction = async (actionType) => {
        if (isLoading) return;
        setIsLoading(true);
        setFeedbackMsg(''); 

        const element = document.getElementById(templateId);
        if (!element) {
            console.error("Error: No se encontró el elemento de la plantilla.");
            showFeedback("Error al generar la imagen.");
            setIsLoading(false);
            return;
        }

        try {
            const html2canvas = (await import('html2canvas')).default; 
            await document.fonts.ready; 
            await new Promise(resolve => setTimeout(resolve, 100)); 
            
            // eslint-disable-next-line no-unused-vars
            const _forceReflow = element.offsetHeight; 

            const canvas = await html2canvas(element, { 
                useCORS: true, 
                backgroundColor: showBgImage ? '#1f2937' : bgColor,
                scale: 2, 
                logging: true 
            });

            if (actionType === 'download') {
                const imageSrc = canvas.toDataURL('image/png');
                const link = document.createElement('a');
                link.href = imageSrc;
                link.download = `cita_${story?.title || 'historia'}.png`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                showFeedback("¡Imagen descargada!");

            } else if (actionType === 'copy') {
                 if (!navigator.clipboard?.write) {
                    showFeedback("Tu navegador no soporta copiar imágenes directamente.");
                    setIsLoading(false); 
                    return; 
                 }
                canvas.toBlob(async (blob) => {
                    if (blob) {
                        try {
                            await navigator.clipboard.write([ new ClipboardItem({ 'image/png': blob }) ]);
                            showFeedback("¡Imagen copiada al portapapeles!");
                        } catch (copyError) {
                            console.error("Error al copiar imagen:", copyError);
                            showFeedback("No se pudo copiar la imagen.");
                        }
                    } else {
                         showFeedback("Error al generar imagen para copiar.");
                    }
                }, 'image/png');

            } else if (actionType === 'share') {
                canvas.toBlob(async (blob) => {
                    if (blob && navigator.share) {
                        const file = new File([blob], `cita_${story?.title || 'historia'}.png`, { type: 'image/png' });
                        const shareData = {
                            files: [file],
                            title: `Cita de "${story?.title || 'una historia'}"`,
                            text: `"${finalQuote.substring(0, 100)}..."\nLee "${chapter?.chapterTitle || 'este capítulo'}" por ${authorProfile?.displayName || 'su autor'} en Typefiction.`,
                        };

                        if (navigator.canShare && navigator.canShare(shareData)) {
                            try {
                                await navigator.share(shareData);
                            } catch (shareError) {
                                if (shareError.name !== 'AbortError') { 
                                    console.error("Error al compartir:", shareError);
                                    showFeedback("No se pudo compartir la imagen.");
                                }
                            }
                        } else {
                            showFeedback("Tu navegador no puede compartir este tipo de archivo.");
                        }
                    } else if (!navigator.share) {
                        showFeedback("Tu navegador no soporta la función de compartir directamente. Intenta copiar o descargar.");
                    } else {
                        showFeedback("Error al generar imagen para compartir.");
                    }
                }, 'image/png');
            }

        } catch (error) {
            console.error("Error con html2canvas:", error);
            showFeedback("Error al generar la imagen.");
        } finally {
            setIsLoading(false);
        }
    };
    
    // getContrastColor (sin cambios)
    const getContrastColor = (hexColor) => {
        if (!hexColor) return '#FFFFFF';
        const hex = hexColor.replace('#', '');
        const r = parseInt(hex.substring(0, 2), 16);
        const g = parseInt(hex.substring(2, 4), 16);
        const b = parseInt(hex.substring(4, 6), 16);
        const yiq = ((r * 299) + (g * 587) + (b * 114)) / 1000;
        return (yiq >= 128) ? '#000000' : '#FFFFFF'; 
    };

    // useEffect para color (sin cambios)
    useEffect(() => {
        if (!showBgImage) { 
            setTextColor(getContrastColor(bgColor));
        } else {
            setTextColor('#FFFFFF'); 
        }
    }, [bgColor, showBgImage]);

    // useLayoutEffect para escala (sin cambios)
    useLayoutEffect(() => {
        if (isOpen && wrapperRef.current) {
            const updateScale = () => {
                const parentWidth = wrapperRef.current.offsetWidth;
                const scale = Math.min(1, parentWidth / 600);
                setPreviewScale(scale);
            };
            updateScale(); 
            window.addEventListener('resize', updateScale); 
            return () => window.removeEventListener('resize', updateScale);
        }
    }, [isOpen]); 

    // Lógica de swatches (sin cambios)
    const swatches = useMemo(() => [
        { id: 'image', type: 'image', value: story?.coverImageURL || 'https://placehold.co/50x50/333/fff?text=IMG', title: 'Imagen de Fondo'},
        { id: 'magic', type: 'color', value: magicColor || '#1f2937', title: 'Color Mágico (promedio de la imagen)'},
        { id: 'black', type: 'color', value: '#111827', title: 'Fondo Negro'},
        { id: 'white', type: 'color', value: '#F9FAFB', title: 'Fondo Blanco'},
        { id: 'indigo', type: 'color', value: '#4f46e5', title: 'Fondo Índigo'},
        { id: 'custom', type: 'custom', title: 'Elegir color'}
    ], [story?.coverImageURL, magicColor]);

    const handleSwatchClick = (swatch) => {
        setActiveSwatchId(swatch.id);
        if (swatch.type === 'image') {
            setShowBgImage(true);
        } else if (swatch.type === 'color') {
            setShowBgImage(false);
            setBgColor(swatch.value);
        } else if (swatch.type === 'custom') {
            colorPickerRef.current.click();
        }
    };

    const handleCustomColorChange = (e) => {
        const newColor = e.target.value;
        setShowBgImage(false);
        setBgColor(newColor);
        setActiveSwatchId('custom'); 
    };
    
    // --- ++ FUNCIONES DE MOUSE 3D CORREGIDAS ++ ---
    const handleMouseMove = (e) => {
        if (!previewRef.current) return;
        const rect = previewRef.current.getBoundingClientRect();
        
        // Coordenadas relativas al *centro* del elemento
        // (rect.width / previewScale) es el ancho real (600px)
        // (e.clientX - rect.left) es el clic del mouse relativo al borde izquierdo del *elemento escalado*
        // (x / previewScale) lo convierte a coordenada 0-600
        const x_original = (e.clientX - rect.left) / previewScale;
        const y_original = (e.clientY - rect.top) / previewScale;

        // Ahora calculamos la rotación basada en las coordenadas originales (0-600)
        const rotateY = (x_original - 300) / 30; // (x - 300) va de -300 a 300. /30 = -10 a 10 grados
        const rotateX = (y_original - 300) / -30; // (y - 300) va de -300 a 300. /-30 = 10 a -10 grados
        
        // Aplicamos scale (que es constante) y la nueva rotación
        previewRef.current.style.transform = `perspective(1000px) translateX(-50%) scale(${previewScale}) rotateX(${rotateX}deg) rotateY(${rotateY}deg)`;
    };

    const handleMouseLeave = () => {
        if (!previewRef.current) return;
        previewRef.current.style.transition = 'transform 0.6s cubic-bezier(0.23, 1, 0.32, 1)';
        // Vuelve a la escala base, centrado, sin rotación
        previewRef.current.style.transform = `perspective(1000px) translateX(-50%) scale(${previewScale}) rotateX(0deg) rotateY(0deg)`;
    };
    
     const handleMouseEnter = () => {
        if (!previewRef.current) return;
        // Establece la transformación inicial (con escala) y prepara la transición rápida
        previewRef.current.style.transform = `perspective(1000px) translateX(-50%) scale(${previewScale}) rotateX(0deg) rotateY(0deg)`;
        previewRef.current.style.transition = 'transform 0.1s ease-out';
     };
     // --- -- FIN FUNCIONES 3D CORREGIDAS -- ---

    if (!isOpen) return null; 

    return createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-[100] p-4" onClick={onClose}> 

            {/* Animación de Brillo (sin cambios) */}
            <style>{`
                @keyframes shine-left-to-right {
                    0% { left: -60%; } 
                    100% { left: 110%; } 
                }
                .animate-shine::after {
                    content: '';
                    position: absolute;
                    top: 0;
                    left: -60%; 
                    width: 50%; 
                    height: 100%;
                    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.1), transparent);
                    animation: shine-left-to-right 3s infinite linear 1s; 
                    transform: skewX(-25deg); 
                    pointer-events: none; 
                    z-index: 11; 
                }
            `}</style>
            
            {/* Modal principal */}
            <div className="bg-gray-800 rounded-lg shadow-xl w-full max-w-[600px] p-4 sm:p-6 text-white flex flex-col items-center" onClick={e => e.stopPropagation()}> 
                
                {/* --- ++ INICIO: EL GEMELO OCULTO (PARA CAPTURA) ++ --- */}
                {/* Este div está fuera de la pantalla. No tiene transforms ni animaciones. */}
                {/* html2canvas capturará ESTE elemento usando 'templateId' */}
                <div className="absolute -left-[9999px] top-0 opacity-0 pointer-events-none">
                    <ShareableImageTemplate 
                       story={story} 
                       chapter={chapter} 
                       authorProfile={authorProfile} 
                       quoteText={finalQuote}
                       templateId={templateId} // <-- El ID de captura está AQUÍ
                       showBgImage={showBgImage}
                       bgColor={bgColor}
                       textColor={textColor}
                    />
                </div>
                {/* --- FIN DEL GEMELO OCULTO --- */}


                <h2 className="text-2xl font-bold mb-4">Vista Previa y Compartir</h2>
                
                {/* --- ++ PREVIEW VISIBLE (PARA EL USUARIO) ++ --- */}
                {/* 1. Contenedor Exterior: Mide el ancho y define la altura escalada. */}
                <div 
                    className="w-full max-w-[600px] mb-6 relative" 
                    ref={wrapperRef} 
                    style={{ 
                        perspective: '1000px',
                        height: `${600 * previewScale}px` 
                    }} 
                >
                    {/* 2. Contenedor Interior: Se escala, rota, y tiene los listeners */}
                    <div
                        className="border border-gray-600 rounded-lg relative overflow-hidden animate-shine" 
                        ref={previewRef} 
                        onMouseMove={handleMouseMove}
                        onMouseLeave={handleMouseLeave}
                        onMouseEnter={handleMouseEnter}
                        style={{
                            width: '600px', 
                            height: '600px', 
                            position: 'absolute', 
                            top: 0,
                            left: '50%', 
                            transformOrigin: 'center', 
                            transform: `perspective(1000px) translateX(-50%) scale(${previewScale})`, 
                            transition: 'transform 0.6s cubic-bezier(0.23, 1, 0.32, 1)'
                        }}
                    >
                        {/* Esta es la plantilla VISIBLE. Fíjate que NO tiene 'templateId' */}
                        <ShareableImageTemplate 
                           story={story} 
                           chapter={chapter} 
                           authorProfile={authorProfile} 
                           quoteText={finalQuote}
                           templateId={null} // <-- SIN ID
                           showBgImage={showBgImage}
                           bgColor={bgColor}
                           textColor={textColor}
                        />
                    </div>
                </div>
                {/* -- FIN PREVIEW -- */}

                {/* Controles de Swatch (sin cambios) */}
                <div className="mb-4 w-full">
                    <div className="flex items-center gap-3 overflow-x-auto pb-2 px-1">
                        {swatches.map((swatch) => {
                            const isActive = activeSwatchId === swatch.id;
                            return (
                                <button
                                    key={swatch.id}
                                    title={swatch.title}
                                    onClick={() => handleSwatchClick(swatch)}
                                    className={`flex-shrink-0 w-10 h-10 p-0.5 rounded-full transition-all duration-150 ${isActive ? 'ring-2 ring-offset-2 ring-offset-gray-800 ring-indigo-400' : 'ring-0 hover:ring-1 hover:ring-gray-500'}`}
                                >
                                    <div 
                                        className="w-full h-full rounded-full border border-white/20 flex items-center justify-center"
                                        style={{
                                            backgroundImage: swatch.type === 'image' ? `url(${swatch.value})` : 'none',
                                            backgroundColor: swatch.type === 'color' ? swatch.value : 'transparent', 
                                            backgroundSize: 'cover',
                                            backgroundPosition: 'center',
                                        }}
                                    >
                                        {swatch.id === 'image' && !story?.coverImageURL && ( 
                                             <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
                                                <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
                                             </svg>
                                        )}
                                        {swatch.id === 'magic' && !magicColor && ( 
                                             <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-purple-400 animate-pulse" viewBox="0 0 20 20" fill="currentColor">
                                                <path fillRule="evenodd" d="M10 3a.75.75 0 01.75.75v1.5a.75.75 0 01-1.5 0v-1.5A.75.75 0 0110 3zM13.03 5.47a.75.75 0 111.06 1.06l-1.06 1.06a.75.75 0 01-1.06-1.06l1.06-1.06zM16.25 10a.75.75 0 01.75.75v1.5a.75.75 0 01-1.5 0v-1.5a.75.75 0 01.75-.75zM14.09 13.03a.75.75 0 111.06 1.06l-1.06 1.06a.75.75 0 01-1.06-1.06l1.06-1.06zM10 16.25a.75.75 0 01.75.75v1.5a.75.75 0 01-1.5 0v-1.5a.75.75 0 01.75-.75zM5.91 14.09a.75.75 0 111.06-1.06l1.06 1.06a.75.75 0 01-1.06 1.06l-1.06-1.06zM3.75 10a.75.75 0 01.75.75v1.5a.75.75 0 01-1.5 0v-1.5A.75.75 0 013.75 10zm1.06-3.03a.75.75 0 011.06-1.06l1.06 1.06a.75.75 0 11-1.06 1.06l-1.06-1.06z" clipRule="evenodd" />
                                                <path d="M10 6.364a3.636 3.636 0 110 7.272 3.636 3.636 0 010-7.272zM12.828 10a2.828 2.828 0 10-5.656 0 2.828 2.828 0 005.656 0z" />
                                             </svg>
                                        )}
                                        {swatch.id === 'custom' && (
                                            <div className="w-full h-full rounded-full" style={{ background: 'conic-gradient(from 180deg at 50% 50%, #ff0000, #ff00ff, #0000ff, #00ffff, #00ff00, #ffff00, #ff0000)' }}></div>
                                        )}
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                    <input 
                        type="color" 
                        ref={colorPickerRef}
                        value={bgColor}
                        onChange={handleCustomColorChange}
                        className="w-0 h-0 p-0 border-0 absolute opacity-0" 
                    />
                </div>
                
                {/* Feedback y Botones de Acción (sin cambios) */}
                {feedbackMsg && ( <p className={`text-sm mb-4 ${feedbackMsg.includes('Error') ? 'text-red-400' : 'text-green-400'}`}>{feedbackMsg}</p> )}
                <div className="flex flex-wrap justify-center gap-3 w-full">
                    {navigator.share && navigator.canShare && ( <button onClick={() => handleAction('share')} disabled={isLoading} className="flex-1 min-w-[140px] px-5 py-2 font-bold text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"><svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor"><path d="M15 8a3 3 0 10-2.977-2.63l-4.94 2.47a3 3 0 100 4.319l4.94 2.47a3 3 0 10.895-1.789l-4.94-2.47a3.027 3.027 0 000-.74l4.94-2.47C13.456 7.68 14.19 8 15 8z" /></svg>{isLoading ? '...' : 'Compartir...'}</button> )}
                    {navigator.clipboard?.write && ( <button onClick={() => handleAction('copy')} disabled={isLoading} className="flex-1 min-w-[140px] px-5 py-2 font-bold text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-50 flex items-center justify-center gap-2"><svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor"><path d="M7 3a1 1 0 000 2h6a1 1 0 100-2H7zM4 7a1 1 0 011-1h10a1 1 0 110 2H5a1 1 0 01-1-1zM2 11a2 2 0 012-2h12a2 2 0 012 2v4a2 2 0 01-2 2H4a2 2 0 01-2-2v-4z" /></svg>{isLoading ? '...' : 'Copiar Imagen'}</button> )}
                    <button onClick={() => handleAction('download')} disabled={isLoading} className="flex-1 min-w-[140px] px-5 py-2 font-bold text-white bg-gray-600 rounded-md hover:bg-gray-500 disabled:opacity-50 flex items-center justify-center gap-2"><svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" /></svg>{isLoading ? '...' : 'Descargar'}</button>
                </div>
                 <button onClick={onClose} disabled={isLoading} className="mt-6 text-sm text-gray-400 hover:text-white">Cerrar</button>
            </div>
        </div>,
        document.body
    );
};
// -- FIN: MODAL DE VISTA PREVIA --

// ==================================================================================
// 4. MAIN PAGE COMPONENTS
// ==================================================================================
const AuthPage = () => {
    const [isLogin, setIsLogin] = useState(true);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [username, setUsername] = useState('');
    const [error, setError] = useState('');
    const handleCreateUserDocs = async (user, uName, dName, isGoogleSignIn = false) => {
    const userDocRef = doc(db, "users", user.uid);
    const usernameRef = doc(db, "usernames", uName.toLowerCase());
    const batch = writeBatch(db);
    const keywords = [...new Set([
        ...generateKeywords(dName), 
        ...generateKeywords(uName)
    ])];

    batch.set(userDocRef, {
        displayName: dName,
        username: uName.toLowerCase(),
        email: user.email,
        description: '',
        avatarURL: user.photoURL || '',
        followerCount: 0,
        followingCount: 0,
        lastUsernameChange: Timestamp.fromMillis(0),
        onboardingComplete: false,
        searchKeywords: keywords,
        interests: [],
        donationsConfig: {
            isEnabled: false,
            provider: null,
            paypalEmail: null,
            stripeAccountId: null,
            widgetConfig: {
                ctaMessage: "Si te gustó la historia, ¡apoya al autor!",
                backgroundColor: "#1F2937",
                showAmount: true,
                buttonText: "Donar"
                         }
         }
    });

    batch.set(usernameRef, {
        userId: user.uid 
    });
        
    await batch.commit(); 
};

    const handleSubmit = async (e) => { e.preventDefault(); setError('');
        if (isLogin) { try { await signInWithEmailAndPassword(auth, email, password);}
        catch (err) { setError("Email o contraseña incorrectos."); } }
        else {
            if (!username || !email || !password) {
                setError("Todos los campos son obligatorios.");
                return; }
                
                if (username.length < 3) {
                    setError("El nombre de usuario debe tener al menos 3 caracteres.");
                    return; }
                    
                    try {
                        const usernameRef = doc(db, "usernames", username.toLowerCase());
                        const usernameSnap = await getDoc(usernameRef);
                        if (usernameSnap.exists()) {
                            setError("Este nombre de usuario ya está en uso. Por favor, elige otro.");
                            return; }
                            
                        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
                        await handleCreateUserDocs(userCredential.user, username, username);
                        await sendEmailVerification(userCredential.user); }
                        
                        catch (err) { if (err.code === 'auth/email-already-in-use') { setError("Este correo electrónico ya está en uso."); }
                        
                        else if (err.code === 'auth/weak-password') { setError("La contraseña debe tener al menos 6 caracteres."); }
                        else { setError("Error al registrarse. Inténtalo de nuevo."); } } } };


const handleGoogleSignIn = async () => {
    setError('');
    const provider = new GoogleAuthProvider();
    try {
        const result = await signInWithPopup(auth, provider);
        const user = result.user;
        const userDocRef = doc(db, "users", user.uid);
        const userDocSnap = await getDoc(userDocRef);

        if (!userDocSnap.exists()) {
            // Esta es la lógica para un usuario nuevo
            const initialUsername = user.email.split('@')[0].replace(/[^a-zA-Z0-9]/g, '');
            const usernameRef = doc(db, "usernames", initialUsername);
            const usernameSnap = await getDoc(usernameRef);
            let finalUsername = initialUsername;

            if (usernameSnap.exists()) {
                finalUsername = `${initialUsername}${Date.now().toString().slice(-4)}`;
            }

            await handleCreateUserDocs(user, finalUsername, user.displayName);
            
            // ======================================================
            // ++ LÍNEA AÑADIDA PARA SOLUCIONAR EL PROBLEMA ++
            await sendEmailVerification(user);
            // ======================================================

        }
    } catch (err) {
        setError(err.message);
    }
};

    return (
        <div className="flex items-center justify-center min-h-screen bg-black">
            <div className="w-full max-w-md p-8 space-y-6 rounded-xl shadow-lg CONTAINER_LOGIN">
                    <h1 className="text-3xl font-bold text-center text-white">
                    {isLogin ? (
                        <>
                        <p className="text-4xl font-bold text-white transition-colors mt-4 mb-6 LOGO_auth">Type<b>fiction</b></p>
                        
                                {/* <div className="flex items-center justify-center">
                                <span className="w-full h-px bg-gray-600"></span>
                                </div> */}

                        <span className="LOGIN_TYPE">INT. APP / DAY 01</span>
                        <span className="LOGIN_TYPE02"> - The login</span>

                        <p className="text-white transition-colors SUB_auth">Porque las mejores historias, nacen desde la creatividad. Y la creatividad nace desde las sombras. Continúa tu aventura...</p>
                        </>
                    ) : (
                        'Crear una Cuenta'
                    )}
                    </h1>
                
                <div className="w-full max-w-md h-18 flex gap-8 SERVICES_LOGIN">
                    
                    <button onClick={handleGoogleSignIn} className="w-full flex items-center justify-center px-4 py-2 font-medium text-white bg-red-600 rounded-md hover:bg-gray-700">
                        <svg className="w-5 h-5 mr-2" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8c-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039L38.804 8.841C34.524 4.938 29.507 2 24 2C11.854 2 2 11.854 2 24s9.854 22 22 22s22-9.854 22-22c0-1.341-.138-2.65-.389-3.917z"></path><path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.843-5.843A21.928 21.928 0 0 0 24 2C16.318 2 9.656 6.125 6.306 14.691z"></path><path fill="#4CAF50" d="M24 46c5.507 0 10.524-2.938 14.804-6.841l-5.843-5.843A11.956 11.956 0 0 1 24 36c-5.223 0-9.651-3.657-11.303-8H6.306C9.656 37.875 16.318 42 24 42z"></path><path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303a12.028 12.028 0 0 1-5.344 5.396l5.843 5.843A21.93 21.93 0 0 0 44 24c0-1.341-.138-2.65-.389-3.917z"></path></svg>
                        Google
                    </button>

                    <button onClick={handleGoogleSignIn} className="w-full flex items-center justify-center px-4 py-2 font-medium text-white bg-red-600 rounded-md hover:bg-gray-700">
                        <svg className="w-5 h-5 mr-2" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8c-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039L38.804 8.841C34.524 4.938 29.507 2 24 2C11.854 2 2 11.854 2 24s9.854 22 22 22s22-9.854 22-22c0-1.341-.138-2.65-.389-3.917z"></path><path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.843-5.843A21.928 21.928 0 0 0 24 2C16.318 2 9.656 6.125 6.306 14.691z"></path><path fill="#4CAF50" d="M24 46c5.507 0 10.524-2.938 14.804-6.841l-5.843-5.843A11.956 11.956 0 0 1 24 36c-5.223 0-9.651-3.657-11.303-8H6.306C9.656 37.875 16.318 42 24 42z"></path><path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303a12.028 12.028 0 0 1-5.344 5.396l5.843 5.843A21.93 21.93 0 0 0 44 24c0-1.341-.138-2.65-.389-3.917z"></path></svg>
                    
                    </button>

                    <button onClick={handleGoogleSignIn} className="w-full flex items-center justify-center px-4 py-2 font-medium text-white bg-red-600 rounded-md hover:bg-gray-700">
                        <svg className="w-5 h-5 mr-2" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8c-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039L38.804 8.841C34.524 4.938 29.507 2 24 2C11.854 2 2 11.854 2 24s9.854 22 22 22s22-9.854 22-22c0-1.341-.138-2.65-.389-3.917z"></path><path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.843-5.843A21.928 21.928 0 0 0 24 2C16.318 2 9.656 6.125 6.306 14.691z"></path><path fill="#4CAF50" d="M24 46c5.507 0 10.524-2.938 14.804-6.841l-5.843-5.843A11.956 11.956 0 0 1 24 36c-5.223 0-9.651-3.657-11.303-8H6.306C9.656 37.875 16.318 42 24 42z"></path><path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303a12.028 12.028 0 0 1-5.344 5.396l5.843 5.843A21.93 21.93 0 0 0 44 24c0-1.341-.138-2.65-.389-3.917z"></path></svg>
                    
                    </button>
                    
                </div>

                <div className="flex items-center justify-center">
                    <span className="w-20 h-px bg-gray-600"></span>
                    <span className="mx-4 text-gray-500">o</span>
                    <span className="w-20 h-px bg-gray-600"></span>
                </div>
                
                <form onSubmit={handleSubmit} className="space-y-6">{!isLogin && (<input type="text" value={username} onChange={(e) => setUsername(e.target.value)} placeholder="Elige tu @nombredeusuario" className="w-full px-4 py-2 text-white bg-gray-700 border border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"/>)}
                    <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Correo Electrónico" className="w-full px-4 py-2 text-white bg-gray-700 border border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500" />
                    <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Contraseña" className="w-full px-4 py-2 text-white bg-gray-700 border border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"/>
                    {error && <p className="text-red-500 text-sm">{error}</p>}
                    <button type="submit" className="w-full px-4 py-2 font-bold text-white bg-indigo-600 rounded-md hover:bg-indigo-700">{isLogin ? 'Login' : 'Registrarse'}</button>
                </form>

                <p className="text-center text-sm text-gray-400">{isLogin ? "¿No tienes una cuenta?" : '¿Ya tienes una cuenta?'} <button onClick={() => setIsLogin(!isLogin)} className="font-medium text-indigo-400 hover:text-indigo-500 ml-1">{isLogin ? 'Regístrate' : 'Inicia sesión'}</button></p>
            </div>
        </div> );
};

const FEATURED_STORY_ID = "4ClK6RDLZem00Zr7mofg";



const Dashboard = ({ userProfile }) => {
    // --- ESTADOS QUE SÍ USAMOS ---
    
    // 1. Recomendaciones (Tags y Exclusiones)
    const [excludedStoryIds, setExcludedStoryIds] = useState([]);
    const [recTags, setRecTags] = useState([]);

    // 2. Hero (Historia Destacada Principal)
    const [featuredStory, setFeaturedStory] = useState(null);
    const [featuredAuthor, setFeaturedAuthor] = useState(null);
    const [loadingFeatured, setLoadingFeatured] = useState(true);

    const bannerData = {
        desktopImage: 'https://routenote.com/es/blog/wp-content/uploads/2025/03/og-kickstarter-social-new-d68ea5b8257b3ff7ec063993ddc0f9662494309832feb40117fc936fa1c819a2-1.png', 
        mobileImage: 'https://images.unsplash.com/photo-1535905557558-afc4877a26fc?q=80&w=1000&auto=format&fit=crop',
        title: "Help us reach our goal!",
        // subtitle: "Explore our best features!"
    };

    // EFECTO 1: Preparar Recomendaciones (Tags del usuario y lista de Likes para excluir si quisieras)
    useEffect(() => {
        const prepareRecommendations = async () => {
            if (!userProfile) return;

            // A. Calcular los tags favoritos
            const tags = getTopUserTags(userProfile);
            setRecTags(tags);

            // B. Obtener IDs de historias ya likeadas (Opcional, por si decides activarlo luego)
            try {
                const q = query(
                    collection(db, 'collections'), 
                    where('ownerId', '==', userProfile.uid),
                    where('isDefaultLiked', '==', true),
                    limit(1)
                );
                const snapshot = await getDocs(q);
                
                if (!snapshot.empty) {
                    const likedData = snapshot.docs[0].data();
                    setExcludedStoryIds(likedData.storyIds || []);
                }
            } catch (error) {
                console.error("Error preparando exclusiones:", error);
            }
        };

        prepareRecommendations();
    }, [userProfile]);

    // EFECTO 2: Cargar Historia Destacada (Hero)
    useEffect(() => {
        const fetchHero = async () => {
            setLoadingFeatured(true);
            try {
                if (FEATURED_STORY_ID) {
                    const featRef = doc(db, 'stories', FEATURED_STORY_ID);
                    const featSnap = await getDoc(featRef);
                    if (featSnap.exists()) {
                        const featData = { id: featSnap.id, ...featSnap.data() };
                        setFeaturedStory(featData);
                        if (featData.authorId) {
                            const userSnap = await getDoc(doc(db, 'users', featData.authorId));
                            if (userSnap.exists()) setFeaturedAuthor(userSnap.data());
                        }
                    }
                }
                // Ya no cargamos "Latest Stories" ni "Collections" aquí, así que borramos ese código
            } catch (error) {
                console.error("Error en dashboard hero:", error);
            } finally {
                setLoadingFeatured(false);
            }
        };

        fetchHero();
    }, []);

    return (
        <div className="min-h-screen w-full">
            {/* --- SECTION HERO --- */}
            <div className="relative w-full mb-12">
                <div className="md:hidden block">
                    <div className="sticky top-0 w-full h-[50vh] z-0">
                        <img src={bannerData.mobileImage} alt="Mobile Banner" className="w-full h-full object-cover opacity-100" />
                        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black"></div>
                    </div>
                    <div className="relative z-10 px-6 -mt-24 pb-8 flex justify-center">
                        {loadingFeatured ? (
                            <div className="h-96 w-full max-w-[320px] bg-gray-800 rounded-[34px] animate-pulse"></div>
                        ) : featuredStory ? (
                            <STORYCARD_DEST story={featuredStory} authorProfile={featuredAuthor} className="!m-0 !w-full !h-auto shadow-[0_0_50px_rgba(0,0,0,0.7)] TEST_02 STCRD_DEST" />
                        ) : null}
                    </div>
                </div>

                <div className="hidden md:block max-w-6xl mx-auto px-8 pt-8">
                    <div className="flex flex-row gap-6 h-[400px] items-center">
                        <div className="flex-[3] h-full relative rounded-[40px] overflow-hidden border border-[#2D2F31] shadow-2xl group">
                            <img src={bannerData.desktopImage} alt="Banner" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                            {/* <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent"></div> */}
                            <div className="absolute inset-0"></div>
                            {/* <div className="absolute bottom-10 left-10">
                                <h2 className="text-4xl mb-2 BANNER_TEXT drop-shadow-lg">
                                    {bannerData.title} <br/> <span className="text-[#44EFFF]">{bannerData.subtitle}</span>
                                </h2>
                                <p className="text-gray-300 font-inria italic">Learn more in our Kickstarter!</p>
                            </div> */}
                        </div>
                        <div className="flex-1 flex justify-center relative TEST_01">
                            {loadingFeatured ? (
                                <div className="w-full h-full rounded-[34px] animate-pulse"></div>
                            ) : featuredStory ? (
                                <STORYCARD_DEST story={featuredStory} authorProfile={featuredAuthor} className="!m-0 !w-full !h-auto shadow-[0_0_40px_rgba(0,0,0,0.5)] DEST STCRD_DEST" />
                            ) : (
                                <div className="text-white">No hay historia destacada</div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* --- SECCIÓN PRINCIPAL (CARRILES) --- */}
            <div className="relative z-10 bg-black pb-20 pt-4 md:pt-10 rounded-t-[3rem] md:rounded-none border-t border-white/10 md:border-none shadow-[0_-10px_40px_rgba(0,0,0,0.8)]">
                <div className="max-w-6xl mx-auto px-0 md:px-8 space-y-8">
                    
                    {/* 1. CARRILES DE RECOMENDACIÓN (Basado en Tags) */}
                    {userProfile && recTags.length > 0 && (
                        <section className="pt-4">
                            {recTags.map(tag => (
                                <RecommendationRow 
                                    key={tag}
                                    tag={tag}
                                    title={`Porque te gusta "${tag}"`}
                                    excludedStoryIds={excludedStoryIds}
                                    // Ya no pasamos authorProfilesMap porque RecommendationRow lo gestiona solo
                                />
                            ))}
                        </section>
                    )}

                    {/* 2. CARRIL DE POPULARES (Siempre visible) */}
                    <section>
                        <PopularStoriesRow 
                            // Ya no pasamos authorProfilesMap
                        />
                    </section>

                </div>
            </div>
        </div>
    );
};

const CollectionCard = ({ collection, authorProfile }) => {
    const cardRef = useRef(null);

    // Efecto 3D (Igual que StoryCard para consistencia)
    const handleMouseMove = (e) => {
        if (!cardRef.current) return;
        const { clientX, clientY } = e;
        const { width, height, left, top } = cardRef.current.getBoundingClientRect();
        const x = clientX - left;
        const y = clientY - top;
        const rotateX = 10 * ((y - height / 2) / (height / 2)) * -1;
        const rotateY = 10 * ((x - width / 2) / (width / 2));
        cardRef.current.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale3d(1.05, 1.05, 1.05)`;
    };

    const handleMouseEnter = () => {
        if (!cardRef.current) return;
        cardRef.current.style.transition = 'transform 0.1s ease-out';
        cardRef.current.style.zIndex = '10';
    };

    const handleMouseLeave = () => {
        if (!cardRef.current) return;
        cardRef.current.style.transition = 'transform 0.6s cubic-bezier(0.23, 1, 0.32, 1)';
        cardRef.current.style.transform = 'perspective(1000px) rotateX(0deg) rotateY(0deg) scale3d(1, 1, 1)';
        cardRef.current.style.zIndex = '1';
    };

    // Fallback para saber quién es el dueño (si viene el objeto authorProfile o solo el ID)
    const displayOwner = authorProfile?.displayName || "Usuario";

    return (
        <Link
            to={`/collection/${collection.id}`}
            ref={cardRef}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            onMouseEnter={handleMouseEnter}
            className="relative block group h-full"
        >
            {/* Efecto de "Pila" visual detrás de la tarjeta */}
            <div className="absolute top-1 -right-1 w-full h-full bg-gray-700 rounded-lg border border-gray-600 transform rotate-3 transition-transform group-hover:rotate-6 z-0 CollectionCard Card_back2"></div>
            <div className="absolute top-2 -right-2 w-full h-full bg-gray-800 rounded-lg border border-gray-700 transform rotate-1 transition-transform group-hover:rotate-4 z-0 CollectionCard Card_back1"></div>

            {/* Tarjeta Principal */}
            <div className="relative bg-gray-900 rounded-lg overflow-hidden shadow-xl border border-gray-700 z-10 h-full flex flex-col CollectionCard">
                {/* Portada */}
                <div className="relative h-48 overflow-hidden bg-gray-800">
                    {collection.coverImageURL ? (
                        <img src={collection.coverImageURL} alt={collection.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                    ) : (
                        <div className="w-full h-full flex items-center justify-center text-gray-600">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" /></svg>
                        </div>
                    )}
                    
                    {/* Badge de Privacidad o Tipo */}
                    <div className="absolute top-2 right-2">
                        {collection.visibility === 'private' && (
                            <span className="bg-black/70 text-white text-xs px-2 py-1 rounded backdrop-blur-sm flex items-center gap-1">
                                🔒 Privada
                            </span>
                        )}
                        {collection.isDefaultLiked && (
                            <span className="bg-red-600 text-white text-xs px-2 py-1 rounded shadow-lg flex items-center gap-1">
                                ❤️ Favoritos
                            </span>
                        )}
                    </div>
                </div>

                {/* Contenido */}
                <div className="p-4 flex-grow flex flex-col">
                    <h3 className="text-lg font-bold text-white leading-tight mb-1 line-clamp-2 group-hover:text-indigo-400 transition-colors">
                        {collection.title}
                    </h3>
                    <p className="text-xs text-gray-400 mb-3">
                        de {displayOwner}
                    </p>
                    
                    <div className="mt-auto pt-3 border-t border-gray-800 flex justify-between items-center text-xs text-gray-500 font-medium uppercase tracking-wider">
                        <span className="flex items-center gap-1">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" /></svg>
                            {collection.storyCount || 0} Historias
                        </span>
                        <span>
                            {collection.lastUpdatedAt ? new Date(collection.lastUpdatedAt.seconds * 1000).toLocaleDateString() : ''}
                        </span>
                    </div>
                </div>
            </div>
        </Link>
    );
};

const StoryCard = ({ story, authorProfile, className = "" }) => { 
    const cardRef = useRef(null);

    const handleMouseMove = (e) => {
        if (!cardRef.current) return;
        const { clientX, clientY } = e;
        const { width, height, left, top } = cardRef.current.getBoundingClientRect();
        const x = clientX - left;
        const y = clientY - top;
        const rotateX = 10 * ((y - height / 2) / (height / 2)) * -1;
        const rotateY = 10 * ((x - width / 2) / (width / 2));
        cardRef.current.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale3d(1.16, 1.16, 1.16)`;
    };

    const handleMouseEnter = () => {
        if (!cardRef.current) return;
        cardRef.current.style.transition = 'transform 0.1s ease-out';
        cardRef.current.style.zIndex = '10';
    };

    const handleMouseLeave = () => {
        if (!cardRef.current) return;
        cardRef.current.style.transition = 'transform 0.6s cubic-bezier(0.23, 1, 0.32, 1)';
        cardRef.current.style.transform = 'perspective(1000px) rotateX(0deg) rotateY(0deg) scale3d(1, 1, 1)';
        cardRef.current.style.zIndex = '1';
    };

    // --- LÓGICA DE FALLBACK ---
    // Si 'authorProfile' (de Firestore) existe, úsalo.
    // Si no, usa el 'story' (de Typesense) que puede tener 'authorUsername'.
    const displayUsername = authorProfile?.username || story?.authorUsername;
    // --- FIN LÓGICA ---

    return (
        <Link
            key={story.id} 
            to={`/story/${story.id}`}
            ref={cardRef}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            onMouseEnter={handleMouseEnter}
            className={`STORY_CARD relative block group ${className}`}
        >
            <div className="relative w-full h-full">
                <img
                    src={story.coverImageURL || 'https://placehold.co/600x800/1f2937/ffffff?text=No+Image'}
                    alt={`${story.title} cover`}
                    className="card-child card-image w-full h-64 object-cover"
                />
                <div className="card-child card-content p-4">
                    <h3 className="text-lg font-bold text-white">{story.title}</h3>
                    <p className="text-sm text-gray-400 mt-1 h-[60px] overflow-hidden">
                        {story.description 
                            ? (story.description.length > 100 ? story.description.substring(0, 100) + '...' : story.description) 
                            : 'Esta historia aún no tiene una descripción.'
                        }
                    </p>
                    <div className="flex justify-between items-center mt-4 pt-2 border-t border-gray-700">
                        
                        {/* --- ¡AQUÍ SE USA EL FALLBACK! --- */}
                        {displayUsername ? (
                            <p className="text-xs text-gray-500">por @{displayUsername}</p>
                        ) : (
                            // Si no hay NINGÚN username, muestra el loader
                            <p className="text-xs text-gray-700 animate-pulse rounded">cargando autor...</p>
                        )}
                        {/* --- FIN DE LA CORRECCIÓN --- */}

                        <p className="text-xs text-gray-500 whitespace-nowrap">{formatTimestamp(story.lastUpdatedAt)}</p>
                    </div>
                </div>
            </div>
        </Link>
    );
};

const STORYCARD_DEST = ({ story, authorProfile, className = "" }) => { 
    const cardRef = useRef(null);

    const handleMouseMove = (e) => {
        if (!cardRef.current) return;
        const { clientX, clientY } = e;
        const { width, height, left, top } = cardRef.current.getBoundingClientRect();
        const x = clientX - left;
        const y = clientY - top;
        const rotateX = 10 * ((y - height / 2) / (height / 2)) * -1;
        const rotateY = 10 * ((x - width / 2) / (width / 2));
        cardRef.current.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale3d(1.16, 1.16, 1.16)`;
    };

    const handleMouseEnter = () => {
        if (!cardRef.current) return;
        cardRef.current.style.transition = 'transform 0.1s ease-out';
        cardRef.current.style.zIndex = '10';
    };

    const handleMouseLeave = () => {
        if (!cardRef.current) return;
        cardRef.current.style.transition = 'transform 0.6s cubic-bezier(0.23, 1, 0.32, 1)';
        cardRef.current.style.transform = 'perspective(1000px) rotateX(0deg) rotateY(0deg) scale3d(1, 1, 1)';
        cardRef.current.style.zIndex = '1';
    };

    // --- LÓGICA DE FALLBACK ---
    // Si 'authorProfile' (de Firestore) existe, úsalo.
    // Si no, usa el 'story' (de Typesense) que puede tener 'authorUsername'.
    const displayUsername = authorProfile?.username || story?.authorUsername;
    // --- FIN LÓGICA ---

    return (
        <Link
            key={story.id} 
            to={`/story/${story.id}`}
            ref={cardRef}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            onMouseEnter={handleMouseEnter}
            className={`STORY_CARD rounded-lg overflow-hidden shadow-lg transition-all duration-300 ease-out group ${className}`}
        >
            <div className="relative w-full h-full">
                <img
                    src={story.coverImageURL || 'https://placehold.co/600x800/1f2937/ffffff?text=No+Image'}
                    alt={`${story.title} cover`}
                    className="card-child card-image w-full h-64 object-cover"
                />

                <div className='relative ETIQUETA_DESTACADA flex items-center'>
                   <div className='icon_dest'>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M2 18H22" stroke="#000000ff" strokeWidth="2" strokeLinecap="round"/>
                            <path d="M12 2L15 10H19L22 18H2L5 18H9L12 2Z" fill="#000000ff" fillOpacity="0.2" stroke="#000000ff" strokeWidth="2" strokeLinejoin="round"/>
                        </svg>
                   </div>
                    <p className='winner'>Last month winner</p>
                </div>

                <div className="card-child card-content p-4">
                    <h3 className="text-lg font-bold text-white">{story.title}</h3>
                    <p className="text-sm text-gray-400 mt-1 h-[60px] overflow-hidden">
                        {story.description 
                            ? (story.description.length > 100 ? story.description.substring(0, 100) + '...' : story.description) 
                            : 'Esta historia aún no tiene una descripción.'
                        }
                    </p>
                    <div className="flex justify-between items-center mt-4 pt-2 border-t border-gray-700">
                        
                        {/* --- ¡AQUÍ SE USA EL FALLBACK! --- */}
                        {displayUsername ? (
                            <p className="text-xs text-gray-500">por @{displayUsername}</p>
                        ) : (
                            // Si no hay NINGÚN username, muestra el loader
                            <p className="text-xs text-gray-700 animate-pulse rounded">cargando autor...</p>
                        )}
                        {/* --- FIN DE LA CORRECCIÓN --- */}

                        <p className="text-xs text-gray-500 whitespace-nowrap">{formatTimestamp(story.lastUpdatedAt)}</p>
                    </div>
                </div>
            </div>
        </Link>
    );
};

const StoryCarousel = ({ children }) => {
    const scrollRef = useRef(null);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);
    const [scrollProgress, setScrollProgress] = useState(0);
    const [hasOverflow, setHasOverflow] = useState(false); // <--- NUEVO ESTADO
    
    // Estados para "Drag to Scroll"
    const [isDragging, setIsDragging] = useState(false);
    const [startX, setStartX] = useState(0);
    const [scrollLeft, setScrollLeft] = useState(0);

    const checkScroll = () => {
        if (scrollRef.current) {
            const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
            
            // 1. Detectar si hay desbordamiento real (contenido más grande que contenedor)
            const isScrollable = scrollWidth > clientWidth;
            setHasOverflow(isScrollable);

            // Solo calculamos el resto si es scrolleable
            if (isScrollable) {
                setCanScrollLeft(scrollLeft > 10);
                setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
                
                const maxScroll = scrollWidth - clientWidth;
                const progress = maxScroll > 0 ? (scrollLeft / maxScroll) * 100 : 0;
                setScrollProgress(progress);
            } else {
                // Si no es scrolleable, reseteamos
                setCanScrollLeft(false);
                setCanScrollRight(false);
                setScrollProgress(0);
            }
        }
    };

    useEffect(() => {
        checkScroll();
        window.addEventListener('resize', checkScroll);
        return () => window.removeEventListener('resize', checkScroll);
    }, [children]);

    const scroll = (direction) => {
        if (scrollRef.current) {
            const scrollAmount = direction === 'left' ? -304 : 304;
            scrollRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
            // No usamos setTimeout aquí para la barra, el evento onScroll lo manejará
        }
    };

    // --- MANEJADORES DE ARRASTRE (DRAG) ---
    const handleMouseDown = (e) => {
        if (!hasOverflow) return; // No permitir drag si no hay overflow
        setIsDragging(true);
        setStartX(e.pageX - scrollRef.current.offsetLeft);
        setScrollLeft(scrollRef.current.scrollLeft);
    };

    const handleMouseLeave = () => {
        setIsDragging(false);
    };

    const handleMouseUp = () => {
        setIsDragging(false);
    };

    const handleMouseMove = (e) => {
        if (!isDragging) return;
        e.preventDefault();
        const x = e.pageX - scrollRef.current.offsetLeft;
        const walk = (x - startX) * 2;
        scrollRef.current.scrollLeft = scrollLeft - walk;
    };

    return (
        <div className="relative group/carousel mb-8">
            
            {/* CONTENEDOR SCROLL (Las tarjetas) */}
            <div 
                ref={scrollRef}
                onScroll={checkScroll}
                onMouseDown={handleMouseDown}
                onMouseLeave={handleMouseLeave}
                onMouseUp={handleMouseUp}
                onMouseMove={handleMouseMove}
                // Si no hay overflow, centramos el contenido (justify-center opcional, depende de tu gusto)
                // Si hay overflow, usamos justify-start normal
                className={`flex overflow-x-auto space-x-6 py-12 custom-scrollbar scroll-smooth items-center snap-x snap-mandatory 
                    ${isDragging ? 'cursor-grabbing snap-none' : hasOverflow ? 'cursor-grab' : 'cursor-default'}`}
                style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }} 
            >
                <style>{`
                    .custom-scrollbar::-webkit-scrollbar {
                        display: none;
                    }
                `}</style>
                {children}
            </div>

            {/* CONTROLES INFERIORES (Solo se renderizan si hay overflow) */}
            {hasOverflow && (
                <div className="flex items-center justify-center gap-4 mt-[-20px] px-10 opacity-0 group-hover/carousel:opacity-100 transition-opacity duration-300">
                    
                    {/* Botón Izquierda */}
                    <button 
                        onClick={() => scroll('left')}
                        disabled={!canScrollLeft}
                        className={`p-2 rounded-full transition-colors ${canScrollLeft ? 'bg-gray-700 hover:bg-indigo-600 text-white' : 'bg-gray-800 text-gray-600 cursor-default'}`}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                    </button>

                    {/* Barra de Progreso Visual */}
                    <div className="flex-grow max-w-xs h-1.5 bg-gray-800 rounded-full overflow-hidden relative">
                        <div className="absolute inset-0 bg-gray-800"></div>
                        <div 
                            className="h-full bg-indigo-500 rounded-full transition-all duration-100 ease-out"
                            style={{ 
                                width: '30%', 
                                transform: `translateX(${(scrollProgress / 100) * 230}%)` 
                            }}
                        ></div>
                    </div>

                    {/* Botón Derecha */}
                    <button 
                        onClick={() => scroll('right')}
                        disabled={!canScrollRight}
                        className={`p-2 rounded-full transition-colors ${canScrollRight ? 'bg-gray-700 hover:bg-indigo-600 text-white' : 'bg-gray-800 text-gray-600 cursor-default'}`}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                    </button>

                </div>
            )}
        </div>
    );
};

const RecommendationRow = ({ tag, title, excludedStoryIds = [], authorProfilesMap: initialAuthorsMap }) => {
    const [stories, setStories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [localAuthors, setLocalAuthors] = useState({});

    useEffect(() => {
        const fetchRecommendedStories = async () => {
            if (!tag) return;
            
            // 1. Para VISUALIZAR (Título bonito): "Arcane"
            // (Esto no lo usamos en la query, solo es estético si quisieras usarlo en logs)
            // const displayTag = tag.charAt(0).toUpperCase() + tag.slice(1);

            // 2. Para BUSCAR en DB (Estrictamente minúsculas): "arcane"
            // Esto coincide con tu CreateStoryModal que hace .toLowerCase()
            const searchTag = tag.toLowerCase(); 

            try {
                const q = query(
                    collection(db, 'stories'),
                    // CORRECCIÓN: Buscamos 'searchTag' que ahora es minúscula pura
                    where('tags', 'array-contains', searchTag),
                    orderBy('lastUpdatedAt', 'desc'), 
                    limit(15)
                );

                const snapshot = await getDocs(q);
                
                // Mapeamos los datos
                const filteredStories = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                
                // (Opcional: filtrar historias actuales si quieres reactivarlo después)
                // const finalStories = filteredStories.filter(story => !excludedStoryIds.includes(story.id));

                setStories(filteredStories);

                // --- CARGAR AUTORES (Lógica que ya teníamos bien) ---
                if (filteredStories.length > 0) {
                    const authorIds = [...new Set(filteredStories.map(s => s.authorId))];
                    const idsToFetch = authorIds.filter(id => !initialAuthorsMap?.[id]);

                    if (idsToFetch.length > 0) {
                        const chunks = [];
                        for (let i = 0; i < idsToFetch.length; i += 10) chunks.push(idsToFetch.slice(i, i + 10));
                        const newProfiles = {};
                        for (const chunk of chunks) {
                            const qUsers = query(collection(db, 'users'), where(documentId(), 'in', chunk));
                            const snaps = await getDocs(qUsers);
                            snaps.forEach(d => newProfiles[d.id] = d.data());
                        }
                        setLocalAuthors(newProfiles);
                    }
                }
                // ------------------------------------------------

            } catch (error) {
                console.error(`Error fetching recommendations for tag "${searchTag}":`, error);
            } finally {
                setLoading(false);
            }
        };

        fetchRecommendedStories();
    }, [tag, excludedStoryIds, initialAuthorsMap]);

    if (!loading && stories.length === 0) return null;

    // Aseguramos que el título del carril tenga mayúscula inicial para estética
    const displayTitle = title.charAt(0).toUpperCase() + title.slice(1);

    return (
        <div className="mb-4">
            <h3 className="text-xl font-bold text-white px-2 border-l-4 border-indigo-500 capitalize">
                {displayTitle}
            </h3>

            <StoryCarousel>
                {loading ? (
                    [...Array(4)].map((_, i) => (
                        <div key={i} className="min-w-[220px] sm:min-w-[260px] h-[420px] bg-gray-800 rounded-[34px] animate-pulse flex-shrink-0 snap-start"></div>
                    ))
                ) : (
                    stories.map(story => {
                        const profile = localAuthors[story.authorId] || (initialAuthorsMap ? initialAuthorsMap[story.authorId] : null);
                        return (
                            <StoryCard 
                                key={story.id}
                                story={story} 
                                authorProfile={profile}
                                className="snap-start min-w-[220px] sm:min-w-[260px] flex-shrink-0" 
                            />
                        );
                    })
                )}
            </StoryCarousel>
        </div>
    );
};

const PopularStoriesRow = ({ authorProfilesMap: initialAuthorsMap }) => {
    const [stories, setStories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [localAuthors, setLocalAuthors] = useState({});

    useEffect(() => {
        const fetchPopular = async () => {
            try {
                const q = query(collection(db, 'stories'), orderBy('heartCount', 'desc'), limit(10));
                const snapshot = await getDocs(q);
                const fetchedStories = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                setStories(fetchedStories);

                if (fetchedStories.length > 0) {
                    const authorIds = [...new Set(fetchedStories.map(s => s.authorId))];
                    const idsToFetch = authorIds.filter(id => !initialAuthorsMap?.[id]);

                    if (idsToFetch.length > 0) {
                        const chunks = [];
                        for (let i = 0; i < idsToFetch.length; i += 10) chunks.push(idsToFetch.slice(i, i + 10));
                        const newProfiles = {};
                        for (const chunk of chunks) {
                            const qUsers = query(collection(db, 'users'), where(documentId(), 'in', chunk));
                            const snaps = await getDocs(qUsers);
                            snaps.forEach(d => newProfiles[d.id] = d.data());
                        }
                        setLocalAuthors(newProfiles);
                    }
                }
            } catch (error) {
                console.error("Error fetching popular stories:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchPopular();
    }, [initialAuthorsMap]);

    if (!loading && stories.length === 0) return null;

    return (
        <div className="mb-8">
            <h3 className="text-2xl font-bold text-white px-2 border-l-4 border-yellow-500 ml-2">
                🔥 Historias más Populares
            </h3>
            
            <StoryCarousel>
                {loading ? (
                    [...Array(4)].map((_, i) => (
                         <div key={i} className="min-w-[220px] sm:min-w-[260px] h-[420px] bg-gray-800 rounded-[34px] animate-pulse flex-shrink-0 snap-start"></div>
                    ))
                ) : (
                    stories.map(story => {
                        const profile = localAuthors[story.authorId] || (initialAuthorsMap ? initialAuthorsMap[story.authorId] : null);
                        return (
                            <StoryCard 
                                key={story.id}
                                story={story} 
                                authorProfile={profile}
                                className="snap-start min-w-[220px] sm:min-w-[260px] flex-shrink-0"
                            />
                        );
                    })
                )}
            </StoryCarousel>
        </div>
    );
};

const ProfileSearchCard = ({ profile }) => {
    const cardRef = useRef(null);

  const handleMouseMove = (e) => {
    if (!cardRef.current) return;

    const { clientX, clientY } = e;
    const { width, height, left, top } = cardRef.current.getBoundingClientRect();
    
    const x = clientX - left;
    const y = clientY - top;
    const rotateX = 10 * ((y - height / 2) / (height / 2)) * -1;
    const rotateY = 10 * ((x - width / 2) / (width / 2));

    cardRef.current.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale3d(1.16, 1.16, 1.16)`;
  };

    const handleMouseEnter = () => {
    if (!cardRef.current) return;
    cardRef.current.style.transition = 'transform 0.1s ease-out';
    cardRef.current.style.zIndex = '10';
    };

  const handleMouseLeave = () => {
    if (!cardRef.current) return;
    cardRef.current.style.transition = 'transform 0.6s cubic-bezier(0.23, 1, 0.32, 1)';
    cardRef.current.style.transform = 'perspective(1000px) rotateX(0deg) rotateY(0deg) scale3d(1, 1, 1)';
    cardRef.current.style.zIndex = '1';
  };
  
    return (
        <Link 
            to={`/profile/${profile.username}`}
            ref={cardRef}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            onMouseEnter={handleMouseEnter}
            className="profSearch_CARD rounded-lg shadow-lg p-4 flex flex-col items-center text-left transition-all duration-300 ease-out group"
            
        >
            <img
                src={profile.avatarURL || `https://placehold.co/150x150/374151/E5E7EB?text=${profile.displayName.charAt(0)}`}
                alt={`${profile.displayName}'s avatar`}
                className="w-24 h-24 rounded-full border-4 border-gray-700 object-cover mb-4"
            />
            <h3 className="text-lg font-bold truncate text-white">{profile.displayName}</h3>
            <p className="text-sm text-indigo-400">@{profile.username}</p>
        </Link>
    );
};

const ChapterSearchCard = ({ chapter, authorProfile, queryTerm }) => { // <-- 1. ACEPTA `authorProfile`
    const cardRef = useRef(null);

  const handleMouseMove = (e) => {
    if (!cardRef.current) return;

    const { clientX, clientY } = e;
    const { width, height, left, top } = cardRef.current.getBoundingClientRect();
    
    const x = clientX - left;
    const y = clientY - top;
    const rotateX = 10 * ((y - height / 2) / (height / 2)) * -1;
    const rotateY = 10 * ((x - width / 2) / (width / 2));

    cardRef.current.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale3d(1.16, 1.16, 1.16)`;
  };

    const handleMouseEnter = () => {
    if (!cardRef.current) return;
    cardRef.current.style.transition = 'transform 0.1s ease-out';
    cardRef.current.style.zIndex = '10';
    };

  const handleMouseLeave = () => {
    if (!cardRef.current) return;
    cardRef.current.style.transition = 'transform 0.6s cubic-bezier(0.23, 1, 0.32, 1)';
    cardRef.current.style.transform = 'perspective(1000px) rotateX(0deg) rotateY(0deg) scale3d(1, 1, 1)';
    cardRef.current.style.zIndex = '1';
  };



    const highlightedSnippet = useMemo(() => {
        const fullContent = chapter.content;
        const snippetLength = 600;

        // Si no hay término de búsqueda, simplemente mostramos el inicio del capítulo.
        if (!queryTerm || queryTerm.trim() === '') {
            return fullContent.length > snippetLength 
                ? fullContent.substring(0, snippetLength) + '...' 
                : fullContent;
        }

        // 1. Obtenemos las palabras clave de la búsqueda (como antes).
        const searchWords = queryTerm.split(/\s+/)
            .filter(word => word.length > 2 && !spanishStopWords.includes(word.toLowerCase()));

        if (searchWords.length === 0) {
            return fullContent.substring(0, snippetLength) + '...';
        }

        // 2. Buscamos la POSICIÓN de la primera coincidencia en el texto.
        // Para hacerlo de forma fiable, buscamos en una versión de texto plano (sin HTML).
        const plainTextContent = fullContent.replace(/<[^>]+>/g, ' ');
        const firstMatchIndex = plainTextContent.toLowerCase().indexOf(searchWords[0].toLowerCase());

        let snippet;

        if (firstMatchIndex === -1) {
            // Fallback: si por alguna razón no se encuentra la palabra, mostramos el inicio.
            snippet = fullContent.substring(0, snippetLength);
        } else {
            // 3. Calculamos el inicio del snippet para centrar la palabra encontrada.
            // Restamos la mitad del tamaño del snippet para centrarlo, asegurándonos de no empezar en un número negativo.
            const start = Math.max(0, firstMatchIndex - (snippetLength / 2));
            snippet = fullContent.substring(start, start + snippetLength);
        }

        // 4. Añadimos puntos suspensivos para indicar que no es el inicio/fin del texto.
        if (snippet.length > 0 && snippet.length < fullContent.length) {
            if (snippet.startsWith('... ') === false) {
                 snippet = '... ' + snippet;
            }
           if (snippet.endsWith(' ...') === false) {
                 snippet = snippet + ' ...';
            }
        }
        
        // 5. Aplicamos el resaltado al snippet que ya está centrado.
        const regex = new RegExp(`(${searchWords.join('|')})`, 'gi');
        return snippet.replace(regex, '<mark class="bg-yellow-400 text-black rounded-sm px-1">$1</mark>');

    }, [chapter.content, queryTerm]);

    return (
        <Link 
            to={`/story/${chapter.storyId}/chapter/${chapter.id}`}
            ref={cardRef}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            onMouseEnter={handleMouseEnter}
            className="STORY_CARD bg-gray-800 rounded-lg overflow-hidden shadow-lg p-4 flex flex-col text-left transition-all duration-300 ease-out group h-full"
        >
            <h3 className="text-md font-bold text-indigo-400">"{chapter.chapterTitle}"</h3>
            <p className="text-xs text-gray-400 mb-2">de la historia: {chapter.storyTitle}</p>
            
            <div 
                className="text-sm text-gray-300 italic" 
                dangerouslySetInnerHTML={{ __html: highlightedSnippet }} 
            />
            
            {/* ¡ESTA ES LA LÓGICA CORREGIDA QUE YA TENÍAS! ESTÁ BIEN. */}
            {authorProfile ? (
                <p className="text-xs text-gray-500 mt-auto pt-2">por @{authorProfile.username}</p>
            ) : (
                <p className="text-xs text-gray-700 mt-auto pt-2 animate-pulse rounded">cargando autor...</p>
            )}
        </Link>
    );
};

const ProfilePage = ({ loggedInUserProfile }) => {
    const { username } = useParams();
    
    // Datos del Perfil
    const [profileData, setProfileData] = useState(null);
    const [isOwner, setIsOwner] = useState(false);
    
    // Datos de Contenido Separados
    const [stories, setStories] = useState([]);
    const [manualCollections, setManualCollections] = useState([]); // Solo las creadas por usuario
    const [likedCollection, setLikedCollection] = useState(null);   // La colección automática única
    
    // UI States
    const [activeTab, setActiveTab] = useState('stories'); // 'stories' | 'collections' | 'liked'
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    
    // Modales y Follows
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isFollowing, setIsFollowing] = useState(false);
    const [isFollowLoading, setIsFollowLoading] = useState(true);
    const [showFollowModal, setShowFollowModal] = useState(false);
    const [followListType, setFollowListType] = useState('followers');

    useEffect(() => {
        let unsubscribeProfile;
        let unsubscribeFollow;

        const fetchProfileAndContent = async () => {
            setLoading(true);
            setError(null);
            setIsFollowLoading(true);

            try {
                // 1. Obtener UID
                const usernameRef = doc(db, 'usernames', username.toLowerCase());
                const usernameSnap = await getDoc(usernameRef);
                
                if (!usernameSnap.exists()) throw new Error("Perfil no encontrado.");
                
                const { userId } = usernameSnap.data();
                const currentUserIsOwner = loggedInUserProfile && loggedInUserProfile.uid === userId;
                setIsOwner(currentUserIsOwner);

                // 2. Obtener datos del usuario
                const userRef = doc(db, 'users', userId);
                unsubscribeProfile = onSnapshot(userRef, (userSnap) => {
                    if (!userSnap.exists()) throw new Error("Error cargando datos.");
                    setProfileData({ uid: userSnap.id, ...userSnap.data() });
                });

                // 3. Obtener Historias
                const storiesQuery = query(collection(db, 'stories'), where("authorId", "==", userId), orderBy("createdAt", "desc"));
                const storiesSnapshot = await getDocs(storiesQuery);
                setStories(storiesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));

                // 4. Obtener TODAS las Colecciones visibles
                let collectionsQuery;
                if (currentUserIsOwner) {
                    // Dueño: Ve todo
                    collectionsQuery = query(collection(db, 'collections'), where("ownerId", "==", userId), orderBy("lastUpdatedAt", "desc"));
                } else {
                    // Visitante: Solo ve públicas
                    collectionsQuery = query(collection(db, 'collections'), where("ownerId", "==", userId), where("visibility", "==", "public"), orderBy("lastUpdatedAt", "desc"));
                }
                
                const collectionsSnapshot = await getDocs(collectionsQuery);
                const allColls = collectionsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

                // --- SEPARACIÓN DE COLECCIONES ---
                const manual = [];
                let liked = null;

                allColls.forEach(coll => {
                    if (coll.isDefaultLiked) {
                        liked = coll;
                    } else {
                        manual.push(coll);
                    }
                });

                setManualCollections(manual);
                setLikedCollection(liked); // Será null si soy visitante y la colección es privada

                // 5. Estado de Follow
                if (loggedInUserProfile && !currentUserIsOwner) {
                    const followRef = doc(db, `users/${loggedInUserProfile.uid}/following/${userId}`);
                    unsubscribeFollow = onSnapshot(followRef, (doc) => setIsFollowing(doc.exists()));
                } else {
                    setIsFollowLoading(false);
                }

            } catch (err) {
                console.error(err);
                setError(err.message);
            } finally {
                setLoading(false);
                setIsFollowLoading(false);
            }
        };

        if (username) fetchProfileAndContent();

        return () => {
            if (unsubscribeProfile) unsubscribeProfile();
            if (unsubscribeFollow) unsubscribeFollow();
        };
    }, [username, loggedInUserProfile]);

    const handleProfileUpdate = (updatedData) => setProfileData(prev => ({ ...prev, ...updatedData }));
    const openFollowers = () => { setFollowListType('followers'); setShowFollowModal(true); };
    const openFollowing = () => { setFollowListType('following'); setShowFollowModal(true); };
    
    const handleFollowToggle = async () => {
        // ... (Tu lógica de follow se mantiene idéntica)
        setIsFollowLoading(true);
        const batch = writeBatch(db);
        const currentUserRef = doc(db, `users/${loggedInUserProfile.uid}`);
        const profileUserRef = doc(db, `users/${profileData.uid}`);
        const followingRef = doc(db, `users/${loggedInUserProfile.uid}/following/${profileData.uid}`);
        const followerRef = doc(db, `users/${profileData.uid}/followers/${loggedInUserProfile.uid}`);

        if (isFollowing) {
            batch.delete(followingRef);
            batch.delete(followerRef);
            batch.update(currentUserRef, { followingCount: increment(-1) });
            batch.update(profileUserRef, { followerCount: increment(-1) });
        } else {
            batch.set(followingRef, { followedAt: serverTimestamp() });
            batch.set(followerRef, { followedBy: serverTimestamp() });
            batch.update(currentUserRef, { followingCount: increment(1) });
            batch.update(profileUserRef, { followerCount: increment(1) });
        }
        try { await batch.commit(); } catch (err) { console.error(err); } finally { setIsFollowLoading(false); }
    };

    if (loading) return <div className="flex items-center justify-center min-h-screen bg-gray-900 text-white">Cargando Perfil...</div>;
    if (error) return <div className="flex items-center justify-center min-h-screen bg-gray-900 text-red-500">{error}</div>;
    if (!profileData) return null;

    return (
        <div className="max-w-6xl mx-auto p-4 sm:p-8 PROFILE_DIV">
            {/* HEADER (Sin cambios) */}
            <header className="flex flex-col sm:flex-row items-center sm:items-start text-center sm:text-left p-8 bg-gray-800 rounded-lg shadow-lg relative PROFILE_BANNER_DIV">
                <img src={profileData.avatarURL || `https://placehold.co/150x150/374151/E5E7EB?text=${profileData.displayName.charAt(0)}`} alt="Avatar" className="w-36 h-36 rounded-full border-4 border-indigo-500 object-cover mb-4 sm:mb-0 sm:mr-8"/>
                <div className="flex-1">
                    <div className="flex flex-col sm:flex-row items-center justify-between">
                        <div>
                            <h1 className="text-4xl font-bold">{profileData.displayName}</h1>
                            <p className="text-lg text-indigo-400">@{profileData.username}</p>
                        </div>
                        {!isOwner && loggedInUserProfile && (
                            <button onClick={handleFollowToggle} disabled={isFollowLoading} className={`mt-4 sm:mt-0 px-6 py-2 font-bold rounded-md transition-colors disabled:opacity-50 ${isFollowing ? 'bg-gray-700 text-white hover:bg-gray-600' : 'bg-indigo-600 text-white hover:bg-gray-700'}`}>
                                {isFollowLoading ? '...' : (isFollowing ? 'Siguiendo' : 'Seguir')}
                            </button>
                        )}
                    </div>
                    <p className="text-gray-300 mt-2 max-w-xl">{profileData.description}</p>
                    <div className="flex justify-center sm:justify-start space-x-6 mt-4">
                        <button onClick={openFollowers} className="text-left hover:text-indigo-400">
                            <span className="font-bold text-white">{profileData.followerCount}</span> <span className="text-gray-400">Seguidores</span>
                        </button>
                        <button onClick={openFollowing} className="text-left hover:text-indigo-400">
                            <span className="font-bold text-white">{profileData.followingCount}</span> <span className="text-gray-400">Siguiendo</span>
                        </button>
                    </div>
                </div>
                {isOwner && (<button onClick={() => setIsEditModalOpen(true)} className="absolute top-4 right-4 px-4 py-2 text-sm font-bold text-white bg-indigo-600 rounded-md hover:bg-indigo-700">Editar Perfil</button>)}
            </header>

            {/* PESTAÑAS DE NAVEGACIÓN (Actualizadas) */}
            <div className="mt-8 border-b border-gray-700 flex space-x-8 overflow-x-auto">
                <button 
                    onClick={() => setActiveTab('stories')}
                    className={`pb-4 text-lg font-medium transition-colors relative whitespace-nowrap ${activeTab === 'stories' ? 'text-indigo-400' : 'text-gray-400 hover:text-white'}`}
                >
                    Historias <span className="ml-1 text-xs bg-gray-700 text-white px-2 py-0.5 rounded-full">{stories.length}</span>
                    {activeTab === 'stories' && <div className="absolute bottom-0 left-0 w-full h-1 bg-indigo-500 rounded-t-md"></div>}
                </button>
                
                <button 
                    onClick={() => setActiveTab('collections')}
                    className={`pb-4 text-lg font-medium transition-colors relative whitespace-nowrap ${activeTab === 'collections' ? 'text-indigo-400' : 'text-gray-400 hover:text-white'}`}
                >
                    Colecciones <span className="ml-1 text-xs bg-gray-700 text-white px-2 py-0.5 rounded-full">{manualCollections.length}</span>
                    {activeTab === 'collections' && <div className="absolute bottom-0 left-0 w-full h-1 bg-indigo-500 rounded-t-md"></div>}
                </button>

                <button 
                    onClick={() => setActiveTab('liked')}
                    className={`pb-4 text-lg font-medium transition-colors relative whitespace-nowrap ${activeTab === 'liked' ? 'text-indigo-400' : 'text-gray-400 hover:text-white'}`}
                >
                    Me Gusta
                    {/* Si hay colección pública, mostramos contador. Si no, candado o nada */}
                    {likedCollection ? (
                        <span className="ml-2 text-xs bg-pink-900 text-pink-200 px-2 py-0.5 rounded-full">{likedCollection.storyCount || 0}</span>
                    ) : (
                        !isOwner && <span className="ml-2 text-xs">🔒</span>
                    )}
                    {activeTab === 'liked' && <div className="absolute bottom-0 left-0 w-full h-1 bg-indigo-500 rounded-t-md"></div>}
                </button>
            </div>

            {/* CONTENIDO PRINCIPAL */}
            <main className="mt-8 min-h-[300px]">
                
                {/* 1. TAB HISTORIAS */}
                {activeTab === 'stories' && (
                    stories.length > 0 ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6" style={{ perspective: '2000px' }}>
                            {stories.map(story => (
                                <StoryCard key={story.id} story={story} authorProfile={profileData} />
                            ))}
                        </div>
                    ) : (
                        <div className="text-center py-12 text-gray-500 bg-gray-800/30 rounded-lg border border-gray-700 border-dashed">
                            <p>Aún no hay historias publicadas.</p>
                        </div>
                    )
                )}

                {/* 2. TAB COLECCIONES (Solo Manuales) */}
                {activeTab === 'collections' && (
                    manualCollections.length > 0 ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-8">
                            {manualCollections.map(coll => (
                                <CollectionCard key={coll.id} collection={coll} authorProfile={profileData} />
                            ))}
                        </div>
                    ) : (
                        <div className="text-center py-12 text-gray-500 bg-gray-800/30 rounded-lg border border-gray-700 border-dashed">
                            <p>{isOwner ? 'Crea tu primera colección guardando historias.' : 'Este usuario no tiene colecciones públicas.'}</p>
                        </div>
                    )
                )}

                {/* 3. TAB ME GUSTA (Lógica del Candado) */}
                {activeTab === 'liked' && (
                    // Caso A: Tenemos la colección (Soy dueño O es pública)
                    likedCollection ? (
                        // CAMBIO AQUÍ: Usamos Flexbox en lugar de Grid para evitar solapamientos
                        <div className="flex flex-col md:flex-row gap-8 items-start md:items-center p-4 bg-gray-800/30 rounded-xl border border-gray-700/50">
                            
                            {/* Contenedor de la Tarjeta (Ancho fijo en desktop para que no se estire) */}
                            <div className="w-full md:w-72 flex-shrink-0">
                                <CollectionCard collection={likedCollection} authorProfile={profileData} />
                            </div>

                            {/* Contenedor del Texto */}
                            <div className="flex flex-col justify-center text-gray-400">
                                <h3 className="text-2xl font-bold text-white mb-3">
                                    Historias que le gustan a {profileData.displayName}
                                </h3>
                                <p className="text-lg text-gray-300 mb-4">
                                    Esta es la colección personal de favoritos. Aquí se guardan automáticamente las historias a las que das corazón.
                                </p>
                                
                                {/* Badge de visibilidad */}
                                {isOwner && (
                                    <div className="inline-flex items-center gap-2 bg-black/40 px-3 py-2 rounded-lg border border-gray-600 self-start">
                                        <span className="text-sm text-gray-400">Actualmente visible:</span>
                                        <span className={`text-sm font-bold ${likedCollection.visibility === 'public' ? 'text-green-400' : 'text-yellow-400'}`}>
                                            {likedCollection.visibility === 'public' ? 'Pública 🌎' : 'Privada 🔒'}
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>
                    ) : (
                        // Caso B: No tenemos la colección (Soy visitante Y es privada)
                        <div className="flex flex-col items-center justify-center py-20 bg-gray-800/30 rounded-lg border border-gray-700 border-dashed group relative cursor-help">
                            <div className="bg-gray-700 p-4 rounded-full mb-4">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                </svg>
                            </div>
                            <h3 className="text-xl font-bold text-gray-300">Me Gusta Privados</h3>
                            
                            {/* Tooltip al pasar el mouse */}
                            <div className="mt-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-black text-white text-sm py-2 px-4 rounded shadow-lg">
                                @{profileData.username} mantiene sus "Me gusta" en privado.
                            </div>
                        </div>
                    )
                )}
            </main>

            <ProfileComments profileOwnerId={profileData.uid} loggedInUserProfile={loggedInUserProfile} />
            
            {isEditModalOpen && <EditProfileModal currentUserProfile={profileData} onClose={() => setIsEditModalOpen(false)} onProfileUpdate={handleProfileUpdate} />}
            {showFollowModal && <FollowListModal userId={profileData.uid} listType={followListType} onClose={() => setShowFollowModal(false)} />}
        </div>
    );
};

// Sub-componente para capítulos en Pre-Estreno (Hype)
const LockedChapterCard = ({ chapter, index }) => {
    const [timeLeft, setTimeLeft] = useState('');

    useEffect(() => {
        const calculateTimeLeft = () => {
            if (!chapter.scheduledAt) return;
            // Convertir Timestamp de Firestore a Date de JS
            const releaseDate = chapter.scheduledAt.toDate(); 
            const now = new Date();
            const difference = releaseDate - now;

            if (difference > 0) {
                const days = Math.floor(difference / (1000 * 60 * 60 * 24));
                const hours = Math.floor((difference / (1000 * 60 * 60)) % 24);
                const minutes = Math.floor((difference / 1000 / 60) % 60);
                setTimeLeft(`${days}d ${hours}h ${minutes}m`);
            } else {
                setTimeLeft('Coming soon...');
            }
        };

        calculateTimeLeft();
        const timer = setInterval(calculateTimeLeft, 60000); // Actualizar cada minuto
        return () => clearInterval(timer);
    }, [chapter.scheduledAt]);

    return (
        <div className="flex items-center justify-between p-4 border border-gray-700/50 bg-gray-800/40 rounded-md text-gray-400 select-none relative overflow-hidden">
            {/* Fondo rayado sutil para indicar "bloqueado" */}
            <div className="absolute inset-0 opacity-5 pointer-events-none" style={{backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 10px, #000 10px, #000 20px)'}}></div>

            <div className="flex items-center gap-4 relative z-10">
                <span className="text-gray-600 font-mono text-sm p-4 text-right">{index + 1}</span>
                <div>
                    <p className="font-bold text-lg text-gray-500 flex items-center gap-2">
                        {chapter.chapterTitle}
                        <span className="text-xs bg-pink-900/30 text-pink-400 px-2 py-0.5 rounded border border-pink-800/50 uppercase tracking-widest">
                            Próximamente
                        </span>
                    </p>
                    <div className="flex items-center gap-2 text-xs text-indigo-400 mt-1 font-mono">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                        Disponible en: {timeLeft}
                    </div>
                </div>
            </div>
            
            <div className="relative z-10 pr-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
            </div>
        </div>
    );
};

const StoryPage = ({ loggedInUserProfile }) => {
    const { storyId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();

    const [story, setStory] = useState(null);
    const [chapters, setChapters] = useState([]);
    const [characterDetails, setCharacterDetails] = useState({});

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const authUser = auth.currentUser;
    const [authorProfile, setAuthorProfile] = useState(null);

    // Modales y estados
    const [isCollectionModalOpen, setIsCollectionModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isEditChaptersMode, setIsEditChaptersMode] = useState(false);
    const [chapterToDelete, setChapterToDelete] = useState(null);
    const [isDeleteStoryModalOpen, setIsDeleteStoryModalOpen] = useState(false);

    // Estados contadores
    const [totalViews, setTotalViews] = useState(0);
    const [storyHearts, setStoryHearts] = useState({ count: 0, hasLiked: false, loading: true });
    const [showShareTooltip, setShowShareTooltip] = useState(false);

    const [notification, setNotification] = useState(null);

    useEffect(() => {
        if (location.state?.notification) {
            setNotification(location.state.notification);
            window.history.replaceState({}, document.title);
            const timer = setTimeout(() => setNotification(null), 4000);
            return () => clearTimeout(timer);
        }
    }, [location]);
    
    // Simplemente pegaré los hooks esenciales para que funcione el renderizado:
    useEffect(() => {
        const fetchStoryData = async () => {
            setLoading(true); setAuthorProfile(null); setError(null);
            try {
                const storyRef = doc(db, "stories", storyId);
                const storySnap = await getDoc(storyRef);
                if (!storySnap.exists()) { throw new Error("Esta historia no existe."); }
                const storyData = { id: storySnap.id, ...storySnap.data() };
                setStory(storyData);

                if (storyData.authorId) {
                    const authorRef = doc(db, 'users', storyData.authorId);
                    const authorSnap = await getDoc(authorRef);
                    if (authorSnap.exists()) setAuthorProfile(authorSnap.data());
                }

                const chaptersQuery = query(collection(db, "chapters"), where("storyId", "==", storyId), where("status", "==", "published"), orderBy("createdAt", "asc"));
                const chaptersSnapshot = await getDocs(chaptersQuery);
                let fetchedChapters = chaptersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                
                const total = fetchedChapters.reduce((sum, chapter) => sum + (chapter.viewCount || 0), 0);
                setTotalViews(total);

                const chaptersWithoutOrder = fetchedChapters.filter(c => c.order === undefined);
                if (chaptersWithoutOrder.length > 0) {
                    const batch = writeBatch(db);
                    fetchedChapters.forEach((chapter, index) => {
                        if (chapter.order === undefined) {
                            const chapterRef = doc(db, "chapters", chapter.id);
                            batch.update(chapterRef, { order: index + 1 });
                            chapter.order = index + 1;
                        }
                    });
                    await batch.commit();
                }
                fetchedChapters.sort((a, b) => a.order - b.order);
                setChapters(fetchedChapters);
            } catch (err) { setError(err.message); }
            setLoading(false);
        };
        fetchStoryData();
    }, [storyId]);

    // --- NUEVO: Cargar detalles de personajes (Fotos) ---
    useEffect(() => {
        const fetchCharacters = async () => {
            if (!story || !story.mainCharacters || story.mainCharacters.length === 0) return;

            // 1. Generar los IDs basados en los nombres (Misma lógica que Cloud Function)
            // Ej: "Jax (TADC)" -> "jax_(tadc)"
            const charIds = story.mainCharacters.map(name => 
                name.trim().toLowerCase().replace(/\s+/g, "_")
            );

            // 2. Buscar en Firestore (en lotes de 10)
            const chunks = [];
            for (let i = 0; i < charIds.length; i += 10) {
                chunks.push(charIds.slice(i, i + 10));
            }

            try {
                const promises = chunks.map(chunk => 
                    getDocs(query(collection(db, 'characters'), where(documentId(), 'in', chunk)))
                );
                
                const snapshots = await Promise.all(promises);
                const details = {};

                snapshots.forEach(snap => {
                    snap.forEach(doc => {
                        details[doc.data().name] = doc.data(); // Guardamos usando el nombre real como clave
                    });
                });

                setCharacterDetails(details);
            } catch (e) {
                console.error("Error cargando personajes:", e);
            }
        };

        fetchCharacters();
    }, [story]);

    // --- LOGICA DE HEARTS (Sin cambios) ---
    useEffect(() => {
        if (!storyId) return;
        const storyRef = doc(db, "stories", storyId);
        const unsubscribeStory = onSnapshot(storyRef, (doc) => {
            if (doc.exists()) {
                setStoryHearts(prev => ({ ...prev, count: doc.data().heartCount || 0 }));
            }
        });
        return () => unsubscribeStory();
    }, [storyId]);

    useEffect(() => {
        if (!loggedInUserProfile?.uid) {
            setStoryHearts(prev => ({ ...prev, hasLiked: false, loading: false }));
            return;
        }
        const heartRef = doc(db, `stories/${storyId}/heartedBy`, loggedInUserProfile.uid);
        const unsubscribeHeart = onSnapshot(heartRef, (doc) => {
            setStoryHearts(prev => ({ ...prev, hasLiked: doc.exists(), loading: false }));
        });
        return () => unsubscribeHeart();
    }, [storyId, loggedInUserProfile?.uid]);

    const toggleStoryHeart = async () => {
        // 1. Bloqueo de seguridad si está cargando o no hay usuario
        if (storyHearts.loading || !loggedInUserProfile?.uid) return;
        
        // Indicamos carga en la UI local
        setStoryHearts(prev => ({ ...prev, loading: true }));

        const batch = writeBatch(db);
        
        // Referencias existentes
        const storyRef = doc(db, "stories", storyId);
        const heartRef = doc(db, `stories/${storyId}/heartedBy`, loggedInUserProfile.uid);
        const userRef = doc(db, "users", loggedInUserProfile.uid);

        const newHasLiked = !storyHearts.hasLiked;
        const incrementValue = newHasLiked ? 1 : -1;

        // --- LOGICA EXISTENTE (Contadores y Subcolección) ---
        batch.update(storyRef, { heartCount: increment(incrementValue) });

        if (newHasLiked) {
            batch.set(heartRef, { heartedAt: serverTimestamp() });
        } else {
            batch.delete(heartRef);
        }

        // --- LOGICA EXISTENTE (Intereses del usuario) ---
        if (story.tags && story.tags.length > 0) {
            const interestUpdate = {};
            story.tags.forEach(tag => {
                const sanitizedTag = tag.toLowerCase().replace(/[^a-z0-9_]/g, '_');
                interestUpdate[`tagInterests.${sanitizedTag}`] = increment(incrementValue);
            });
            batch.update(userRef, interestUpdate);
        }

        // ==================================================================
        // 🔥 NUEVA LÓGICA: SISTEMA DE COLECCIONES (FASE 1)
        // ==================================================================
        try {
            // 1. Buscamos si el usuario YA tiene una colección de "Likes"
            const collectionsRef = collection(db, 'collections');
            // Query: Dame la colección que sea del usuario actual Y que sea la marcada como "default liked"
            const q = query(
                collectionsRef, 
                where('ownerId', '==', loggedInUserProfile.uid), 
                where('isDefaultLiked', '==', true), 
                limit(1)
            );
            const snapshot = await getDocs(q);

            let likedCollectionRef;

            if (snapshot.empty) {
                // CASO A: NO EXISTE LA COLECCIÓN (Es su primer like)
                
                if (newHasLiked) {
                    // Solo creamos la colección si está dando like (no si está quitando)
                    likedCollectionRef = doc(collection(db, 'collections')); // Generamos un ID nuevo
                    
                    batch.set(likedCollectionRef, {
                        ownerId: loggedInUserProfile.uid,
                        title: "Historias que me gustan", // Título por defecto
                        description: "Colección automática de mis 'Me gusta'.",
                        coverImageURL: story.coverImageURL || null, // Usamos la portada de la 1ra historia como portada inicial
                        visibility: "private", // Privada por defecto
                        isDefaultLiked: true, // ¡IMPORTANTE! Esta bandera la identifica
                        storyIds: [storyId], // Creamos el array con la primera historia
                        createdAt: serverTimestamp(),
                        lastUpdatedAt: serverTimestamp(),
                        storyCount: 1
                    });
                }
                // Si snapshot está vacío y newHasLiked es false (quitando like), no hacemos nada porque la colección no existe.
            } else {
                // CASO B: YA EXISTE LA COLECCIÓN
                
                // Obtenemos la referencia del documento existente
                likedCollectionRef = doc(db, 'collections', snapshot.docs[0].id);

                if (newHasLiked) {
                    // AGREGAR: Usamos arrayUnion para evitar duplicados
                    batch.update(likedCollectionRef, {
                        storyIds: arrayUnion(storyId),
                        lastUpdatedAt: serverTimestamp(),
                        storyCount: increment(1)
                        // Opcional: Podríamos actualizar coverImageURL aquí si quisiéramos que cambie con el último like
                    });
                } else {
                    // QUITAR: Usamos arrayRemove
                    batch.update(likedCollectionRef, {
                        storyIds: arrayRemove(storyId),
                        lastUpdatedAt: serverTimestamp(),
                        storyCount: increment(-1)
                    });
                }
            }

            // ==================================================================
            // FIN NUEVA LÓGICA
            // ==================================================================

            await batch.commit();
            
        } catch (error) {
            console.error("Error toggling story heart & collection:", error);
            // Aquí podrías poner un alert o toast si falla
        } finally {
            setStoryHearts(prev => ({ ...prev, loading: false }));
        }
    };


    // --- ACCIONES DE COMPARTIR ---
    const handleShareLink = async () => {
        const url = window.location.href;
        try {
            await navigator.clipboard.writeText(url);
            setShowShareTooltip(true);
            setTimeout(() => setShowShareTooltip(false), 3000);
        } catch (err) {
            console.error('Error al copiar:', err);
        }
    };

    // --- LOGICA DE CAPITULOS (Borrar, Reordenar) - Sin cambios ---
    const handleOpenDeleteChapterModal = (chapter) => setChapterToDelete(chapter);

    const handleConfirmDeleteChapter = async () => {
        if (!chapterToDelete) return;
        const chapterIdToDelete = chapterToDelete.id;
        try {
            const batch = writeBatch(db);
            // 1. Borrar el capítulo y sus documentos asociados
            const chapterRef = doc(db, "chapters", chapterIdToDelete);
            batch.delete(chapterRef);
            
            const chapterHeartsSnapshot = await getDocs(collection(chapterRef, 'heartedBy'));
            chapterHeartsSnapshot.forEach(doc => batch.delete(doc.ref));

            const commentsQuery = query(collection(db, 'chapterComments'), where('chapterId', '==', chapterIdToDelete));
            const commentsSnapshot = await getDocs(commentsQuery);
            commentsSnapshot.forEach(doc => batch.delete(doc.ref));

            // 2. Reordenar los capítulos restantes
            const remainingChapters = chapters
                .filter(c => c.id !== chapterIdToDelete)
                .sort((a, b) => a.order - b.order);
            remainingChapters.forEach((chapter, index) => {
                const newOrder = index + 1;
                if (chapter.order !== newOrder) {
                    batch.update(doc(db, 'chapters', chapter.id), { order: newOrder });
                }
            });

            await batch.commit();
            // 3. Actualizar el estado local
            setChapters(remainingChapters.map((c, i) => ({ ...c, order: i + 1 })));
        } catch (error) {
            console.error("Error al eliminar el capítulo:", error);
            alert("No se pudo eliminar el capítulo.");
        } finally {
            setChapterToDelete(null); // Cierra el modal
        }
    };

    const handleConfirmDeleteStory = async () => {
        try {
            const batch = writeBatch(db);
            const storyRef = doc(db, "stories", storyId);
            
            // 1. Eliminar la historia y sus subcolecciones (corazones)
            batch.delete(storyRef);
            const storyHeartsSnapshot = await getDocs(collection(storyRef, 'heartedBy'));
            storyHeartsSnapshot.forEach(doc => batch.delete(doc.ref));

            // 2. Eliminar cada capítulo y sus subcolecciones/comentarios
            for (const chapter of chapters) {
                const chapterRef = doc(db, 'chapters', chapter.id);
                batch.delete(chapterRef);

                const chapterHeartsSnapshot = await getDocs(collection(chapterRef, 'heartedBy'));
                chapterHeartsSnapshot.forEach(doc => batch.delete(doc.ref));

                const chapterCommentsQuery = query(collection(db, 'chapterComments'), where('chapterId', '==', chapter.id));
                const chapterCommentsSnapshot = await getDocs(chapterCommentsQuery);
                chapterCommentsSnapshot.forEach(doc => batch.delete(doc.ref));
            }

            await batch.commit();
            navigate(`/profile/${loggedInUserProfile.username}`);
        } catch (error) {
            console.error("Error en la eliminación en cascada de la historia:", error);
            alert("Ocurrió un error al eliminar la historia.");
        } finally {
            setIsDeleteStoryModalOpen(false);
            onclose(window.location.reload(),);
        }
    };

    
    const handleMoveChapter = (index, direction) => {
        const newChapters = [...chapters];
        const targetIndex = direction === 'up' ? index - 1 : index + 1;
        if (targetIndex < 0 || targetIndex >= newChapters.length) return;
        [newChapters[index], newChapters[targetIndex]] = [newChapters[targetIndex], newChapters[index]];
        setChapters(newChapters);
    };
    const handleSaveOrder = async () => {
        setIsEditChaptersMode(false);
        const batch = writeBatch(db);
        chapters.forEach((chapter, index) => {
            const chapterRef = doc(db, "chapters", chapter.id);
            if (chapter.order !== index + 1) batch.update(chapterRef, { order: index + 1 });
        });
        try { await batch.commit(); window.location.reload(); } catch (error) { alert("Error guardando orden"); }
    };

    if (loading) return <div className="flex items-center justify-center min-h-screen bg-gray-900 text-white">Cargando Historia...</div>;
    if (error) return <div className="flex items-center justify-center min-h-screen bg-gray-900 text-red-500">{error}</div>;
    if (!story) return null;

    const isOwner = loggedInUserProfile && loggedInUserProfile.uid === story.authorId;

    return (
        <div className="bg-black text-white min-h-screen pb-20 relative">

            {/* --- 3. NOTIFICACIÓN FLOTANTE --- */}
            {notification && (
                <div className="fixed top-24 right-4 md:right-8 z-50 animate-bounce-in">
                    <div className="bg-indigo-600 text-white px-6 py-4 rounded-lg shadow-2xl flex items-center gap-3 border border-indigo-400">
                        <div className="bg-white rounded-full p-1">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                            </svg>
                        </div>
                        <div>
                            <h4 className="font-bold text-sm">¡Hecho!</h4>
                            <p className="text-sm text-indigo-100">{notification.message}</p>
                        </div>
                        <button onClick={() => setNotification(null)} className="ml-2 text-indigo-200 hover:text-white">✕</button>
                    </div>
                </div>
            )}
            
           {/* --- 1. BANNER DE FONDO (SIEMPRE NEGRO CON IMAGEN BORROSA) --- */}
            <div className="w-full h-44 md:h-48 relative overflow-hidden bg-black">
                {/* Imagen de fondo borrosa y muy sutil */}
                {story.coverImageURL && (
                    <img src={story.coverImageURL} className="absolute inset-0 w-full h-full object-cover opacity-20 blur-2xl" alt="" />
                )}
                {/* Degradado a Negro Puro */}
                <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent"></div>
            </div>

            {/* --- 2. CONTENIDO PRINCIPAL --- */}
            <div className="max-w-6xl mx-auto px-4 sm:px-8 relative -mt-32 z-10"> {/* Margen negativo ajustado */}
                
                <div className="flex flex-col md:flex-row gap-8 items-start">
                    
                    {/* COLUMNA IZQUIERDA: PORTADA FIJA (h-96) */}
                    <div className="flex-shrink-0 mx-auto md:mx-0 w-64">
                        <div className="w-64 h-96 rounded-lg shadow-[0_20px_50px_rgba(0,0,0,0.5)] overflow-hidden border border-white/10 bg-gray-900 relative group">
                            <img 
                                src={story.coverImageURL || 'https://placehold.co/600x900/1f2937/ffffff?text=No+Image'} 
                                alt={story.title} 
                                className="w-full h-full object-cover"
                            />
                            {story.isMature && <span className="absolute top-2 right-2 bg-red-600 text-white text-xs font-bold px-2 py-1 rounded shadow">18+</span>}
                        </div>
                        
                        {isOwner && (
                            <button onClick={() => setIsEditModalOpen(true)} className="w-full mt-4 flex items-center justify-center gap-2 px-4 py-2 bg-gray-800 hover:bg-gray-700 border border-gray-700 rounded-md text-sm font-medium transition-colors">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.5L14.732 3.732z" /></svg>
                                Editar Detalles
                            </button>
                        )}
                    </div>

                    {/* COLUMNA DERECHA: INFORMACIÓN (Altura forzada para coincidir con imagen) */}
                    {/* Usamos h-96 en desktop para igualar la altura de la imagen y flex-col */}
                    <div className="flex-grow pt-2 md:pt-4 w-full md:h-96 flex flex-col">
                        
                        {/* 1. TÍTULO Y AUTOR */}
                        <div className="flex-shrink-0 mb-4">
                            <h1 className="text-4xl md:text-5xl font-bold leading-tight mb-1 text-white drop-shadow-lg">
                                {story.title}
                            </h1>
                            <div className="flex items-center gap-2 text-lg">
                                <span className="text-gray-400">por</span>
                                {authorProfile ? (
                                    <Link to={`/profile/${authorProfile.username}`} className="font-semibold text-white hover:text-indigo-400 transition-colors flex items-center gap-2">
                                        <img src={authorProfile.avatarURL} className="w-6 h-6 rounded-full object-cover" alt=""/>
                                        {authorProfile.displayName}
                                    </Link>
                                ) : (
                                    <span className="text-gray-300">@{story.authorUsername}</span>
                                )}
                            </div>
                        </div>

                        {/* 2. TAGS Y PERSONAJES */}
                        <div className="flex-shrink-0 mb-1">

                            {/* Personajes (Link a Search) */}
                            <div className="flex flex-wrap gap-3 mb-4 items-center"> 
                                {story.mainCharacters && story.mainCharacters.map(charName => {
                                    const charData = characterDetails[charName]; 
                                    const avatar = charData?.avatarURL; 

                                    return (
                                        <Link 
                                            key={charName} 
                                            to={`/search?type=stories&character=${encodeURIComponent(charName)}`} 
                                            className="inline-flex items-center pr-4 pl-1 py-1 bg-blue-900/30 border border-blue-500/30 text-blue-100 rounded-full transition-all group hover:bg-blue-800/50 hover:border-blue-400 hover:shadow-[0_0_15px_rgba(59,130,246,0.3)]"
                                        >
                                            {/* Avatar GRANDE (w-10 h-10 = 40px) */}
                                            {avatar ? (
                                                <img 
                                                    src={avatar} 
                                                    alt={charName} 
                                                    className="w-10 h-10 rounded-full object-cover mr-3 border-2 border-blue-400/50 group-hover:border-blue-300"
                                                />
                                            ) : (
                                                // Placeholder Grande
                                                <div className="w-10 h-10 rounded-full bg-blue-800 flex items-center justify-center mr-3 text-sm font-bold border-2 border-blue-600 group-hover:border-blue-400">
                                                    {charName.charAt(0)}
                                                </div>
                                            )}
                                            
                                            {/* Nombre con mejor tipografía */}
                                            <span className="font-semibold text-sm tracking-wide text-blue-50">
                                                {charName}
                                            </span>
                                        </Link>
                                    );
                                })}
                            </div>
                        </div>

                        <div className="inline text-xs leading-relaxed">
                            {story.tags.map((tag) => (
                                <Link 
                                    key={tag} 
                                    to={`/search?type=stories&tag=${encodeURIComponent(tag)}`} // <-- LINK AQUI
                                    className="text-gray-400 opacity-60 hover:text-indigo-400 hover:underline mr-2 cursor-pointer transition-colors"
                                >
                                    #{tag}
                                </Link>
                            ))}
                        </div>
                    

                        {/* 3. BOTONES DE ACCIÓN (PILL) */}
                        <div className="flex-shrink-0 w-full flex justify-start mb-6">
                            <div className="ACTION_PILL_STORY flex items-center py-1">
                                {/* Botón Corazón */}
                                <VerificationGuard user={authUser} placement="top">
                                    <button onClick={toggleStoryHeart} disabled={storyHearts.loading} className={`ACTION_BTN group flex items-center px-4 py-2 rounded-full transition-colors ${storyHearts.hasLiked ? 'text-red-500 bg-red-500/10' : 'text-gray-400 hover:text-white hover:bg-white/5'}`} title={storyHearts.hasLiked ? 'Quitar Me Gusta' : 'Me Gusta'}>
                                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
                                            <path d="M11.645 20.91l-.007-.003-.022-.012a15.247 15.247 0 01-.383-.218 25.18 25.18 0 01-4.244-3.17C4.688 15.36 2.25 12.174 2.25 8.25 2.25 5.322 4.714 3 7.688 3A5.5 5.5 0 0112 5.052 5.5 5.5 0 0116.313 3c2.973 0 5.437 2.322 5.437 5.25 0 3.925-2.438 7.111-4.739 9.256a25.175 25.175 0 01-4.244 3.17 15.247 15.247 0 01-.383.219l-.022.012-.007.004-.003.001a.752.752 0 01-.704 0l-.003-.001z" />
                                        </svg>
                                        <span className="font-medium text-sm ml-2">{storyHearts.count.toLocaleString()}</span>
                                    </button>
                                </VerificationGuard>
                                {/* Botón Compartir */}
                                <div className="relative flex items-center">
                                    {showShareTooltip && ( <div className="absolute bottom-full mb-3 left-1/2 -translate-x-1/2 bg-green-600 text-white text-xs font-bold py-1.5 px-3 rounded-full shadow-lg whitespace-nowrap animate-bounce z-50">¡Copiado!</div> )}
                                    <button onClick={handleShareLink} className="ACTION_BTN text-gray-400 hover:text-white hover:bg-white/5 px-3 py-2 rounded-full transition-colors" title="Copiar enlace">
                                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5"><path fillRule="evenodd" d="M15.75 4.5a3 3 0 11.825 2.066l-8.421 4.679a3.002 3.002 0 010 1.51l8.421 4.679a3 3 0 11-.729 1.31l-8.421-4.678a3 3 0 110-4.132l8.421-4.679a3 3 0 01-.096-.755z" clipRule="evenodd" /></svg>
                                    </button>
                                </div>
                                {/* Botón Guardar */}
                                <button onClick={() => setIsCollectionModalOpen(true)} disabled={!loggedInUserProfile} className="ACTION_BTN text-gray-400 hover:text-white hover:bg-white/5 px-3 py-2 rounded-full transition-colors" title="Guardar">
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5"><path strokeLinecap="round" strokeLinejoin="round" d="M17.593 3.322c1.1.128 1.907 1.077 1.907 2.185V21L12 17.25 4.5 21V5.507c0-1.108.806-2.057 1.907-2.185a48.507 48.507 0 0111.186 0z" /></svg>
                                </button>
                                <div className="h-6 w-px bg-gray-700 mx-2"></div>
                                {/* Vistas */}
                                <div className="flex items-center gap-2 text-gray-500 cursor-default px-4 py-2">
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5"><path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" /><path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                                    <span className="font-medium text-sm">{totalViews.toLocaleString()} views</span>
                                </div>
                            </div>
                        </div>

                        {/* 4. DESCRIPCIÓN CON SCROLL INTERNO (¡La solución!) */}
                        <div className="DESC_STORYPAGE flex-grow overflow-y-auto custom-scrollbar max-w-none text-gray-300 text-sm leading-relaxed mb-1">
                            <style>{`
                                .custom-scrollbar::-webkit-scrollbar { width: 6px; }
                                .custom-scrollbar::-webkit-scrollbar-track { background: rgba(255,255,255,0.05); }
                                .custom-scrollbar::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.2); border-radius: 10px; }
                                .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: rgba(255,255,255,0.3); }
                            `}</style>
                            <p className="whitespace-pre-wrap">
                                {story.description || 'Sin descripción disponible para esta historia.'}
                            </p>
                        </div>

                    </div>
                </div>

                {/* --- SECCIÓN CAPÍTULOS (AQUÍ ES EL CAMBIO GRANDE) --- */}
                <div className="mt-8">
                    <div className="flex justify-between items-end border-b border-gray-800 pb-4 mb-6">
                        <div className="flex items-center gap-3">
                            <h2 className="text-2xl font-bold text-white">Capítulos</h2>
                            <span className="bg-gray-800 text-gray-400 text-xs font-bold px-2 py-1 rounded-md border border-gray-700">{chapters.length}</span>
                        </div>
                        
                        <div className="flex gap-3">
                            {isOwner && (
                                <>
                                    <button onClick={() => setIsEditChaptersMode(!isEditChaptersMode)} className="text-sm font-medium text-gray-400 hover:text-white transition-colors">
                                        {isEditChaptersMode ? 'Cancelar' : 'Organizar'}
                                    </button>
                                    {isEditChaptersMode && <button onClick={handleSaveOrder} className="text-sm font-bold text-green-400 hover:text-green-300">Guardar</button>}
                                    <Link to={`/write?storyId=${story.id}`} state={{ story: story }} className="flex items-center gap-1 bg-indigo-600 hover:bg-indigo-500 text-white px-3 py-1.5 rounded-md font-bold text-xs transition-colors">
                                        + Nuevo
                                    </Link>
                                </>
                            )}
                        </div>
                    </div>

                    <div className="space-y-3 ">
                        {chapters.length > 0 ? (
                            chapters.map((chapter, index) => {
                                const now = new Date();
                                let isLocked = false;
                                if (chapter.scheduledAt) {
                                    const releaseDate = chapter.scheduledAt.toDate(); 
                                    if (releaseDate > now) isLocked = true;
                                }

                                // --- MODO EDICIÓN (Reordenar) - Sigue igual
                                if (isEditChaptersMode) {
                                   return (
                                       <div key={chapter.id} className="flex items-center justify-between p-4 border border-gray-700 rounded-md text-white">
                                            <div className="flex items-center">
                                                <span className="text-gray-500 font-mono mr-4 w-6 text-right">{index + 1}</span>
                                                <p className="font-semibold">{chapter.chapterTitle}</p>
                                                {/* Badge opcional para que el autor sepa que está programado */}
                                                {isLocked && <span className="ml-2 text-xs bg-indigo-900 text-indigo-300 px-2 rounded">Programado</span>}
                                            </div>
                                            <div className="flex items-center space-x-2">
                                                <button onClick={() => handleMoveChapter(index, 'up')} disabled={index === 0} className="p-2 rounded-full hover:bg-gray-700 disabled:opacity-30 text-gray-400">
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M5.293 7.707a1 1 0 010-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414L11 5.414V17a1 1 0 11-2 0V5.414L6.707 7.707a1 1 0 01-1.414 0z" clipRule="evenodd" /></svg>
                                                </button>
                                                <button onClick={() => handleMoveChapter(index, 'down')} disabled={index === chapters.length - 1} className="p-2 rounded-full hover:bg-gray-700 disabled:opacity-30 text-gray-400">
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M14.707 12.293a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L9 14.586V3a1 1 0 012 0v11.586l2.293-2.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                                                </button>
                                                <button onClick={() => handleOpenDeleteChapterModal(chapter)} className="p-2 rounded-full text-red-400 hover:bg-red-900/30 ml-2">
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm4 0a1 1 0 012 0v6a1 1 0 11-2 0V8z" clipRule="evenodd" /></svg>
                                                </button>
                                            </div>
                                       </div>
                                   );
                                }

                                // 3. LOGICA DE VISUALIZACIÓN PÚBLICA
                                
                                // CASO A: Si soy el dueño, SIEMPRE veo todo (aunque esté programado)
                                if (isOwner) {
                                     // AJUSTE 3: Si es HYPE y está bloqueado, mostramos el LockedChapterCard pero con overlay de autor
                                     if (isLocked && chapter.releaseType === 'hype') {
                                         return (
                                             <div key={chapter.id} className="relative group">
                                                 {/* Renderizamos la tarjeta visual bonita */}
                                                 <LockedChapterCard chapter={chapter} index={index} />
                                                 
                                                 {/* Overlay para decirle al autor que es SU vista */}
                                                 <div className="absolute inset-0 bg-black/60 flex items-center justify-between px-6 opacity-0 group-hover:opacity-100 transition-opacity rounded-md backdrop-blur-sm border border-pink-500/50">
                                                     <div className="text-white">
                                                         <p className="font-bold text-pink-400 flex items-center gap-2">
                                                             🔒 MODO HYPE ACTIVO
                                                         </p>
                                                         <p className="text-xs text-gray-300">Los lectores ven el contador. Tú puedes editar.</p>
                                                     </div>
                                                     <div className="flex gap-3">
                                                         <Link to={`/story/${story.id}/chapter/${chapter.id}`} className="px-3 py-1.5 bg-gray-700 hover:bg-gray-600 rounded text-xs font-bold">Ver</Link>
                                                         <Link to={`/write/edit/${chapter.id}`} className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-500 rounded text-xs font-bold text-white">Editar</Link>
                                                     </div>
                                                 </div>
                                             </div>
                                         );
                                     }

                                     // AJUSTE 2: Si es SILENT SCHEDULE y está bloqueado
                                     if (isLocked && chapter.releaseType !== 'hype') {
                                         return (
                                            <div key={chapter.id} className="relative group border border-dashed border-gray-600 bg-gray-900/30 rounded-md p-4 flex items-center justify-between opacity-75 hover:opacity-100 transition-all">
                                                <div className="flex items-center gap-4">
                                                    <span className="text-gray-600 font-mono text-sm p-4 text-right">{index + 1}</span>
                                                    <div>
                                                        <p className="font-bold text-lg text-gray-400 flex items-center gap-2">
                                                            {chapter.chapterTitle}
                                                            <span className="text-[10px] uppercase font-bold px-2 py-0.5 rounded bg-gray-700 text-gray-300 border border-gray-500">
                                                                👁️ INVISIBLE
                                                            </span>
                                                        </p>
                                                        <div className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                                                            <span>Solo tú puedes ver esto. Se publicará el:</span>
                                                            <span className="text-indigo-400 font-mono">{chapter.scheduledAt.toDate().toLocaleString()}</span>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                                    <Link to={`/story/${story.id}/chapter/${chapter.id}`} className="px-3 py-1 bg-gray-800 rounded text-xs">Ver</Link>
                                                    <Link to={`/write/edit/${chapter.id}`} className="px-3 py-1 bg-indigo-900 text-indigo-200 rounded text-xs">Editar</Link>
                                                </div>
                                            </div>
                                         );
                                     }

                                     // CAPITULO NORMAL (PUBLICADO) - VISTA AUTOR
                                     return (
                                        <Link key={chapter.id} to={`/story/${story.id}/chapter/${chapter.id}`} className="group flex items-center justify-between p-4 hover:bg-gray-800 border border-gray-700/50 hover:border-gray-600 rounded-md transition-all duration-200">
                                             <div className="flex items-center gap-4">
                                                <span className="text-gray-500 font-mono text-sm p-4 text-right group-hover:text-indigo-400">{index + 1}</span>
                                                <div>
                                                    <p className="font-bold text-lg text-gray-200 group-hover:text-white transition-colors">{chapter.chapterTitle}</p>
                                                    <div className="flex items-center gap-3 text-xs text-gray-500 mt-1">
                                                        <span>{formatTimestamp(chapter.createdAt)}</span>
                                                        <span>•</span>
                                                        <span className="flex items-center gap-1">
                                                            {chapter.viewCount || 0} views
                                                        </span>
                                                    </div>
                                                </div>
                                             </div>
                                             <div className="text-gray-600 group-hover:text-indigo-400 transition-colors">
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" /></svg>
                                             </div>
                                        </Link>
                                     );
                                }

                                // --- VISTA LECTOR (NO DUEÑO) ---
                                
                                // Opción 1: MODO FANTASMA (Scheduled) -> Invisible
                                if (isLocked && chapter.releaseType !== 'hype') return null;

                                // Opción 2: MODO HYPE (Pre-save) -> Candado
                                if (isLocked && chapter.releaseType === 'hype') {
                                    return <LockedChapterCard key={chapter.id} chapter={chapter} index={index} />;
                                }

                                // Opción 3: NORMAL
                                return (
                                    <Link key={chapter.id} to={`/story/${story.id}/chapter/${chapter.id}`} className="group flex items-center justify-between p-4 hover:bg-gray-800 border border-gray-700/50 hover:border-gray-600 rounded-md transition-all duration-200">
                                         <div className="flex items-center gap-4">
                                            <span className="text-gray-500 font-mono text-sm p-4 text-right group-hover:text-indigo-400">{index + 1}</span>
                                            <div>
                                                <p className="font-bold text-lg text-gray-200 group-hover:text-white transition-colors">{chapter.chapterTitle}</p>
                                                <div className="flex items-center gap-3 text-xs text-gray-500 mt-1">
                                                    <span>{formatTimestamp(chapter.createdAt)}</span>
                                                    <span>•</span>
                                                    <span className="flex items-center gap-1">
                                                        {chapter.viewCount || 0} views
                                                    </span>
                                                </div>
                                            </div>
                                         </div>
                                         <div className="text-gray-600 group-hover:text-indigo-400 transition-colors">
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" /></svg>
                                         </div>
                                    </Link>
                                );
                            })
                        ) : (
                            <div className="text-center py-16 bg-gray-800/30 rounded-lg border border-gray-700 border-dashed">
                                <p className="text-gray-500 text-lg mb-2">La historia comienza aquí.</p>
                                <p className="text-gray-600 text-sm">Aún no hay capítulos publicados.</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* MODALES */}
            {isEditModalOpen && <EditStoryModal story={story} onClose={() => setIsEditModalOpen(false)} onDeleteClick={() => {setIsEditModalOpen(false); setIsDeleteStoryModalOpen(true);}} />}
            
            <ConfirmDeleteModal 
                isOpen={!!chapterToDelete}
                onConfirm={handleConfirmDeleteChapter}
                onCancel={() => setChapterToDelete(null)}
                itemType="el capítulo"
                itemName={chapterToDelete?.chapterTitle}
            />

            <ConfirmDeleteModal 
                isOpen={isDeleteStoryModalOpen}
                onConfirm={handleConfirmDeleteStory}
                onCancel={() => setIsDeleteStoryModalOpen(false)}
                itemType="la historia"
                itemName={story?.title}
            />

            {isCollectionModalOpen && (
                <AddToCollectionModal 
                    isOpen={isCollectionModalOpen} 
                    onClose={() => setIsCollectionModalOpen(false)}
                    story={story}
                    userProfile={loggedInUserProfile}
                />
            )}
        </div>
    );
};

// --- COMPONENTE: PÁGINA DE PRE-ESTRENO (MODO HYPE/SILENT) ---
const PreSavePage = ({ chapter, story, authorProfile }) => {
    const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
    const [isPreSaved, setIsPreSaved] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const authUser = auth.currentUser; // Obtenemos el usuario actual

    // 1. Verificar si ya lo guardé antes (al cargar)
    useEffect(() => {
        if (!authUser || !chapter.id) return;
        
        const checkStatus = async () => {
            const q = query(
                collection(db, 'presaves'),
                where('userId', '==', authUser.uid),
                where('chapterId', '==', chapter.id)
            );
            const snap = await getDocs(q);
            if (!snap.empty) {
                setIsPreSaved(true);
            }
        };
        checkStatus();
    }, [authUser, chapter.id]);

    // 2. Contador (Sin cambios)
    useEffect(() => {
        const calculateTimeLeft = () => {
            if (!chapter.scheduledAt) return;
            const releaseDate = chapter.scheduledAt.toDate(); 
            const now = new Date();
            const difference = releaseDate - now;

            if (difference > 0) {
                setTimeLeft({
                    days: Math.floor(difference / (1000 * 60 * 60 * 24)),
                    hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
                    minutes: Math.floor((difference / 1000 / 60) % 60),
                    seconds: Math.floor((difference / 1000) % 60),
                });
            }
        };
        calculateTimeLeft();
        const timer = setInterval(calculateTimeLeft, 1000);
        return () => clearInterval(timer);
    }, [chapter.scheduledAt]);

    // 3. Lógica de Guardado REAL
    const handlePreSave = async () => {
        if (!authUser) {
            alert("Inicia sesión para guardar este capítulo.");
            return;
        }
        setIsLoading(true);
        try {
            // Escribimos en la colección raíz 'presaves'
            await addDoc(collection(db, 'presaves'), {
                userId: authUser.uid,
                chapterId: chapter.id,
                storyId: story.id,
                createdAt: serverTimestamp(),
                status: 'pending' // Importante para el Cron Job
            });
            setIsPreSaved(true);
        } catch (e) {
            console.error("Error pre-saving:", e);
            alert("Hubo un error al guardar. Intenta de nuevo.");
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-black text-white flex flex-col items-center justify-center relative overflow-hidden p-4">
            {/* Fondo Ambiental Borroso */}
            <div className="absolute inset-0 z-0">
                <img 
                    src={story?.coverImageURL || 'https://placehold.co/600x900/1f2937/ffffff?text=No+Image'} 
                    className="w-full h-full object-cover opacity-30 blur-3xl scale-110" 
                    alt="" 
                />
                <div className="absolute inset-0 bg-black/60"></div>
            </div>

            {/* Contenido Central */}
            <div className="relative z-10 text-center max-w-2xl w-full bg-gray-900/40 backdrop-blur-md border border-white/10 p-8 md:p-12 rounded-2xl shadow-[0_0_50px_rgba(0,0,0,0.5)] animate-fadeIn">
                
                {/* Candado Icono */}
                <div className="w-20 h-20 bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-6 border border-gray-700 shadow-lg">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                    </svg>
                </div>

                <h2 className="text-sm font-bold tracking-[0.3em] text-indigo-400 uppercase mb-2">
                    PRÓXIMAMENTE
                </h2>
                
                <h1 className="text-3xl md:text-5xl font-bold text-white mb-4 drop-shadow-lg">
                    {chapter.chapterTitle}
                </h1>
                
                <p className="text-gray-300 mb-8 text-lg">
                    Un nuevo capítulo de <span className="font-semibold text-white">{story?.title}</span><br/>
                    por <span className="text-indigo-300">@{authorProfile?.username || 'el autor'}</span>
                </p>

                {/* CONTADOR GRANDE */}
                <div className="grid grid-cols-4 gap-4 mb-10 max-w-lg mx-auto">
                    {Object.entries(timeLeft).map(([label, value]) => (
                        <div key={label} className="bg-black/50 rounded-lg p-3 border border-gray-700">
                            <span className="block text-2xl md:text-4xl font-mono font-bold text-white">
                                {String(value).padStart(2, '0')}
                            </span>
                            <span className="text-[10px] md:text-xs text-gray-500 uppercase tracking-wider">{label}</span>
                        </div>
                    ))}
                </div>

                {/* Botón Pre-Save Actualizado */}
            <button 
                onClick={handlePreSave}
                disabled={isPreSaved || isLoading}
                className={`w-full md:w-auto px-8 py-4 rounded-full font-bold text-lg transition-all transform hover:scale-105 flex items-center justify-center gap-3 mx-auto ${
                    isPreSaved 
                    ? 'bg-green-600 text-white cursor-default' 
                    : 'bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white shadow-lg shadow-indigo-900/50'
                }`}
            >
                {isLoading ? (
                    <span>Guardando...</span>
                ) : isPreSaved ? (
                    <>
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                        PRE-SAVED
                    </>
                ) : (
                    <>
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" /></svg>
                        PRE-SAVE CHAPTER
                    </>
                )}
            </button>

                
                <p className="mt-4 text-xs text-gray-500">
                    {isPreSaved 
                        ? "Te notificaremos en cuanto se libere el candado." 
                        : "Guarda este capítulo para recibir una notificación en el momento del estreno."}
                </p>
            </div>
        </div>
    );
};

const ReadingPage = ({ loggedInUserProfile }) => { 
    const { storyId, chapterId } = useParams(); 
    const [chapter, setChapter] = useState(null); 
    const [story, setStory] = useState(null); 
    const [chapterList, setChapterList] = useState([]);
    const [loading, setLoading] = useState(true); 
    const [error, setError] = useState(null);
    const [chapterHearts, setChapterHearts] = useState({ count: 0, hasLiked: false, loading: true });
    const [authorProfile, setAuthorProfile] = useState(null);

    const authUser = auth.currentUser;

    // ++ INICIO: NUEVOS ESTADOS PARA "SHARE QUOTE" ++
    const [selectedText, setSelectedText] = useState(null); // Almacenará { text: '...', rect: DOMRect }
    const [isQuoteSelectorOpen, setIsQuoteSelectorOpen] = useState(false);

    const [showShareTooltip, setShowShareTooltip] = useState(false);
    
    // Estos los usaremos en pasos futuros, pero los definimos ya
    const [isQuotePreviewOpen, setIsQuotePreviewOpen] = useState(false); 
    const [finalQuote, setFinalQuote] = useState(''); // El texto que pasa del modal 1 al 2

    const [isCollectionModalOpen, setIsCollectionModalOpen] = useState(false);
    // -- FIN: NUEVOS ESTADOS --

    // View counter effect
    useEffect(() => {
        if (!chapterId) return;

        const viewKey = `viewed-${chapterId}`;
        const hasViewedInSession = sessionStorage.getItem(viewKey);

        if (!hasViewedInSession) {
            const timer = setTimeout(async () => {
                try {
                    const chapterRef = doc(db, 'chapters', chapterId);
                    await updateDoc(chapterRef, { viewCount: increment(1) });
                    sessionStorage.setItem(viewKey, 'true');
                } catch (e) {
                    console.error("Error incrementing view count:", e);
                }
            }, 50000); // 50 seconds

            return () => clearTimeout(timer);
        }
    }, [chapterId]);

    // Chapter hearts effect
    useEffect(() => {
    if (!chapterId) return; // Solo necesitamos el ID del capítulo

    const chapterRef = doc(db, "chapters", chapterId);
    const unsubscribeChapter = onSnapshot(chapterRef, (doc) => {
        if (doc.exists()) {
            setChapterHearts(prev => ({ ...prev, count: doc.data().heartCount || 0 }));
        }
    });

    return () => unsubscribeChapter();
}, [chapterId]); // Solo depende del chapterId

// 2. Hook PRIVADO para saber si el USUARIO ACTUAL ya le dio like
useEffect(() => {
    // Si no hay usuario, reseteamos su estado de "like" y salimos
    if (!loggedInUserProfile?.uid) {
        setChapterHearts(prev => ({ ...prev, hasLiked: false, loading: false }));
        return;
    }
    
    // Si hay usuario, verificamos si existe su documento de "like"
    const heartRef = doc(db, `chapters/${chapterId}/heartedBy`, loggedInUserProfile.uid);
    const unsubscribeHeart = onSnapshot(heartRef, (doc) => {
        setChapterHearts(prev => ({ ...prev, hasLiked: doc.exists(), loading: false }));
    });

    return () => unsubscribeHeart();
}, [chapterId, loggedInUserProfile?.uid]);

    const toggleChapterHeart = async () => {
        if (chapterHearts.loading || !loggedInUserProfile?.uid) return;
        setChapterHearts(prev => ({ ...prev, loading: true }));

        const batch = writeBatch(db);
        const chapterRef = doc(db, "chapters", chapterId);
        const heartRef = doc(db, `chapters/${chapterId}/heartedBy`, loggedInUserProfile.uid);
        
        if (chapterHearts.hasLiked) {
            batch.update(chapterRef, { heartCount: increment(-1) });
            batch.delete(heartRef);
        } else {
            batch.update(chapterRef, { heartCount: increment(1) });
            batch.set(heartRef, { heartedAt: serverTimestamp() });
        }

        try {
            await batch.commit();
        } catch (error) {
            console.error("Error toggling chapter heart:", error);
        } finally {
            setChapterHearts(prev => ({ ...prev, loading: false }));
        }
    };

    // ++ INICIO: NUEVAS FUNCIONES PARA "SHARE QUOTE" ++

    // 1. Detecta la selección de texto en el capítulo
    const handleTextSelection = (e) => {
        const commentBox = e.target.closest('.COMMENT_DIV');
        if (commentBox) {
            setSelectedText(null);
            return; 
        }

        // 1. Captura la selección INMEDIATAMENTE
        const selection = window.getSelection();
        const text = selection.toString().trim();
        const range = text.length > 0 ? selection.getRangeAt(0) : null;
        const rect = range ? range.getBoundingClientRect() : null;

        // 2. Retrasa la actualización del estado
        setTimeout(() => {
            // 3. Vuelve a comprobar la selección actual
            //    Esto evita una "race condition" si el usuario hizo clic en otro lugar muy rápido
            const currentSelectionText = window.getSelection().toString().trim();

            if (text.length > 0 && text === currentSelectionText) {
                // Si el texto que capturamos sigue siendo el seleccionado, MUESTRA la burbuja
                setSelectedText({ text, rect });
            } else if (currentSelectionText.length === 0) {
                // Si ahora no hay nada seleccionado, OCULTA la burbuja
                setSelectedText(null);
            }
            // (Si el texto es diferente, significa que el usuario ya está seleccionando otra cosa,
            // así que no hacemos nada y dejamos que el próximo 'handleTextSelection' se encargue)
        }, 0); // 0ms de retraso es suficiente
    };

    // 2. Se llama al hacer clic en la burbuja "SHARE QUOTE"
    const handleShareFromBubble = () => {
        if (!selectedText) return;
        // Abrimos el modal del PASO 2. 
        // Ya NO borramos el estado aquí.
        setIsQuoteSelectorOpen(true); 
    };

    // 3. Se llama al hacer clic en el botón de "Share Quote" en el bloque de hearts
    const handleShareFromButton = () => {
        setIsQuoteSelectorOpen(true);
        setSelectedText(null); // Asegura que la burbuja se cierre si estaba abierta
    };

    // 4. Se llama cuando el Modal de Selección (PASO 2) termina
    // (La implementaremos en el siguiente paso, pero la dejamos lista)
    const onQuoteSelected = (quote) => {
        setFinalQuote(quote);
        setIsQuoteSelectorOpen(false);
        setIsQuotePreviewOpen(true); // Abre el siguiente modal (PASO 3)
    };

const handleShareLink = async () => {
        const url = window.location.href;
        const title = `Lee "${chapter?.chapterTitle || 'un capítulo'}" de ${story?.title || 'una historia'}`;
        const text = `¡Estoy leyendo esta historia en Typefiction y te la recomiendo!`;
        const shareData = { title, text, url };

        // Detectamos si es un dispositivo móvil
        const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

        if (isMobile && navigator.share && navigator.canShare && navigator.canShare(shareData)) {
            // CASO MÓVIL: Menú nativo
            try {
                await navigator.share(shareData);
            } catch (error) {
                if (error.name !== 'AbortError') {
                    console.error('Error al compartir, intentando copiar...', error);
                    await copyToClipboard(url); // Intenta copiar silenciosamente
                    triggerTooltip(); // Muestra la burbuja
                }
            }
        } else {
            // CASO DESKTOP: Copiar al portapapeles y mostrar burbuja
            await copyToClipboard(url);
            triggerTooltip();
        }
    };

    // Helper para activar la burbuja por 3 segundos
    const triggerTooltip = () => {
        setShowShareTooltip(true);
        setTimeout(() => {
            setShowShareTooltip(false);
        }, 3000); // 3 segundos
    };

    // Helper para copiar al portapapeles (Modificado: Ya no usa alert)
    const copyToClipboard = async (text) => {
        try {
            if (navigator.clipboard && navigator.clipboard.writeText) {
                await navigator.clipboard.writeText(text);
            } else {
                // Fallback antiguo
                const textArea = document.createElement("textarea");
                textArea.value = text;
                document.body.appendChild(textArea);
                textArea.select();
                document.execCommand("copy");
                document.body.removeChild(textArea);
            }
        } catch (err) {
            console.error('No se pudo copiar el enlace:', err);
        }
    };
    
    // -- FIN: NUEVAS FUNCIONES --

    useEffect(() => { 
        const fetchContent = async () => { 
            if (!storyId || !chapterId) return;
            setLoading(true); 
            setError(null); 
            try { 
                // 1. Cargar Capítulo
                const chapterRef = doc(db, 'chapters', chapterId); 
                const chapterSnap = await getDoc(chapterRef); 
                if (!chapterSnap.exists()){ throw new Error("Este capítulo no existe."); } 
                
                const chData = { id: chapterSnap.id, ...chapterSnap.data() };
                
                // Verificar que corresponda a la historia correcta (seguridad extra)
                if(chData.storyId !== storyId) { throw new Error("URL inválida."); }

                setChapter(chData); 
                
                // 2. Cargar Autor
                const authorRef = doc(db, 'users', chData.authorId);
                const authorSnap = await getDoc(authorRef);
                if(authorSnap.exists()){
                    setAuthorProfile({ uid: authorSnap.id, ...authorSnap.data() });
                } else {
                    setAuthorProfile(null); 
                }

                // 3. Cargar Historia
                const storyRef = doc(db, 'stories', storyId); 
                const storySnap = await getDoc(storyRef); 
                if (!storySnap.exists()) { throw new Error("No se pudo encontrar la historia principal."); } 
                setStory({ id: storySnap.id, ...storySnap.data() }); 

                // 4. Cargar Lista de Capítulos (Solo publicados)
                // Nota: Aquí podríamos filtrar también por fecha si quisiéramos ocultar los futuros en la navegación,
                // pero tu lógica actual usa 'status == published', que es correcto.
                const chaptersQuery = query(collection(db, 'chapters'), where("storyId", "==", storyId), where("status", "==", "published"), orderBy("order", "asc"));
                const chaptersSnapshot = await getDocs(chaptersQuery);
                setChapterList(chaptersSnapshot.docs.map(doc => doc.id));

            } catch (err) { 
                setError(err.message); 
            } 
            setLoading(false); 
        }; 
        fetchContent(); 
    }, [storyId, chapterId]);
    

    console.log("[ReadingPage] Verificando estado donaciones:", {
        loading,
        error,
        chapterId: chapter?.id,
        chapterPermiteDonaciones: chapter?.allowDonations,
        authorProfileUid: authorProfile?.uid,
        authorProvider: authorProfile?.donations?.provider,
        authorPayPalStatus: authorProfile?.donations?.paypalOnboardingStatus,
        authorStripeId: authorProfile?.donations?.stripeAccountId,
    });


    if (loading) return <div className="flex items-center justify-center min-h-screen bg-gray-900 text-white">Cargando Capítulo...</div>; 
    if (error) return <div className="flex items-center justify-center min-h-screen bg-gray-900 text-red-500">{error}</div>; 
    if (!chapter || !story ) return null;

    // --- Condición Principal (con logs internos) ---
   const shouldShowDonationBlock = 
        chapter.allowDonations && 
        authorProfile?.uid && // <-- Es bueno verificar que el autor exista primero
        (
            // Opción 1: PayPal está conectado Y completado
            (authorProfile?.donations?.provider === 'paypal' && authorProfile?.donations?.paypalOnboardingStatus === 'COMPLETED') ||
            // Opción 2: Stripe está conectado
            (authorProfile?.donations?.provider === 'stripe') // <-- ¡AQUÍ ESTÁ LA MAGIA!
        );

    console.log("[ReadingPage] ¿Debería mostrar DonationBlock?:", shouldShowDonationBlock);

    const isOwner = loggedInUserProfile && loggedInUserProfile.uid === chapter.authorId;
    const now = new Date();
    let isLocked = false;

    // Verificamos si tiene fecha programada y si está en el futuro
    if (chapter.scheduledAt) {
        const releaseDate = chapter.scheduledAt.toDate();
        if (releaseDate > now) {
            isLocked = true;
        }
    }

    // LA REGLA MAESTRA:
    // Si está bloqueado (fecha futura) Y NO eres el dueño...
    // -> Muestras la PreSavePage. NO muestras el contenido.
    if (isLocked && !isOwner) {
        return (
            <PreSavePage 
                chapter={chapter} 
                story={story} 
                authorProfile={authorProfile} 
            />
        );
    }

    const currentChapterIndex = chapterList.findIndex(id => id === chapterId);
    const prevChapterId = currentChapterIndex > 0 ? chapterList[currentChapterIndex - 1] : null;
    const nextChapterId = currentChapterIndex < chapterList.length - 1 ? chapterList[currentChapterIndex + 1] : null;   

    return ( 
        <div className="bg-gray-100 text-gray-800 min-h-screen LECTOR">
            <header className="sticky top-0 z-50 shadow-md p-4 flex justify-between items-center HEADER_LECTOR ">
                <Link to={`/story/${storyId}`} className="px-4 py-2 bg-gray-800 text-white rounded-md hover:bg-gray-700 BACK_BTN">Regresar</Link>

                <div className="flex items-center space-x-4">
                    {isOwner && (
                        <Link to={`/write/edit/${chapterId}`} className="px-4 py-2 text-sm font-bold text-white bg-indigo-600 rounded-md hover:bg-indigo-700">
                            Editar Capítulo
                        </Link>
                    )}
                    <Link to="/" className="px-4 py-2 bg-gray-800 text-white rounded-md hover:bg-gray-700">Salir del lector</Link>
                </div>
            </header>

        <main className="max-w-4xl mx-auto p-4 sm:p-0 LECTOR_BND">
            {loading && <div>Cargando contenido...</div>}
            {error && <div>Error al cargar: {error}</div>}

            {authorProfile ? (
                <>

                        {/* --- TÍTULO AÑADIDO AL CUERPO --- */}
                        <h1 className="mb-8 mt-6 writing-page-input">{chapter.chapterTitle}</h1>
                        {/* --- FIN TÍTULO AÑADIDO --- */}

                        {/* --- POR AUTOR --- */}
                        <div className="">
                            <p className="truncate writing-page-story-info">
                                Leyendo la historia:{' '} 
                                <Link to={`/story/${storyId}`} className="font-semibold text-hiperlink hover:underline">{story.title}</Link> 
                                {' '}por{' '} 
                                <Link to={`/profile/${authorProfile.username}`} className="font-semibold text-hiperlink hover:underline">@{authorProfile.username}</Link>
                            </p>
                        </div>


                    {/* --- INICIO DE NUEVOS CAMPOS --- */}
                        
                        {/* 2. SUMMARY (Opcional) */}
                        {chapter.summary && (
                            <div className="chapter-summary-readingpage writing-page-text-area">
                                {/* Añadimos la etiqueta antes */}
                                <p style={{ whiteSpace: 'pre-wrap' }}>
                                    <span style={{ color: '#919aaaff', fontFamily: 'Google Sans Code', fontWeight: '600', fontStyle: 'normal', marginRight: '0.6em', fontSize: '1em',}}>&lt;Summary&gt;</span>
                                    {chapter.summary}
                                </p>
                            </div>
                        )}

                        {/* --- 3. NOTAS INICIALES (Con etiqueta prepuesta) --- */}
                        {chapter.initialNotes && (
                            <div className="chapter-notes-readingpage writing-page-text-area">
                                {/* Añadimos la etiqueta antes */}
                                <p style={{ whiteSpace: 'pre-wrap' }}>
                                    <span style={{ color: '#919aaaff', fontFamily: 'Google Sans Code', fontWeight: '600', fontStyle: 'normal', marginRight: '0.6em', fontSize: '1em',}}>&lt;Initial notes&gt;</span>
                                    {chapter.initialNotes}
                                </p>
                            </div>
                        )}

                        <div className="mt-4 mb-4 border-t border-gray-700 pt-4 opacity-60 mr-80 pointer-events-none">
                        {/* <p className="text-sm text-left text-gray-400 mb-4 italic">(Vista previa de cómo verán el bloque tus lectores)</p> */}
                            
                        </div>

                        {/* 4. CONTENIDO (CKEditor) */}
                        <div
                            className="ck-content relative" // "relative" es clave para la burbuja
                            dangerouslySetInnerHTML={{ __html: chapter.content }}
                            onMouseUp={handleTextSelection}
                            onTouchEnd={handleTextSelection} // Para móvil
                        />
                        
                        {/* ++ AQUÍ VA LA BURBUJA FLOTANTE ++ */}
                        {selectedText && (
                            <div
                                className="absolute z-50 p-2 bg-gray-900 text-white rounded-md shadow-lg flex items-center gap-2 cursor-pointer transition-colors SHARE_QUOTE_BUBBLE"
                                style={{
                                    // Ajustamos la posición para que sea relativa a la página, no a la ventana
                                    // Usamos Math.max para evitar que la burbuja se vaya muy arriba si la selección es pequeña
                                    top: `${Math.max(0, selectedText.rect.top + window.scrollY - 50)}px`, // 50px de offset para ponerla *encima*
                                    left: `${selectedText.rect.left + window.scrollX + (selectedText.rect.width / 2)}px`, // Centrada
                                    transform: 'translateX(-50%)', // Ajuste fino de centrado
                                }}
                                // Usamos onMouseDown en lugar de onClick para que no interfiera con onMouseUp del div padre
                                onMouseDown={(e) => { 
                                    e.preventDefault(); // <-- ¡ESTA ES LA LÍNEA MÁS IMPORTANTE!
                                    e.stopPropagation(); 
                                    handleShareFromBubble(); 
                                }}
                                onMouseUp={(e) => e.stopPropagation()}
                            >
                                {/* Ícono de Compartir/Citar */}
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M7.146 3.146a.5.5 0 01.708 0l4 4a.5.5 0 010 .708l-4 4a.5.5 0 11-.708-.708L10.793 7.5 7.146 3.854a.5.5 0 010-.708zm-4 0a.5.5 0 01.708 0l4 4a.5.5 0 010 .708l-4 4a.5.5 0 11-.708-.708L6.793 7.5 3.146 3.854a.5.5 0 010-.708z" clipRule="evenodd" />
                                </svg>
                                <span className="text-sm font-semibold whitespace-nowrap p-2 pr-3">SHARE QUOTE</span>
                            </div>
                        )}
                        {/* -- FIN DE LA BURBUJA -- */}

                        {/* 5. NOTAS FINALES (Opcional) */}
                        {chapter.finalNotes && (
                            <div className="chapter-summary-readingpage writing-page-text-area">
                                {/* Añadimos la etiqueta antes */}
                                <p style={{ whiteSpace: 'pre-wrap' }}>
                                    <span style={{ color: '#919aaaff', fontFamily: 'Google Sans Code', fontWeight: '600', fontStyle: 'normal', marginRight: '0.6em', fontSize: '1em',}}>&lt;Final notes&gt;</span>
                                    {chapter.finalNotes}
                                </p>
                            </div>
                        )}

                    
                    {/* ++ NUEVO BLOQUE DE ACCIONES (ESTILO PILL) ++ */}
                        <div className="w-full flex justify-start my-8">
                            <div className="ACTION_PILL flex items-center">
                                
                                {/* 1. Botón Corazón */}
                                <VerificationGuard user={authUser} placement="top">
                                <button 
                                    onClick={toggleChapterHeart} 
                                    disabled={chapterHearts.loading} 
                                    className={`ACTION_BTN group ${chapterHearts.hasLiked ? 'text-red-500' : 'text-gray-400 hover:text-white'}`}
                                    title={chapterHearts.hasLiked ? 'Quitar Me Gusta' : 'Me Gusta'}
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
                                        <path d="M11.645 20.91l-.007-.003-.022-.012a15.247 15.247 0 01-.383-.218 25.18 25.18 0 01-4.244-3.17C4.688 15.36 2.25 12.174 2.25 8.25 2.25 5.322 4.714 3 7.688 3A5.5 5.5 0 0112 5.052 5.5 5.5 0 0116.313 3c2.973 0 5.437 2.322 5.437 5.25 0 3.925-2.438 7.111-4.739 9.256a25.175 25.175 0 01-4.244 3.17 15.247 15.247 0 01-.383.219l-.022.012-.007.004-.003.001a.752.752 0 01-.704 0l-.003-.001z" />
                                    </svg>
                                    <span className="font-medium text-sm ml-1.5">{chapterHearts.count.toLocaleString()}</span>
                                </button>
                                </VerificationGuard>

                                {/* 2. Botón Share Quote (Citar) */}
                                <button 
                                    onClick={handleShareFromButton} 
                                    className="ACTION_BTN text-gray-300 hover:text-white" 
                                    title="Crear Cita"
                                >
                                    {/* Icono de Scanner/Cita estilo referencia */}
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15" />
                                    </svg>
                                    <span className="font-medium text-sm ml-2">Share quote</span>
                                </button>

                                {/* 3. Botón Compartir (Con Burbuja) */}
                                <div className="relative flex items-center"> {/* Contenedor relativo */}
                                    
                                    {/* La Burbuja de "Enlace Copiado" */}
                                    {showShareTooltip && (
                                        <div className="absolute bottom-full mb-3 left-1/2 transform -translate-x-1/2 bg-green-600 text-white text-xs font-bold py-1.5 px-3 rounded-full shadow-lg whitespace-nowrap animate-bounce z-50">
                                            ¡Enlace copiado!
                                            {/* Triangulito decorativo abajo */}
                                            <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-green-600"></div>
                                        </div>
                                    )}

                                    <button 
                                        className="ACTION_BTN text-gray-400 hover:text-white" 
                                        title="Compartir enlace del capítulo"
                                        onClick={handleShareLink} 
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
                                            <path fillRule="evenodd" d="M15.75 4.5a3 3 0 11.825 2.066l-8.421 4.679a3.002 3.002 0 010 1.51l8.421 4.679a3 3 0 11-.729 1.31l-8.421-4.678a3 3 0 110-4.132l8.421-4.679a3 3 0 01-.096-.755z" clipRule="evenodd" />
                                        </svg>
                                    </button>

                                </div>

                                        {/* --- BOTÓN NUEVO: GUARDAR EN COLECCIÓN --- */}
                                    <button 
                                        onClick={() => setIsCollectionModalOpen(true)}
                                        // Deshabilitamos si no hay usuario logueado (opcional, o redirigir a login)
                                        disabled={!loggedInUserProfile} 
                                        className="ACTION_BTN text-gray-400 hover:text-white group"
                                        title="Guardar en colección"
                                    >
                                        {/* Icono de Bookmark (Relleno si la historia está guardada sería ideal, pero requiere calcularlo. Por ahora Outline) */}
                                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5 group-hover:fill-white/10 transition-colors">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M17.593 3.322c1.1.128 1.907 1.077 1.907 2.185V21L12 17.25 4.5 21V5.507c0-1.108.806-2.057 1.907-2.185a48.507 48.507 0 0111.186 0z" />
                                        </svg>
                                        {/* Opcional: Texto "Save" o sin texto para mantenerlo limpio */}
                                        {/* <span className="font-medium text-sm ml-1.5 hidden sm:inline">Guardar</span> */}
                                    </button>

                                {/* 4. Divisor Vertical */}
                                <div className="h-6 w-px bg-gray-700 mx-6 DIVISOR_BTN"></div>

                                {/* 5. Contador Vistas */}
                                <div className=" flex items-center gap-3 text-gray-500 cursor-default mr-7 VISITAS_BTN" title="Vistas totales">
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                    </svg>
                                    <span className="font-medium text-sm VIEWS_BTN_TEXT">{(chapter.viewCount || 0).toLocaleString()} views</span>
                                </div>

                            </div>
                        </div>
                        {/* -- FIN BLOQUE -- */}

                        {shouldShowDonationBlock && (
                            <DonationBlock
                                authorProfile={authorProfile}
                                chapter={chapter}             
                                loggedInUserProfile={loggedInUserProfile}
                                storyId={storyId} // <-- ¡AÑADE ESTA LÍNEA!
                            />
                        )}

                    <div className="flex justify-between">
                        {prevChapterId ? (
                            <Link to={`/story/${storyId}/chapter/${prevChapterId}`} className="px-6 py-3 rounded-md hover:bg-gray-200">← Capítulo Anterior</Link>
                        ) : (
                            <span></span> // Placeholder for alignment
                        )}
                        {nextChapterId && (
                            <Link to={`/story/${storyId}/chapter/${nextChapterId}`} className="px-6 py-3 bg-indigo-600 text-white rounded-md hover:bg-indigo-700">Siguiente Capítulo →</Link>
                        )}
                    </div>
                
                <ChapterComments chapterId={chapterId} chapterAuthorId={chapter.authorId} loggedInUserProfile={loggedInUserProfile} />
                </>
            ) : (
                  // Muestra un loader específico si solo falta el autor
                  <div>Cargando datos del autor...</div> 
                )}

                {/* ++ RENDERIZADO CONDICIONAL DE LOS NUEVOS MODALES ++ */}
            {isQuoteSelectorOpen && (
                <QuoteSelectorModal
                    isOpen={isQuoteSelectorOpen}
                    onClose={() => {
                        setIsQuoteSelectorOpen(false);
                        setSelectedText(null); // Limpia la selección SÓLO al cerrar
                    }}
                    chapter={chapter}
                    story={story}
                    authorProfile={authorProfile}
                    onQuoteSelected={onQuoteSelected}
                    // Le pasamos el texto por si seleccionó con la burbuja
                    preselectedText={selectedText ? selectedText.text : ''} 
                />
            )}

            {/* ++ RENDERIZADO DEL MODAL DE PREVIEW REAL ++ */}
            {isQuotePreviewOpen && (
                <QuotePreviewModal
                    isOpen={isQuotePreviewOpen}
                    onClose={() => setIsQuotePreviewOpen(false)}
                    story={story}
                    chapter={chapter}
                    authorProfile={authorProfile}
                    finalQuote={finalQuote} // Pasa la cita seleccionada
                />
            )}

            {isCollectionModalOpen && (
                <AddToCollectionModal 
                    isOpen={isCollectionModalOpen} 
                    onClose={() => setIsCollectionModalOpen(false)}
                    story={story}
                    userProfile={loggedInUserProfile}
                />
            )}
            {/* -- FIN DE RENDERIZADO -- */}
        </main>
        </div> 
    ); 
};

const autoResizeTextarea = (element) => {
    if (element) {
        element.style.height = 'auto'; // Resetea la altura
        element.style.height = `${element.scrollHeight}px`; // Ajusta a la altura del contenido
    }
};

    const initialChapterState = {
        title: '',
        summary: '',
        initialNotes: '',
        content: '', // Este reemplaza a [chapterContent]
        finalNotes: '',
        allowDonations: false, // Este reemplaza a [allowDonations]
        // Guardamos los datos antiguos para no perderlos al editar
        tags: [],
        isSensitive: false,
        isMature: false
    };

const WritingPage = ({ userProfile }) => { 
    const authUser = auth.currentUser;
    const { chapterId } = useParams();
    const isEditMode = Boolean(chapterId);
    
    const [searchParams] = useSearchParams(); 
    const storyIdFromQuery = searchParams.get('storyId');

    const [chapterData, setChapterData] = useState(initialChapterState);
    const [initialDbData, setInitialDbData] = useState(null);

    const [isCreateStoryModalOpen, setIsCreateStoryModalOpen] = useState(!isEditMode && !storyIdFromQuery);
    const [isPublishModalOpen, setIsPublishModalOpen] = useState(false); 
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
    const [selectedStory, setSelectedStory] = useState(null); 
    const [isLoading, setIsLoading] = useState(isEditMode);
    const [showRestoredNotification, setShowRestoredNotification] = useState(false);
    const navigate = useNavigate(); 
    const editorRef = useRef(null);
    const saveTimeoutRef = useRef(null);

    const newDraftKey = 'typefiction-autosave-new';
    const editDraftKey = chapterId ? `typefiction-autosave-${chapterId}` : null;

    // const [allowDonations, setAllowDonations] = useState(false);
    const [isDonationConfigModalOpen, setIsDonationConfigModalOpen] = useState(false);

    // --- MANEJADORES DE CAMBIOS ---
    // Un manejador genérico para los <input> y <textarea>
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setChapterData(prevData => ({
            ...prevData,
            [name]: value
        }));
    };

    // Manejador para el CKEditor
    const handleEditorChange = (event, editor) => {
        const data = editor.getData();
        setChapterData(prevData => ({
            ...prevData,
            content: data
        }));
    };

    // Manejador para el toggle de donaciones
    const handleDonationToggle = (isChecked) => {
        setChapterData(prevData => ({
            ...prevData,
            allowDonations: isChecked
        }));
    };


const mapDbDataToState = useCallback((dbData) => { // <-- Añade useCallback
        if (!dbData) return initialChapterState;
        return {
            title: dbData.chapterTitle || '', 
            summary: dbData.summary || '',
            initialNotes: dbData.initialNotes || '',
            content: dbData.content || '',
            finalNotes: dbData.finalNotes || '',
            allowDonations: dbData.allowDonations || false,
            tags: dbData.tags || [],
            isSensitive: dbData.isSensitive || false,
            isMature: dbData.isMature || false
        };
    }, []); // <-- Añade un array de dependencias vacío

    useEffect(() => {
        const loadStoryFromQuery = async () => {
            if (storyIdFromQuery && !isEditMode && userProfile) {
                setIsLoading(true);
                try {
                    const storyRef = doc(db, 'stories', storyIdFromQuery);
                    const storySnap = await getDoc(storyRef);
                    if (storySnap.exists()) {
                        const storyData = storySnap.data();
                        if (storyData.authorId === userProfile.uid) {
                            setSelectedStory({ id: storySnap.id, ...storyData });
                            
                        } else {
                            alert("No tienes permiso para añadir capítulos a esta historia.");
                            navigate('/');
                        }
                    } else {
                        alert("La historia que intentas continuar no fue encontrada.");
                        navigate('/');
                    }
                } catch (error) {
                    console.error("Error al cargar la historia desde el query:", error);
                    alert("Ocurrió un error al cargar la historia.");
                    navigate('/');
                } finally {
                    setIsLoading(false);
                }
            }
        };
        loadStoryFromQuery();
    }, [storyIdFromQuery, isEditMode, userProfile, navigate]);




    useEffect(() => {
        let draftKey = isEditMode ? editDraftKey : newDraftKey;
        if (draftKey) {
            try {
                const savedDraftRaw = localStorage.getItem(draftKey);
                if (savedDraftRaw) {
                    const savedDraft = JSON.parse(savedDraftRaw);
                    if (isEditMode && savedDraft) {
                        // En modo edición, el borrador es SOLO el objeto chapterData
                        setChapterData(savedDraft);
                    } else if (!isEditMode && savedDraft.story && savedDraft.chapterData) {
                        // En modo nuevo, el borrador contiene la historia Y el chapterData
                        setSelectedStory(savedDraft.story);
                        setChapterData(savedDraft.chapterData);
                        setIsCreateStoryModalOpen(false);
                    }
                    setShowRestoredNotification(true);
                    setTimeout(() => setShowRestoredNotification(false), 3000);
                }
            } catch (error) {
                console.error("Error restoring draft:", error);
                localStorage.removeItem(draftKey);
            }
        }
    }, [isEditMode, chapterId, editDraftKey]); // Dependencias simplificadas

    

    useEffect(() => {
        if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);

        saveTimeoutRef.current = setTimeout(() => {
            if (isEditMode && editDraftKey && initialDbData) {
                // Compara el contenido actual con el de la DB, no con el estado inicial vacío
                if (chapterData.content !== initialDbData.content || chapterData.title !== initialDbData.chapterTitle) {
                    localStorage.setItem(editDraftKey, JSON.stringify(chapterData));
                }
            } else if (!isEditMode && selectedStory) {
                const draftToSave = { story: selectedStory, chapterData: chapterData };
                localStorage.setItem(newDraftKey, JSON.stringify(draftToSave));
            }
        }, 1500);

        return () => {
            if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
        };
    }, [chapterData, selectedStory, isEditMode, editDraftKey, initialDbData]); // Ahora depende de 'chapterData'

    const handleSaveSuccess = () => {
        const keyToRemove = isEditMode ? editDraftKey : newDraftKey;
        if (keyToRemove) localStorage.removeItem(keyToRemove);
    };

    // 4. --- handleDiscardDraft (Modificado) ---
    // Ahora resetea el objeto 'chapterData'
    const handleDiscardDraft = () => {
        const keyToRemove = isEditMode ? editDraftKey : newDraftKey;
        if (keyToRemove) localStorage.removeItem(keyToRemove);
        setIsConfirmModalOpen(false);

        if (isEditMode) {
            // Vuelve a los datos originales de la DB
            setChapterData(mapDbDataToState(initialDbData));
        } else {
            // Resetea todo
            setSelectedStory(null);
            setChapterData(initialChapterState);
            setIsCreateStoryModalOpen(true);
            navigate('/write');
        }
    };

    // 5. --- useEffect para CARGAR CAPÍTULO (Modificado) ---
    // Ahora popula el nuevo estado 'chapterData'
    useEffect(() => {
        const loadChapterForEdit = async () => {
            if (isEditMode && userProfile) {
                const chapterRef = doc(db, 'chapters', chapterId);
                const chapterSnap = await getDoc(chapterRef);
                if (chapterSnap.exists()) {
                    const dbData = { id: chapterSnap.id, ...chapterSnap.data() };
                    if (dbData.authorId !== userProfile.uid) {
                        alert("No tienes permiso para editar este capítulo.");
                        navigate('/');
                        return;
                    }

                    const storyRef = doc(db, 'stories', dbData.storyId);
                    const storySnap = await getDoc(storyRef);
                    
                    if (storySnap.exists()) {
                        // 1. Guarda los datos originales de la DB para "descartar"
                        setInitialDbData(dbData);
                        
                        // 2. Mapea los datos de la DB al estado
                        let stateData = mapDbDataToState(dbData);
                        
                        // 3. Revisa si hay un borrador local MÁS RECIENTE
                        const localDraftRaw = localStorage.getItem(`typefiction-autosave-${chapterId}`);
                        if (localDraftRaw) {
                            try {
                                const localDraft = JSON.parse(localDraftRaw);
                                // Si hay borrador, usa ese en lugar de los datos de la DB
                                stateData = localDraft;
                                setShowRestoredNotification(true);
                                setTimeout(() => setShowRestoredNotification(false), 3000);
                            } catch { 
                                // si el borrador está corrupto, usa los datos de la DB
                            }
                        }
                        
                        // 4. Establece el estado final
                        setChapterData(stateData);
                        setSelectedStory({ id: storySnap.id, ...storySnap.data() });
                        
                    } else {
                        alert("La historia asociada a este capítulo no fue encontrada.");
                        navigate('/');
                    }
                } else {
                    alert("Este capítulo no existe.");
                    navigate('/');
                }
                setIsLoading(false);
            }
        };
        loadChapterForEdit();
    }, [chapterId, isEditMode, navigate, userProfile, mapDbDataToState]); // Dependencias sin cambios

    useEffect(() => {
        if (!isCreateStoryModalOpen && editorRef.current) {
            editorRef.current.editing.view.focus();
        }
    }, [isCreateStoryModalOpen]);

const handleSaveChapter = async (targetStatus = 'published', scheduledDate = null, releaseType = 'now') => {
    if (!chapterData.title.trim()) {
        alert("El título del capítulo es obligatorio.");
        return; 
    }
    // Nota: Permitimos guardar borrador aunque no haya contenido, 
    // pero para publicar sí exigimos contenido.
    if (targetStatus === 'published' && !chapterData.content.trim()) {
        alert("El contenido del capítulo es obligatorio para publicar.");
        return; 
    }

    // Cierra modal si venía de 'Publicar'
    if (targetStatus === 'published') setIsPublishModalOpen(false);
    setIsLoading(true);

    const dataToSave = {
        storyId: selectedStory.id,
        storyTitle: selectedStory.title,
        authorId: userProfile.uid,
        coverImageURL: selectedStory.coverImageURL || '',
        
        // Campos del formulario
        chapterTitle: chapterData.title,
        summary: chapterData.summary,
        initialNotes: chapterData.initialNotes,
        content: chapterData.content,
        finalNotes: chapterData.finalNotes,
        allowDonations: chapterData.allowDonations,
        
        // Preservar antiguos
        tags: chapterData.tags, 
        isSensitive: chapterData.isSensitive,
        isMature: chapterData.isMature,

        status: targetStatus, 
        wordCount: chapterData.content.replace(/<[^>]+>/g, '').split(/\s+/).filter(Boolean).length,

        scheduledAt: scheduledDate,
        releaseType: releaseType,

        notificationsSent: releaseType === 'hype' ? false : true
    };

    try {
        if (isEditMode) {
            const chapterRef = doc(db, 'chapters', chapterId);
            await updateDoc(chapterRef, { ...dataToSave, lastUpdatedAt: serverTimestamp() });
            if (targetStatus === 'published') {
                const storyRef = doc(db, 'stories', selectedStory.id);
                await updateDoc(storyRef, { lastUpdatedAt: serverTimestamp() });
            }
            handleSaveSuccess();
            
            // --- NUEVA LÓGICA DE REDIRECCIÓN ---
            if (targetStatus === 'published') {
                if (scheduledDate && new Date(scheduledDate) > new Date()) {
                    // SI ES PROGRAMADO (Hype o Silent) -> Ir a la Historia con Aviso
                    navigate(`/story/${selectedStory.id}`, {
                        state: { 
                            notification: {
                                message: releaseType === 'hype' 
                                    ? "¡Capítulo en PRE-ESTRENO (Hype) activado!" 
                                    : "¡Capítulo programado SILENCIOSAMENTE con éxito!",
                                type: "success"
                            }
                        }
                    });
                } else {
                    // SI ES PUBLICACIÓN INMEDIATA -> Ir a leerlo
                    navigate(`/story/${selectedStory.id}/chapter/${chapterId}`);
                }
            } else {
                navigate('/dashboard', { 
                    state: { notification: { message: "¡Borrador guardado en la nube exitosamente!", type: "success" } } 
                });
            }

        } else {
            // --- MODO CREACIÓN (Nuevo documento) ---
            const chaptersQuery = query(collection(db, 'chapters'), where("storyId", "==", selectedStory.id));
            const chaptersSnapshot = await getDocs(chaptersQuery);
            const newOrder = chaptersSnapshot.size + 1;

            const newChapter = await addDoc(collection(db, 'chapters'), { 
                ...dataToSave, 
                createdAt: serverTimestamp(),
                lastUpdatedAt: serverTimestamp(),
                order: newOrder,
                viewCount: 0,
                heartCount: 0
            });
            
            if (targetStatus === 'published') {
                const storyRef = doc(db, 'stories', selectedStory.id);
                await updateDoc(storyRef, { lastUpdatedAt: serverTimestamp() });
            }
            
            handleSaveSuccess(); // Limpia el autosave local
            
            // --- NUEVA LÓGICA DE REDIRECCIÓN (CREACIÓN) ---
            if (targetStatus === 'published') {
                if (scheduledDate && new Date(scheduledDate) > new Date()) {
                    // PROGRAMADO -> Historia
                    navigate(`/story/${selectedStory.id}`, {
                        state: { 
                            notification: {
                                message: releaseType === 'hype' 
                                    ? "¡Capítulo en PRE-ESTRENO (Hype) creado!" 
                                    : "¡Capítulo programado SILENCIOSAMENTE creado!",
                                type: "success"
                            }
                        }
                    });
                } else {
                    // INMEDIATO -> Leer
                    navigate(`/story/${selectedStory.id}/chapter/${newChapter.id}`); 
                }
            } else {
                navigate(`/write/edit/${newChapter.id}`);
            }
        }
    } catch (err) {
        console.error("Error al guardar:", err);
        alert('No se pudo guardar. Inténtalo de nuevo.');
        setIsLoading(false);
        if (targetStatus === 'published') setIsPublishModalOpen(true);
    }
};

    const handleStorySelect = (story) => { setSelectedStory(story); setIsCreateStoryModalOpen(false); };
    // Modificado para no hacer validación aquí, se hace en handlePublish
    const handleOpenPublishModal = () => { 
        if (!selectedStory) { 
            alert("Error: no hay historia seleccionada."); 
            return; 
        }
        setIsPublishModalOpen(true); 
    };

    if (authUser && !authUser.emailVerified) {
        return (
            <div className="flex flex-col items-center justify-center min-h-screen bg-gray-900 text-white p-4 text-center">
                <div className="bg-gray-800 p-8 rounded-xl shadow-2xl max-w-md border border-gray-700">
                    <div className="w-16 h-16 bg-yellow-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                    </div>
                    <h1 className="text-2xl font-bold mb-2">Verifica tu correo</h1>
                    <p className="text-gray-400 mb-6">
                        Para empezar a escribir y publicar historias en Typefiction, necesitamos confirmar que tu correo 
                        <span className="text-white font-semibold"> {authUser.email} </span> es real.
                    </p>
                    <button 
                        onClick={async () => {
                            try {
                                await sendEmailVerification(authUser);
                                alert("Correo de verificación reenviado. Revisa tu bandeja de entrada (y spam).");
                            } catch (e) {
                                alert("Error al enviar. Intenta más tarde.");
                            }
                        }}
                        className="w-full py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-md transition-colors"
                    >
                        Reenviar correo de verificación
                    </button>
                    <button onClick={() => navigate('/')} className="mt-4 text-gray-500 hover:text-gray-300 text-sm underline">
                        Volver al inicio
                    </button>
                </div>
            </div>
        );
    }

    if (!userProfile || (isLoading && !selectedStory)) { // Modificado para permitir renderizar si solo está cargando
        return <div className="flex items-center justify-center min-h-screen bg-gray-900 text-white">Cargando editor...</div>;
    }

    return (
        <div className="min-h-screen flex flex-col justify-center">
            <style>{`
                /* Estilos de CKEditor (sin cambios) */
                .ck-editor-container .ck-editor__main > .ck-editor__editable {
                    color: #d1d5db;
                    min-height: 70vh;
                    text-align: justify;
                }
                .ck.ck-balloon-panel { z-index: 51 !important; }
            `}</style>
            
            {showRestoredNotification && (
                <div className="fixed top-20 right-4 z-50 bg-green-600 text-white px-4 py-2 rounded-md shadow-lg">
                    Borrador restaurado.
                </div>
            )}
            <header className="sticky top-0 z-40 bg-gray-800 border-b border-gray-700 shadow-md">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-16">
                        <div className="flex-grow min-w-0 flex items-center ">
                            {selectedStory && (
                                <button
                                    onClick={() => setIsConfirmModalOpen(true)}
                                    title={isEditMode ? "Descartar cambios" : "Cambiar de historia"}
                                    className="mr-7 p-2 mt-2 rounded-full text-gray-400 hover:text-white hover:scale transition-colors"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
                                        <path fillRule="evenodd" d="M7.72 1.22a.75.75 0 010 1.06L3.84 6.25h10.91a.75.75 0 010 1.5H3.84l3.88 3.97a.75.75 0 11-1.06 1.06L1.22 7.28a.75.75 0 010-1.06l5.44-5.44a.75.75 0 011.06 0z" clipRule="evenodd" />
                                    </svg>
                                </button>
                            )}
                            <h2 className="font-semibold text-white truncate">
                                {isEditMode ? 'Editando capítulo en: ' : (selectedStory ? `Escribiendo en: ` : 'Selecciona una historia para empezar')}
                                <span className="text-indigo-400 font-bold">{selectedStory ? ` ${selectedStory.title}` : ''}</span>
                            </h2>
                        </div>
                        <div className="flex items-center space-x-4">
                            <button onClick={() => navigate('/')} className="text-sm text-gray-300 hover:text-white">Volver al Dashboard</button>

                            {userProfile.isPro && (
                                <button 
                                    onClick={() => handleSaveChapter('draft')} 
                                    disabled={!selectedStory || isLoading} 
                                    className="px-4 py-2 font-bold text-gray-300 border border-gray-600 bg-gray-800 rounded-md hover:bg-gray-700 hover:text-white disabled:opacity-50 transition-colors flex items-center gap-2"
                                >
                                    {isLoading ? '...' : (
                                        <>
                                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 16.5V9.75m0 0l3 3m-3-3l-3 3M6.75 19.5a4.5 4.5 0 01-1.41-8.775 5.25 5.25 0 0110.233-2.33 3 3 0 013.758 3.848A3.752 3.752 0 0118 19.5H6.75z" />
                                            </svg>
                                            <span>Save as draft</span>
                                        </>
                                    )}
                                </button>
                            )}

                            <button onClick={handleOpenPublishModal} disabled={!selectedStory || isLoading} className="px-4 py-2 font-bold text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed">
                                {isLoading ? 'Cargando...' : (isEditMode ? 'Guardar Cambios...' : 'Publicar...')}
                            </button>
                        </div>
                    </div>
                </div>
            </header>
            <main className="flex-grow p-4 sm:p-8">
                {/* --- NUEVO LAYOUT DE ESCRITURA --- */}
                <div className="max-w-4xl mx-auto writing_page_Editor">
                    {/* 1. TÍTULO */}
                    <input
                        type="text"
                        name="title"
                        placeholder="Título del Capítulo"
                        value={chapterData.title}
                        onChange={handleInputChange}
                        className="writing-page-input"
                        disabled={!selectedStory || isLoading}
                    />

                    {/* 2. SUMMARY */}
                    <div className={`textarea-container with-label summary-label ${chapterData.summary ? 'has-content' : ''}`}>
                        <textarea
                            name="summary"
                            placeholder="<Summary> (dejar en blanco para omitir)" // El placeholder original ya no se verá
                            value={chapterData.summary}
                            onChange={(e) => { handleInputChange(e); autoResizeTextarea(e.target); }}
                            onInput={(e) => autoResizeTextarea(e.target)}
                            ref={(el) => autoResizeTextarea(el)}
                            className="writing-page-textarea" // Mantenemos la clase original
                            disabled={!selectedStory || isLoading}
                            rows="1"
                        />
                    </div>

                    {/* 3. NOTAS INICIALES */}
                    <div className={`textarea-container with-label initial-notes-label ${chapterData.initialNotes ? 'has-content' : ''}`}>
                        <textarea
                            name="initialNotes"
                            placeholder="<Initial notes> (dejar en blanco para omitir)" // Ya no se verá
                            value={chapterData.initialNotes}
                            onChange={(e) => { handleInputChange(e); autoResizeTextarea(e.target); }}
                            onInput={(e) => autoResizeTextarea(e.target)}
                            ref={(el) => autoResizeTextarea(el)}
                            className="writing-page-textarea"
                            disabled={!selectedStory || isLoading}
                            rows="1"
                        />
                    </div>

                    <div className="mt-4 border-t border-gray-700 pt-4 opacity-60 mr-80 pointer-events-none">
                        {/* <p className="text-sm text-left text-gray-400 mb-4 italic">(Vista previa de cómo verán el bloque tus lectores)</p> */}
                            
                    </div>

                    {/* 4. CONTENIDO (CKEditor) */}
                    <div className="ck-editor-container">
                        <CKEditor
                            editor={BalloonEditor}
                            data={chapterData.content}
                            onReady={editor => { editorRef.current = editor; }}
                            onChange={handleEditorChange} // Usamos el manejador nuevo
                            config={{
                                placeholder: 'Aquí comienza tu capítulo...',
                                toolbar: ['heading', '|', 'bold', 'italic', 'link', 'bulletedList', 'numberedList', 'blockQuote', 'undo', 'redo']
                            }}
                            disabled={!selectedStory || isLoading}
                        />
                    </div>

                    <div className="mt-6 border-t border-gray-700 pt-4 opacity-60 pointer-events-none">

                            
                    </div>
                    
                    {/* 5. NOTAS FINALES */}
                    <div className={`textarea-container with-label final-notes-label ${chapterData.finalNotes ? 'has-content' : ''}`} style={{marginTop: '1rem'}}>
                        <textarea
                            name="finalNotes"
                            placeholder="<Final notes> (dejar en blanco para omitir)" // Ya no se verá
                            value={chapterData.finalNotes}
                            onChange={(e) => { handleInputChange(e); autoResizeTextarea(e.target); }}
                            onInput={(e) => autoResizeTextarea(e.target)}
                            ref={(el) => autoResizeTextarea(el)}
                            className="writing-page-textarea"
                            // style={{marginTop: '1rem'}} // Quitamos el margen de aquí, lo pusimos en el div
                            disabled={!selectedStory || isLoading}
                            rows="1"
                        />
                    </div>
                </div>
                {/* --- FIN DEL NUEVO LAYOUT --- */}


                {selectedStory && ( 
                    <div className="max-w-4xl mx-auto mt-8 p-6 bg-gray-800 rounded-lg shadow-md BLOQUE_PREVIEW_DONO_BLOCK">
                        {userProfile?.donations?.provider ? ( // Simplificado
                            <>
                                <div className="flex items-center justify-between mb-4">
                                    <div>
                                        <h3 className="font-semibold text-white text-lg">Donaciones para este capítulo</h3>
                                        <p className="text-sm text-gray-400">Permite que los lectores te apoyen al final de este capítulo.</p>
                                    </div>
                                    <div className="flex items-center space-x-4">
                                        {chapterData.allowDonations && (
                                            <button onClick={() => setIsDonationConfigModalOpen(true)} className="text-sm text-indigo-400 hover:underline">
                                                Editar Widget
                                            </button>
                                        )}
                                        <button
                                            onClick={() => handleDonationToggle(!chapterData.allowDonations)} // Usamos el manejador nuevo
                                            className={`px-4 py-2 rounded-md font-bold text-sm transition-colors ${
                                                chapterData.allowDonations
                                                ? 'bg-red-600 hover:bg-red-700 text-white'
                                                : 'bg-green-600 hover:bg-green-700 text-white'
                                            }`}
                                        >
                                            {chapterData.allowDonations ? 'Desactivar Donaciones' : 'Activar Donaciones'}
                                        </button>
                                    </div>
                                </div>
                                {chapterData.allowDonations && (
                                    <div className="mt-6 border-t border-gray-700 pt-6 opacity-75 pointer-events-none">
                                        <p className="text-sm text-center text-gray-400 mb-4 italic">(Vista previa de cómo verán el bloque tus lectores)</p>
                                        <DonationBlock
                                            authorProfile={userProfile} 
                                            chapter={{ id: chapterId || 'new_chapter_preview' }} 
                                            loggedInUserProfile={null}
                                        />
                                    </div>
                                )}
                            </>
                        ) : (
                            <div className="flex items-center justify-between">
                                <div>
                                    <h3 className="font-semibold text-white">¿Quieres recibir donaciones por tu trabajo?</h3>
                                    <p className="text-sm text-gray-400">Vincula una cuenta de PayPal o Stripe para permitir que tus lectores te apoyen.</p>
                                </div>
                                <Link to="/settings" className="px-4 py-2 rounded-md font-bold text-sm bg-indigo-600 hover:bg-indigo-700 text-white whitespace-nowrap">
                                    Vincular cuenta
                                </Link>
                            </div>
                        )}
                    </div>
                )}
            </main>

            
            {isCreateStoryModalOpen && (
                <CreateStoryModal 
                    userProfile={userProfile} 
                    onStorySelected={handleStorySelect} 
                    onClose={() => navigate('/')} // Esto hace que regrese al Dashboard
                />
            )}

            {/* 8. --- MODAL DE PUBLICACIÓN (Modificado) --- */}
            {/* Le pasamos los nuevos props de estado sincronizado */}
            {isPublishModalOpen &&
                <PublishModal
                    story={selectedStory}
                    onClose={() => setIsPublishModalOpen(false)}
                    
                    // --- La magia de la sincronización ---
                    chapterTitle={chapterData.title}
                    onTitleChange={(newTitle) => setChapterData(prev => ({ ...prev, title: newTitle }))}
                    
                    acceptDonations={chapterData.allowDonations}
                    onDonationsChange={(isChecked) => handleDonationToggle(isChecked)} // Usamos el manejador
                    
                    // Ahora recibimos (date, type) del modal y se lo pasamos a nuestra función
                    onPublish={(date, type) => handleSaveChapter('published', date, type)}
                    
                    // Prop para deshabilitar el botón mientras se publica (opcional pero recomendado)
                    isPublishing={isLoading}
                    userProfile={userProfile}
                />}

            {isDonationConfigModalOpen && (
                <DonationWidgetConfigModal
                    isOpen={isDonationConfigModalOpen}
                    onClose={() => setIsDonationConfigModalOpen(false)}
                    userProfile={userProfile}
                />
            )}

            <ConfirmDiscardModal
                isOpen={isConfirmModalOpen}
                onConfirm={handleDiscardDraft}
                onCancel={() => setIsConfirmModalOpen(false)}
                isEditMode={isEditMode}
            />
        </div>
    );
};

const SearchPage = () => {
    const [searchParams] = useSearchParams();
    const queryTerm = searchParams.get('q') || '';
    const searchType = searchParams.get('type') || 'stories';
    
    const tagParam = searchParams.get('tag');
    const charParam = searchParams.get('character');
    
    const [results, setResults] = useState([]);
    const [profilesMap, setProfilesMap] = useState({});
    const [loading, setLoading] = useState(false);
    
    const [activeTab, setActiveTab] = useState(searchType);
    const [sortBy, setSortBy] = useState('heartCount:desc');
    const [facets, setFacets] = useState(null);
    const [activeFilters, setActiveFilters] = useState({
            mainCharacters: [],
            tags: []
        });

    const [filterInput, setFilterInput] = useState('');
    const [isInputFocused, setIsInputFocused] = useState(false);


    const buildFilterByString = useCallback(() => {
        let filters = [];
        
        // Filtro para Personajes
        if (activeFilters.mainCharacters.length > 0) {
            const escapedChars = activeFilters.mainCharacters.map(val => `\`${val}\``);
            filters.push(`mainCharacters:=[${escapedChars.join(',')}]`);
        }

        // Filtro para Tags
        if (activeFilters.tags.length > 0) {
            const escapedTags = activeFilters.tags.map(val => `\`${val}\``);
            filters.push(`tags:=[${escapedTags.join(',')}]`);
        }

        return filters.join(' && ');
    }, [activeFilters]);

    useEffect(() => {
        setActiveTab(searchType);
        
        // Configurar sorteo default
        if (searchType === 'users') setSortBy('followerCount:desc');
        else if (searchType === 'chapters') setSortBy('viewCount:desc');
        else setSortBy('heartCount:desc');

        // --- PRE-LLENADO DE FILTROS ---
        if (tagParam || charParam) {
            setActiveFilters(prev => ({
                mainCharacters: charParam ? [charParam] : [],
                tags: tagParam ? [tagParam] : []
            }));
        } else {
            // Si no hay params en URL, reseteamos (opcional, depende UX deseada)
            // setActiveFilters({ mainCharacters: [], tags: [] }); 
        }

    }, [searchType, tagParam, charParam]);

    useEffect(() => {
        const performSearch = async () => {
            // 1. Verificamos si hay filtros activos (Tags o Personajes)
            const hasActiveFilters = activeFilters.tags.length > 0 || activeFilters.mainCharacters.length > 0;

            // 2. Condición corregida: 
            // Solo detenemos si NO hay texto Y NO hay filtros.
            if (!queryTerm && !hasActiveFilters) {
                setResults([]);
                setProfilesMap({});
                return;
            }
            
            setLoading(true);
            setResults([]);
            setProfilesMap({});

            let collectionToSearch = '';
            let queryByFields = '';
            let currentSortBy = sortBy; 

            if (activeTab === 'stories') {
                collectionToSearch = 'stories';
                queryByFields = 'title,tags,searchKeywords,description,authorUsername';
                
            } else if (activeTab === 'users') {
                collectionToSearch = 'users';
                queryByFields = 'displayName,username,searchKeywords';
                if (!sortBy.includes('followerCount')) {
                    currentSortBy = 'followerCount:desc';
                }
            } else if (activeTab === 'chapters') {
                collectionToSearch = 'chapters';
                queryByFields = 'chapterTitle,content,storyTitle,authorUsername,authorId';
                if (!sortBy.includes('viewCount') && !sortBy.includes('lastUpdatedAt')) {
                    currentSortBy = 'viewCount:desc'; // O 'lastUpdatedAt:desc' si prefieres
                }
            }

            const commonSearchParams = {
                q: queryTerm || '*', 
                per_page: 20,
                sort_by: currentSortBy,
                query_by: queryByFields,

                // 1. Le dice a Typesense que filtre por los filtros activos
                filter_by: buildFilterByString(), 

                // 2. Le pide a Typesense que nos devuelva los conteos de facetas
                //    (solo si estamos en la pestaña 'stories')
                facet_by: activeTab === 'stories' ? 'mainCharacters,tags' : '' 
            };

            try {
                const response = await typesenseClient.collections(collectionToSearch)
                                    .documents()
                                    .search(commonSearchParams);

                // Guarda los resultados de las facetas si existen
                if (response && response.facet_counts) {
                    setFacets(response.facet_counts);
                } else {
                    // Si no es una búsqueda de historias, limpia las facetas
                    setFacets(null); 
                }

                let finalResults = [];
                let tempProfilesMap = {};
                let authorIdsToFetch = new Set();

                if (response && response.hits) {
                    response.hits.forEach(hit => {
                        let item = hit.document;
                        item.resultType = activeTab; 
                        
                        if (activeTab === 'stories' && item.authorId) {
                            authorIdsToFetch.add(item.authorId); 
                        } else if (activeTab === 'users' && item.id) {
                            tempProfilesMap[item.id] = item; 
                        } else if (activeTab === 'chapters' && item.authorId) {
                            authorIdsToFetch.add(item.authorId); 
                        }
                        
                        finalResults.push(item);
                    });
                }
                
                setResults(finalResults);
                const missingIds = [...authorIdsToFetch].filter(id => id && !tempProfilesMap[id]);

                if (missingIds.length > 0) {
                    const chunks = [];
                    for (let i = 0; i < missingIds.length; i += 30) {
                        chunks.push(missingIds.slice(i, i + 30));
                    }
                    
                    const promises = chunks.map(chunk =>
                        getDocs(query(collection(db, 'users'), where(documentId(), 'in', chunk)))
                    );
                    
                    const snapshots = await Promise.all(promises);
                    
                    snapshots.forEach(snapshot => {
                        snapshot.forEach(doc => {
                            tempProfilesMap[doc.id] = doc.data(); 
                        });
                    });
                }
                
                setProfilesMap(tempProfilesMap);

            } catch (error) {
                console.error("Error en la búsqueda con TypeSense o Firestore:", error);
            } finally {
                setLoading(false);
            }
        };

        performSearch();
    }, [queryTerm, sortBy, activeTab, activeFilters, buildFilterByString]);


const handleFilterToggle = (type, value) => {
    setActiveFilters(prev => {
        const current = prev[type] || [];
        const isSelected = current.includes(value);
        return {
            ...prev,
            [type]: isSelected 
                ? current.filter(item => item !== value) // Quitarlo
                : [...current, value] // Añadirlo
        };
    });
    
    // --- ¡AÑADIDO! ---
    setFilterInput(''); // Limpia el input
    setIsInputFocused(false); // Cierra el dropdown
};

const renderFilters = () => {
    // Solo muestra filtros en la pestaña 'stories' y si tenemos facetas
    if (activeTab !== 'stories' || !facets) return null;

    const mainCharFacets = facets.find(f => f.field_name === 'mainCharacters');
    const tagFacets = facets.find(f => f.field_name === 'tags');

    // 1. Combina todos los filtros DISPONIBLES en una sola lista
    let allAvailableFilters = [];
    if (mainCharFacets) {
        allAvailableFilters.push(...mainCharFacets.counts.map(f => ({ ...f, type: 'mainCharacters', label: 'Personaje' })));
    }
    if (tagFacets) {
        allAvailableFilters.push(...tagFacets.counts.map(f => ({ ...f, type: 'tags', label: 'Tag' })));
    }

    // 2. Filtra la lista basada en lo que el usuario está escribiendo
    const filteredSuggestions = allAvailableFilters.filter(f => 
        // Coincide con el texto del input
        f.value.toLowerCase().includes(filterInput.toLowerCase()) &&
        // Y oculta sugerencias que ya han sido seleccionadas
        !activeFilters[f.type].includes(f.value)
    ).sort((a, b) => b.count - a.count); // Ordena por popularidad

    return (
        // --- CAMBIO 1: El div principal ahora solo alinea ---
        <div className="flex items-center w-full">
            {/* --- CAMBIO 2: Título con 'flex-shrink-0' y margen derecho --- */}
            <h3 className="font-bold text-white flex-shrink-0 mr-3">Filtrar por:</h3>
            
            {/* --- CAMBIO 3: Este div ahora es el contenedor principal que crece y tiene el estilo --- */}
            <div className="flex-grow flex flex-wrap items-center gap-2 p-2 bg-gray-700 border border-gray-600 focus-within:border-indigo-500 focus-within:ring-1 focus-within:ring-indigo-500 SearchPage_TagInput">
                
                {/* --- 1. Las "Pills" (Filtros Activos) --- */}
                {activeFilters.tags.map(tag => (
                    <span key={`tag-${tag}`} className="flex items-center gap-1.5 bg-indigo-600 text-white text-sm font-semibold px-2 py-0.5 rounded-full">
                        {tag}
                        <button 
                            onClick={() => handleFilterToggle('tags', tag)} 
                            className="text-indigo-200 hover:text-white font-bold"
                            title={`Quitar filtro: ${tag}`}
                        >
                            &times;
                        </button>
                    </span>
                ))}
                {activeFilters.mainCharacters.map(char => (
                    <span key={`char-${char}`} className="flex items-center gap-1.5 bg-blue-600 text-white text-sm font-semibold px-2 py-0.5 rounded-full">
                        {char}
                        <button 
                            onClick={() => handleFilterToggle('mainCharacters', char)} 
                            className="text-blue-200 hover:text-white font-bold"
                            title={`Quitar filtro: ${char}`}
                        >
                            &times;
                        </button>
                    </span>
                ))}
                
                {/* --- 2. El Input de Autocompletado --- */}
                <div className="relative flex-grow min-w-[150px]">
                    <input 
                        type="text"
                        value={filterInput}
                        onChange={(e) => setFilterInput(e.target.value)}
                        onFocus={() => setIsInputFocused(true)}
                        onBlur={() => setTimeout(() => setIsInputFocused(false), 150)} 
                        placeholder="Añadir filtro..."
                        className="w-full bg-transparent text-white placeholder-gray-400 focus:outline-none"
                    />
                    
                    {/* --- 3. El Dropdown de Sugerencias --- */}
                    {isInputFocused && filteredSuggestions.length > 0 && (
                        <div className="absolute top-full left-0 z-50 mt-2 w-full max-h-60 overflow-y-auto bg-gray-700 border border-gray-600 rounded-md shadow-lg filteredsuggestions SearchPage_Categorias">
                            {filteredSuggestions.map(facet => (
                                <div 
                                    key={`${facet.type}-${facet.value}`}
                                    onMouseDown={(e) => e.preventDefault()} 
                                    onClick={() => handleFilterToggle(facet.type, facet.value)}
                                    className="px-4 py-2 text-gray-200 hover:bg-indigo-600 hover:text-white cursor-pointer flex justify-between"
                                >
                                    <span>
                                        {facet.value}
                                        <span className={`text-xs ml-2 ${facet.type === 'tags' ? 'text-indigo-400' : 'text-blue-400'}`}>
                                            ({facet.label})
                                        </span>
                                    </span>
                                    <span className="text-gray-400">{facet.count}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

    const renderResults = () => {
        if (loading) {
            return <p className="text-center text-gray-400 py-16">Buscando...</p>;
        }

        let categoryTitle = 'Historias';
        if (activeTab === 'users') categoryTitle = 'Autores';
        if (activeTab === 'chapters') categoryTitle = 'Citas';

        if (results.length === 0) {
            return <p className="text-center text-gray-500 py-16">No se encontraron {categoryTitle.toLowerCase()} para "{queryTerm}".</p>;
        }

        return (
            <div>
                <h2 className="text-xl font-semibold mb-4 text-gray-300">
                    {results.length} {categoryTitle} encontradas
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6" style={{ perspective: '2000px' }}>
                    {results.map(item => { 
                        const profileId = item.resultType === 'user' ? item.id : item.authorId;
                        const authorProfile = profilesMap[profileId];
                        
                        // --- ¡PARCHE DE FALLBACK AÑADIDO! ---
                        // Si 'authorProfile' no ha cargado, usamos el 'item' (de Typesense) como fallback.
                        // Para 'story' y 'chapter', esto significa que usará el 'authorUsername' obsoleto
                        // en lugar de quedarse en "cargando..."
                        
                        switch (item.resultType) {
                            case 'stories':
                                return <StoryCard 
                                            key={`story-${item.id}`} 
                                            story={item}
                                            // Si el perfil no ha cargado, pasa el 'item' que tiene 'authorUsername'
                                            authorProfile={authorProfile} 
                                        />;
                            case 'users':
                                return <ProfileSearchCard 
                                            key={`user-${item.id}`} 
                                            // Si el perfil no ha cargado, usa el 'item' de typesense
                                            profile={authorProfile || item} 
                                        />;
                            case 'chapters':
                                return <ChapterSearchCard 
                                            key={`chapter-${item.id}`} 
                                            chapter={item} 
                                            // Si el perfil no ha cargado, pasa el 'item' que tiene 'authorUsername'
                                            authorProfile={authorProfile} 
                                            queryTerm={queryTerm} 
                                        />;
                            default:
                                return null;
                        }
                    })}
                </div>
            </div>
        );
    };

    const renderSortOptions = () => {
        const relevanceOption = <option value="heartCount:desc">Ordenar por Relevancia</option>;
        
        const dateOptions = (
            <>
                <option value="lastUpdatedAt:desc">Recientes (Descendente)</option>
                <option value="lastUpdatedAt:asc">Recientes (Ascendente)</option>
            </>
        );

        if (activeTab === 'stories') {
            return (
                <>
                    {relevanceOption}
                    {dateOptions}
                    <option value="heartCount:desc">Likes (Descendente)</option>
                    <option value="heartCount:asc">Likes (Ascendente)</option>
                </>
            );
        }

        if (activeTab === 'users') {
            return (
                <>
                    {/* {relevanceOption} <-- Eliminado */ }
                    <option value="followerCount:desc">Seguidores (Descendente)</option>
                    <option value="followerCount:asc">Seguidores (Ascendente)</option>
                </>
            );
        }

        if (activeTab === 'chapters') {
             return (
                <>
                    {relevanceOption}
                    {dateOptions}
                    <option value="viewCount:desc">Vistas (Descendente)</option>
                    <option value="viewCount:asc">Vistas (Ascendente)</option>
                </>
            );
        }
    };
    
    const handleTabClick = (tabName) => {
    setActiveTab(tabName);
    // --- ¡AQUÍ LA CORRECCIÓN! ---
    // Asigna el orden por defecto correcto para cada pestaña
    if (tabName === 'users') {
        setSortBy('followerCount:desc');
    } else if (tabName === 'chapters') {
        setSortBy('viewCount:desc'); // O 'lastUpdatedAt:desc' si lo prefieres
    } else {
        setSortBy('heartCount:desc'); // Para 'stories'
    }
    };

    return (
        <div className="max-w-6xl mx-auto p-4 sm:p-6 md:p-8">
            <h1 className="text-3xl font-bold mb-4">Resultados de búsqueda para: <span className="text-indigo-400">"{queryTerm}"</span></h1>

            <div className="flex space-x-2 border-b border-gray-700 mb-6">
                <button
                    onClick={() => handleTabClick('stories')}
                    className={`py-2 px-4 text-lg font-semibold transition-colors ${
                        activeTab === 'stories' ? 'searchSectionsActive' : 'searchSectionsUnactive'
                    }`}
                >
                    Historias
                </button>
                <button
                    onClick={() => handleTabClick('users')}
                    className={`py-2 px-4 text-lg font-semibold transition-colors ${
                        activeTab === 'users' ? 'searchSectionsActive' : 'searchSectionsUnactive'
                    }`}
                >
                    Autores
                </button>
                <button
                    onClick={() => handleTabClick('chapters')}
                    className={`py-2 px-4 text-lg font-semibold transition-colors ${
                        activeTab === 'chapters' ? 'searchSectionsActive' : 'searchSectionsUnactive'
                    }`}
                >
                    Citas
                </button>
            </div>

            {/* --- NUEVO BLOQUE DE FILTRO Y SORTEO --- */}
            <div className="flex flex-col md:flex-row justify-between md:items-start gap-4 mb-6 relative z-10">
                
                {/* Filtros a la izquierda (ocupa el espacio) */}
                <div className="flex-grow w-full md:w-auto">
                    {renderFilters()} 
                </div>

                {/* Sorteo a la derecha (no se encoge) */}
                <div className="flex-shrink-0 ">
                    <label htmlFor="sort-select" className="text-sm font-medium text-gray-300 mr-2">Ordenar por:</label>
                    <select 
                        id="sort-select"
                        value={sortBy} 
                        onChange={(e) => setSortBy(e.target.value)}
                        className="bg-gray-700 text-white rounded-md p-2 w-full md:w-auto"
                    >
                        {renderSortOptions()}
                    </select>
                </div>
            </div>

            {renderResults()}
        </div>
    );
};

const AccountSettingsPage = ({ userProfile }) => {
    const navigate = useNavigate();
    const [currentPassword, setCurrentPassword] = useState('');
    const [searchParams] = useSearchParams();
    const [newEmail, setNewEmail] = useState('');
    const [feedback, setFeedback] = useState({ message: '', type: '' });
    const [isDonationConfigModalOpen, setIsDonationConfigModalOpen] = useState(false);
    const [isConnecting, setIsConnecting] = useState(false);
    const [isDisconnecting, setIsDisconnecting] = useState(false);
    const [isLoadingPro, setIsLoadingPro] = useState(false);
     // eslint-disable-next-line
    const [_paypalEmail, setPaypalEmail] = useState('');
    // const [isSavingDonations, setIsSavingDonations] = useState(false);
    const [isConnectingPayPal, setIsConnectingPayPal] = useState(false);
    const [isVerifyingPayPal, setIsVerifyingPayPal] = useState(false);
    const [isDisconnectingPayPal, setIsDisconnectingPayPal] = useState(false);
    const [isConfirmDisconnectModalOpen, setIsConfirmDisconnectModalOpen] = useState(false);
    const [isGeneratingLink, setIsGeneratingLink] = useState(false);
    const [notifSettings, setNotifSettings] = useState({
    pushLikes: true,
    pushComments: true,
    pushFollows: true,
    pushSystemEvents: true
    });
    const [isSavingSettings, setIsSavingSettings] = useState(false);

    useEffect(() => {
    if (userProfile?.notificationSettings) {
        setNotifSettings(prev => ({ ...prev, ...userProfile.notificationSettings }));
    }
    }, [userProfile]);

    const handleToggleSetting = async (key) => {
    const newSettings = { ...notifSettings, [key]: !notifSettings[key] };
    setNotifSettings(newSettings); // Optimistic update
    setIsSavingSettings(true); // Opcional, para mostrar un loader pequeño si quieres

    try {
        const userRef = doc(db, 'users', userProfile.uid);
        await updateDoc(userRef, {
            notificationSettings: newSettings
        });
    } catch (e) {
        console.error("Error updating settings:", e);
        // Revertir si falla
        setNotifSettings(prev => ({ ...prev, [key]: !prev[key] })); 
    } finally {
        setIsSavingSettings(false);
    }
    };

    useEffect(() => {
        const handleFocus = () => setIsLoadingPro(false);
        window.addEventListener('focus', handleFocus);
        return () => window.removeEventListener('focus', handleFocus);
    }, []);
    
    // Helper para debug
    const showFeedback = useCallback((message, type) => {
        setFeedback({ message, type });
        // Considera si realmente necesitas quitar el mensaje automáticamente,
        // a veces es mejor dejarlo hasta la siguiente acción del usuario.
        // Si lo necesitas, asegúrate de que el timeout se limpie si se llama de nuevo.
        const timer = setTimeout(() => setFeedback({ message: '', type: '' }), 7000); // Aumentado a 7 seg
        return () => clearTimeout(timer); // Descomenta si usas cleanup
    }, []); // <-- Array de dependencias vacío, la función no cambia

    useEffect(() => {
        if (userProfile?.donations) {
            setPaypalEmail(userProfile.donations.paypalEmail || '');
        }
    }, [userProfile]);


    

    const handlePayPalConnect = async () => {
        setIsConnectingPayPal(true);
        showFeedback('Redirigiendo a PayPal...', 'success');
        try {
            const functions = getFunctions();
            // Asegúrate de que el nombre coincida con tu backend
            const generateLink = httpsCallable(functions, 'generatePayPalSignupLink'); 
            const result = await generateLink(); // No necesita data, usa el contexto

            if (result.data.url) {
                // Redirige al usuario a la URL de onboarding de PayPal
                window.location.href = result.data.url;
            } else {
                throw new Error("No se recibió la URL de PayPal.");
            }
        } catch (error) {
            console.error("Error al generar URL de PayPal:", error);
            showFeedback('Error al iniciar conexión con PayPal.', 'error');
            setIsConnectingPayPal(false); // Restablece el botón
        }
    };

   useEffect(() => {
        const sellerMerchantIdFromUrl = searchParams.get('merchantIdInPayPal');

        // Solo actúa si:
        // 1. Hay merchantId
        // 2. Tenemos perfil
        // 3. NO está ya conectado
        // 4. NO estamos ya verificando (¡NUEVA CONDICIÓN!)
        if (sellerMerchantIdFromUrl && userProfile && userProfile.donations?.provider !== 'paypal' && !isVerifyingPayPal) {

            // Define la función de verificación DENTRO del useEffect
            const verifyOnboarding = async (merchantId) => {
                setIsVerifyingPayPal(true); // <-- Marca que estamos verificando
                showFeedback('Verificando tu cuenta de PayPal...', 'success');
                try {
                    const functions = getFunctions();
                    const checkStatus = httpsCallable(functions, 'checkSellerOnboardingStatus');
                    const result = await checkStatus({ sellerMerchantIdFromUrl: merchantId });

                    if (result.data.status === 'COMPLETED') {
                        showFeedback(`¡Cuenta PayPal vinculada! Email: ${result.data.email}`, 'success');
                        // No necesitas hacer nada más, el onSnapshot de App.js actualizará userProfile
                    } else if (result.data.status === 'PENDING') {
                        showFeedback('Casi listo. Por favor, revisa tu email y completa los pasos pendientes en PayPal.', 'error');
                    } else {
                         showFeedback('No se pudo verificar el estado con PayPal. Intenta conectar de nuevo.', 'error');
                    }
                    
                    // Limpia la URL DESPUÉS de verificar
                    navigate('/settings', { replace: true });
                } catch (error) {
                    console.error("Error al verificar la vinculación con PayPal:", error);
                    // Muestra el mensaje de error específico de Firebase si existe
                    const errorMessage = error.message || 'Hubo un error al verificar tu cuenta de PayPal.';
                    showFeedback(errorMessage, 'error');
                    navigate('/settings', { replace: true });
                } finally {
                     setIsVerifyingPayPal(false); // <-- Marca que hemos terminado de verificar
                     // ¡No llames a showFeedback aquí! Ya se llamó en try/catch.
                }
            };

            verifyOnboarding(sellerMerchantIdFromUrl);
        }
        // La dependencia userProfile asegura que esto corra solo cuando el perfil esté listo
    }, [searchParams, userProfile, navigate, showFeedback, isVerifyingPayPal]);

    useEffect(() => {
        // Verifica si venimos de regreso de Stripe
        const stripeReturn = searchParams.get('stripe_return');
        if (stripeReturn === 'true' && userProfile?.donations?.provider === 'stripe') {
            // Muestra un mensaje de éxito
            showFeedback('¡Cuenta de Stripe vinculada correctamente!', 'success');
            // Limpia la URL
            navigate('/settings', { replace: true });
        }
        // No necesitas un 'else', onSnapshot se encarga de actualizar la UI
    }, [searchParams, userProfile, navigate, showFeedback]);

    // --- LÓGICA PARA CAMBIAR CONTRASEÑA ---
    const handlePasswordReset = async () => {
        if (!auth.currentUser) return;
        try {
            await sendPasswordResetEmail(auth, auth.currentUser.email);
            showFeedback('Se ha enviado un correo para restablecer tu contraseña.', 'success');
        } catch (error) {
            console.error("Error sending password reset email:", error);
            showFeedback('Error al enviar el correo. Inténtalo de nuevo.', 'error');
        }
    };

    // --- LÓGICA PARA CAMBIAR EMAIL ---
    const handleChangeEmail = async (e) => {
        e.preventDefault();
        if (!newEmail || !currentPassword) {
            showFeedback('Por favor, completa todos los campos para cambiar el correo.', 'error');
            return;
        }

        const user = auth.currentUser;
        const credential = EmailAuthProvider.credential(user.email, currentPassword);

        try {
            // 1. Re-autenticar al usuario
            await reauthenticateWithCredential(user, credential);

            // 2. Actualizar el email en Firebase Auth
            await updateEmail(user, newEmail);

            // 3. Actualizar el email en Firestore
            const userDocRef = doc(db, "users", user.uid);
            await updateDoc(userDocRef, { email: newEmail });
            
            showFeedback('¡Correo electrónico actualizado con éxito!', 'success');
            setNewEmail('');
            setCurrentPassword('');

        } catch (error) {
            console.error("Error updating email:", error);
            if (error.code === 'auth/wrong-password') {
                showFeedback('La contraseña es incorrecta.', 'error');
            } else if (error.code === 'auth/email-already-in-use') {
                showFeedback('El nuevo correo ya está en uso por otra cuenta.', 'error');
            } else {
                showFeedback('Ocurrió un error. Inténtalo de nuevo.', 'error');
            }
        }
    };

    const handleStripeConnect = async () => {
        setIsConnecting(true);
        try {
            // Prepara la llamada a la Cloud Function
            const { getFunctions, httpsCallable } = await import('firebase/functions');
            const functions = getFunctions();
            const createStripeConnectAccount = httpsCallable(functions, 'createStripeConnectAccount');
            
            const result = await createStripeConnectAccount();
            
            // Redirige al usuario a la URL de onboarding de Stripe
            const { url } = result.data;
            window.location.href = url;

        } catch (error) {
            console.error("Error al iniciar la conexión con Stripe:", error);
            alert("Error al conectar con Stripe: " + error.message);
            setIsConnecting(false);
        }
    };


        const handleSubscribePro = async () => {
        setIsLoadingPro(true);
        try {
            const functions = getFunctions();
            const createSession = httpsCallable(functions, 'createProCheckoutSession');
            const PRICE_ID = "price_1SZ0PbRDgzLGjOjK0LxbLIuo"; 

            const { data } = await createSession({ 
                priceId: PRICE_ID,
                successUrl: window.location.origin + '/dashboard',
                cancelUrl: window.location.href
            });
            
            window.location.assign(data.url);
        } catch (error) {
            console.error("Error suscripción:", error);
            alert("Error al iniciar pago: " + error.message);
            setIsLoadingPro(false);
        }
    };

    const handleStripeDisconnect = async () => {
        if (!window.confirm("¿Estás seguro de que quieres desvincular tu cuenta de Stripe? Ya no podrás recibir donaciones.")) {
            return;
        }
        setIsConfirmDisconnectModalOpen(false);
        setIsDisconnecting(true);
        try {
            const { getFunctions, httpsCallable } = await import('firebase/functions');
            const functions = getFunctions();
            const disconnectStripe = httpsCallable(functions, 'disconnectStripeAccount');
            await disconnectStripe();
            showFeedback('Cuenta de Stripe desvinculada correctamente.', 'success');
        } catch (error) {
            console.error("Error al desvincular Stripe:", error);
            showFeedback(error.message, 'error');
        } finally {
            setIsDisconnecting(false);
        }
    };

const handleDeleteAccount = async () => {
    const isGoogleProvider = auth.currentUser?.providerData[0]?.providerId === 'google.com';

    const confirmation = prompt("Esta acción es irreversible y borrará TODO tu contenido (historias, capítulos, comentarios). Escribe 'ELIMINAR' para confirmar.");
    if (confirmation !== 'ELIMINAR') {
        showFeedback('La eliminación de la cuenta fue cancelada.', 'success');
        return;
    }

    const user = auth.currentUser;

    try {
        // --- PASO DE RE-AUTENTICACIÓN ---
        if (isGoogleProvider) {
            const provider = new GoogleAuthProvider();
            await reauthenticateWithPopup(user, provider);
        } else {
            const password = prompt("Por seguridad, por favor, introduce tu contraseña para continuar.");
            if (!password) {
                showFeedback('Se requiere contraseña para eliminar la cuenta.', 'error');
                return;
            }
            const credential = EmailAuthProvider.credential(user.email, password);
            await reauthenticateWithCredential(user, credential);
        }

        showFeedback('Procesando eliminación... Esto puede tardar. No cierres la página.', 'success');

        // --- INICIA LÓGICA DE BORRADO EN CASCADA ---
        const userId = user.uid;
        const allBatches = [];
        let currentBatch = writeBatch(db);
        let operationCount = 0;

        const commitBatchIfNeeded = () => {
            if (operationCount >= 490) {
                allBatches.push(currentBatch.commit());
                currentBatch = writeBatch(db);
                operationCount = 0;
            }
        };

        // ++ NUEVO PASO: ACTUALIZAR SEGUIDORES Y SEGUIDOS ++
        // 1. Obtener la lista de usuarios que el usuario actual SIGUE.
        const followingQuery = query(collection(db, `users/${userId}/following`));
        const followingSnapshot = await getDocs(followingQuery);
        for (const docSnapshot of followingSnapshot.docs) {
            const followedUserId = docSnapshot.id;
            const followedUserRef = doc(db, 'users', followedUserId);
            // Restar 1 al contador de seguidores del usuario que seguía
            currentBatch.update(followedUserRef, { followerCount: increment(-1) });
            // Borrar el documento de la subcolección de seguidores del otro usuario
            currentBatch.delete(doc(db, `users/${followedUserId}/followers/${userId}`));
            operationCount += 2;
            commitBatchIfNeeded();
        }

        // 2. Obtener la lista de usuarios que SIGUEN al usuario actual.
        const followersQuery = query(collection(db, `users/${userId}/followers`));
        const followersSnapshot = await getDocs(followersQuery);
        for (const docSnapshot of followersSnapshot.docs) {
            const followerUserId = docSnapshot.id;
            const followerUserRef = doc(db, 'users', followerUserId);
            // Restar 1 al contador de seguidos del usuario que lo seguía
            currentBatch.update(followerUserRef, { followingCount: increment(-1) });
            // Borrar el documento de la subcolección de seguidos del otro usuario
            currentBatch.delete(doc(db, `users/${followerUserId}/following/${userId}`));
            operationCount += 2;
            commitBatchIfNeeded();
        }
        // ++ FIN DEL NUEVO PASO ++

        // 3. OBTENER Y AÑADIR TODAS LAS HISTORIAS Y CAPÍTULOS
        const storiesQuery = query(collection(db, 'stories'), where('authorId', '==', userId));
        const storiesSnapshot = await getDocs(storiesQuery);
        for (const storyDoc of storiesSnapshot.docs) {
            currentBatch.delete(storyDoc.ref);
            operationCount++;
            commitBatchIfNeeded();

            const chaptersQuery = query(collection(db, 'chapters'), where('storyId', '==', storyDoc.id));
            const chaptersSnapshot = await getDocs(chaptersQuery);
            for (const chapterDoc of chaptersSnapshot.docs) {
                currentBatch.delete(chapterDoc.ref);
                operationCount++;
                commitBatchIfNeeded();
            }
        }

        // 4. OBTENER Y AÑADIR TODOS LOS COMENTARIOS HECHOS POR EL USUARIO
        const profileCommentsQuery = query(collection(db, 'profileComments'), where('commenterId', '==', userId));
        const chapterCommentsQuery = query(collection(db, 'chapterComments'), where('commenterId', '==', userId));
        
        const [profileCommentsSnapshot, chapterCommentsSnapshot] = await Promise.all([
            getDocs(profileCommentsQuery),
            getDocs(chapterCommentsQuery)
        ]);

        profileCommentsSnapshot.forEach(doc => {
            currentBatch.delete(doc.ref);
            operationCount++;
            commitBatchIfNeeded();
        });

        chapterCommentsSnapshot.forEach(doc => {
            currentBatch.delete(doc.ref);
            operationCount++;
            commitBatchIfNeeded();
        });

        // 5. AÑADIR LOS DOCUMENTOS PRINCIPALES DEL USUARIO
        const userDocRef = doc(db, "users", userId);
        const usernameDocRef = doc(db, "usernames", userProfile.username);
        currentBatch.delete(userDocRef);
        currentBatch.delete(usernameDocRef);
        
        // 6. EJECUTAR TODOS LOS BATCHES
        allBatches.push(currentBatch.commit());
        await Promise.all(allBatches);
        console.log("Todos los documentos de Firestore, incluyendo follows, han sido eliminados.");

        // 7. ¡ÚLTIMO PASO! Borrar el usuario de Firebase Auth.
        await deleteUser(user);
        
        alert("Tu cuenta y todos tus datos han sido eliminados permanentemente.");
        navigate('/');

    } catch (error) {
        console.error("Error durante el proceso de eliminación:", error);
        if (error.code === 'auth/wrong-password') {
            showFeedback('La contraseña es incorrecta. La cuenta no ha sido eliminada.', 'error');
        } else if (error.code === 'auth/popup-closed-by-user') {
            showFeedback('El proceso de eliminación fue cancelado.', 'success');
        } else {
            showFeedback('Ocurrió un error. No se pudo completar la eliminación.', 'error');
        }
    }
};

const handlePayPalDisconnect = async () => {
        // Pide confirmación al usuario
        if (!window.confirm("¿Estás seguro de que quieres desvincular tu cuenta de PayPal? Ya no podrás recibir donaciones a través de ella.")) {
            return; // No hace nada si cancela
        }

        setIsDisconnectingPayPal(true);
        showFeedback('Desvinculando cuenta de PayPal...', 'success');
        try {
            const functions = getFunctions();
            const disconnectPayPal = httpsCallable(functions, 'disconnectPayPalAccount');
            await disconnectPayPal(); // No necesita enviar datos, usa el contexto

            // ¡Éxito! El onSnapshot de App.js actualizará userProfile automáticamente
            // y la UI cambiará para mostrar el botón "Conectar".
            showFeedback('Cuenta de PayPal desvinculada correctamente.', 'success');
            // No es necesario limpiar Firestore localmente, onSnapshot lo hará.

        } catch (error) {
            console.error("Error al desvincular PayPal:", error);
            showFeedback(error.message || 'No se pudo desvincular la cuenta.', 'error');
        } finally {
            setIsDisconnectingPayPal(false); // Restablece el botón
        }
    };

const handleGoToStripeDashboard = async () => {
    setIsGeneratingLink(true);
    try {
        const functions = getFunctions();
        const createLoginLink = httpsCallable(functions, 'createStripeLoginLink');
        const result = await createLoginLink(); // No necesita enviar datos

        if (result.data.url) {
            // Redirige al usuario al dashboard de Stripe Express
            window.location.href = result.data.url;
        } else {
            throw new Error("No se recibió la URL del panel de Stripe.");
        }
    } catch (error) {
        console.error("Error al obtener el enlace del panel de Stripe:", error);
        showFeedback(error.message || 'No se pudo generar el enlace al panel de Stripe.', 'error');
        setIsGeneratingLink(false); // Restablece el botón si hay error
    }
    // No ponemos finally aquí, la redirección detiene la ejecución
};



    const isGoogleProvider = auth.currentUser?.providerData[0]?.providerId === 'google.com';

    return (
        <div className="max-w-4xl mx-auto p-4 sm:p-8 text-white">
            <h1 className="text-4xl font-bold mb-8 border-b border-gray-700 pb-4">Ajustes de la Cuenta</h1>
            
            {feedback.message && (
                <div className={`p-4 mb-6 rounded-md text-center ${feedback.type === 'success' ? 'bg-green-600' : 'bg-red-600'}`}>
                    {feedback.message}
                </div>
            )}

            {isVerifyingPayPal && (
           <div className="p-4 mb-6 rounded-md text-center bg-blue-600">
               Verificando tu cuenta de PayPal...
           </div>
            )}

            {/* Sección Cambiar Contraseña */}
            <div className="bg-gray-800 p-6 rounded-lg shadow-lg mb-8 settings_div">
                <h2 className="text-2xl font-semibold mb-4">Cambiar Contraseña</h2>
                <p className="text-gray-400 mb-4">
                    {isGoogleProvider 
                        ? "Iniciaste sesión con Google, por lo que no tienes una contraseña local en Typefiction." 
                        : "Para cambiar tu contraseña, te enviaremos un enlace seguro a tu correo electrónico."
                    }
                </p>
                {!isGoogleProvider && (
                    <button onClick={handlePasswordReset} className="px-5 py-2 font-bold text-white bg-indigo-600 rounded-md hover:bg-indigo-700">
                        Enviar correo de recuperación
                    </button>
                )}
            </div>

            {/* Sección Cambiar Correo Electrónico */}
<div className="bg-gray-800 p-6 rounded-lg shadow-lg mb-8 settings_div">
    <h2 className="text-2xl font-semibold mb-4">Correo Electrónico</h2>
    
    {isGoogleProvider ? (
        // ++ ESTO ES LO NUEVO PARA USUARIOS DE GOOGLE ++
        <div>
            <p className="text-gray-400 mb-2">Tu cuenta está vinculada a Google con el siguiente correo:</p>
            <p className="font-semibold text-indigo-400 text-lg mb-4">{userProfile.email}</p>
            <p className="text-sm text-gray-500">No puedes cambiar tu correo desde aquí porque está gestionado por tu cuenta de Google.</p>
        </div>
    ) : (
        // --- ESTO ES LO QUE YA TENÍAS PARA USUARIOS NORMALES ---
        <div>
            <p className="text-gray-400 mb-4">Tu correo actual es: <span className="font-semibold text-indigo-400">{userProfile.email}</span></p>
            <form onSubmit={handleChangeEmail} className="space-y-4">
                <div>
                    <label className="block text-sm font-medium text-gray-300">Nuevo Correo Electrónico</label>
                    <input type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} required className="mt-1 w-full max-w-sm px-4 py-2 text-white bg-gray-700 border border-gray-600 rounded-md"/>
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-300">Contraseña Actual (para confirmar)</label>
                    <input type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} required className="mt-1 w-full max-w-sm px-4 py-2 text-white bg-gray-700 border border-gray-600 rounded-md"/>
                </div>
                <button type="submit" className="px-5 py-2 font-bold text-white bg-indigo-600 rounded-md hover:bg-indigo-700">
                    Actualizar Correo
                </button>
            </form>
        </div>
    )}
</div>
    {/* Sección Notificaciones */}
<div className="bg-gray-800 p-6 rounded-lg shadow-lg mb-8 settings_div border border-gray-700">
    <h2 className="text-2xl font-semibold mb-4 text-white">Preferencias de Notificaciones</h2>
    <p className="text-gray-400 mb-6 text-sm">Elige qué avisos quieres recibir en la campanita.</p>
    
    <div className="space-y-4">
        {/* Toggle Likes */}
        <div className="flex items-center justify-between">
            <div>
                <p className="font-medium text-gray-200">Me gusta</p>
                <p className="text-xs text-gray-500">Cuando alguien da corazón a tus historias.</p>
            </div>
            <button 
                onClick={() => handleToggleSetting('pushLikes')}
                disabled={isSavingSettings}
                className={`w-12 h-6 rounded-full p-1 transition-colors duration-200 ease-in-out ${notifSettings.pushLikes ? 'bg-indigo-600' : 'bg-gray-600'}`}
            >
                <div className={`w-4 h-4 bg-white rounded-full shadow-md transform transition-transform duration-200 ease-in-out ${notifSettings.pushLikes ? 'translate-x-6' : 'translate-x-0'}`}></div>
            </button>
        </div>

        {/* Toggle Comentarios */}
        <div className="flex items-center justify-between">
            <div>
                <p className="font-medium text-gray-200">Comentarios</p>
                <p className="text-xs text-gray-500">Respuestas en tus historias o perfil.</p>
            </div>
            <button 
                onClick={() => handleToggleSetting('pushComments')}
                disabled={isSavingSettings}
                className={`w-12 h-6 rounded-full p-1 transition-colors duration-200 ease-in-out ${notifSettings.pushComments ? 'bg-indigo-600' : 'bg-gray-600'}`}
            >
                <div className={`w-4 h-4 bg-white rounded-full shadow-md transform transition-transform duration-200 ease-in-out ${notifSettings.pushComments ? 'translate-x-6' : 'translate-x-0'}`}></div>
            </button>
        </div>

        {/* Toggle Seguidores */}
        <div className="flex items-center justify-between">
            <div>
                <p className="font-medium text-gray-200">Nuevos Seguidores</p>
                <p className="text-xs text-gray-500">Cuando alguien empieza a seguirte.</p>
            </div>
            <button 
                onClick={() => handleToggleSetting('pushFollows')}
                disabled={isSavingSettings}
                className={`w-12 h-6 rounded-full p-1 transition-colors duration-200 ease-in-out ${notifSettings.pushFollows ? 'bg-indigo-600' : 'bg-gray-600'}`}
            >
                <div className={`w-4 h-4 bg-white rounded-full shadow-md transform transition-transform duration-200 ease-in-out ${notifSettings.pushFollows ? 'translate-x-6' : 'translate-x-0'}`}></div>
            </button>
        </div>
        
        {/* Toggle Eventos (Opcional) */}
        <div className="flex items-center justify-between">
            <div>
                <p className="font-medium text-gray-200">Eventos y Anuncios</p>
                <p className="text-xs text-gray-500">Novedades importantes de Typefiction.</p>
            </div>
            <button 
                onClick={() => handleToggleSetting('pushSystemEvents')}
                disabled={isSavingSettings}
                className={`w-12 h-6 rounded-full p-1 transition-colors duration-200 ease-in-out ${notifSettings.pushSystemEvents ? 'bg-indigo-600' : 'bg-gray-600'}`}
            >
                <div className={`w-4 h-4 bg-white rounded-full shadow-md transform transition-transform duration-200 ease-in-out ${notifSettings.pushSystemEvents ? 'translate-x-6' : 'translate-x-0'}`}></div>
            </button>
        </div>
    </div>
</div>

    <div className="bg-gray-800 p-6 rounded-lg shadow-lg mb-8 settings_div">
                <h2 className="text-2xl font-semibold mb-4">Pagos y Donaciones</h2>
                <p className="text-gray-400 mb-6">Conecta un proveedor de pagos para permitir que tus lectores te envíen donaciones. Solo puedes tener un proveedor activo a la vez.</p>
                
                {/* --- CASO 1: NINGÚN PROVEEDOR CONECTADO --- */}
                {/* Muestra ambas opciones si no hay ningún proveedor activo */}
                {!userProfile?.donations?.provider && (
                    <div className="space-y-6">
                        {/* Opción Stripe */}
                        <div>
                            <h3 className="text-lg font-semibold text-white">Stripe</h3>
                            <p className="text-gray-400 mb-3 text-sm">El estándar de la industria para pagos. Permite donaciones con tarjeta de crédito/débito. (Recomendado)</p>
                            <button
                                onClick={handleStripeConnect}
                                disabled={isConnecting || isConnectingPayPal || isVerifyingPayPal}
                                className="inline-flex items-center px-5 py-2 font-bold text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50"
                            >
                                {isConnecting ? 'Conectando...' : 'Conectar con Stripe'}
                            </button>
                        </div>

                        <div className="border-t border-gray-700"></div>

                        {/* Opción PayPal */}
                        <div>
                            <h3 className="text-lg font-semibold text-white">PayPal</h3>
                            <p className="text-gray-400 mb-3 text-sm">Permite donaciones usando la plataforma de PayPal.</p>
                            
                            {/* Maneja el caso especial de "Pendiente" de PayPal */}
                            {userProfile?.donations?.paypalOnboardingStatus === 'PENDING' ? (
                                <p className="text-yellow-400 mb-4">
                                    Vinculación pendiente. Revisa tu email o tu cuenta de PayPal para completar los pasos requeridos.
                                </p>
                            ) : (
                                <button 
                                    onClick={handlePayPalConnect}
                                    disabled={isConnecting || isConnectingPayPal || isVerifyingPayPal}
                                    className="inline-flex items-center px-5 py-2 font-bold text-white bg-blue-500 rounded-md hover:bg-blue-600 disabled:opacity-50"
                                >
                                    {isConnectingPayPal ? 'Redirigiendo...' : 'Conectar con PayPal'}
                                </button>
                            )}
                        </div>
                    </div>
                )}

                {/* --- CASO 2: STRIPE CONECTADO --- */}
                {userProfile?.donations?.provider === 'stripe' && (
                    <div>
                        <p className="text-green-400 mb-2">
                            ¡Tu cuenta está correctamente vinculada con Stripe! 🥳
                        </p>
                        <p className="text-gray-500 mb-4 text-sm">
                            {/* ID de Cuenta: {userProfile.donations.stripeAccountId} */}
                            Ahora puedes recibir donaciones con tarjeta.
                        </p>
                        <div className="flex flex-wrap gap-4 items-center">
                            <button 
                                onClick={() => setIsDonationConfigModalOpen(true)}
                                className="px-5 py-2 font-bold text-white bg-indigo-600 rounded-md hover:bg-indigo-700"
                                disabled={isDisconnecting || isGeneratingLink} // Deshabilita si se está haciendo otra acción
                            >
                                Configurar Widget
                            </button>

                             {/* ¡NUEVO! Botón Ir al Panel de Stripe */}
                            <button
                                onClick={handleGoToStripeDashboard}
                                disabled={isGeneratingLink || isDisconnecting}
                                className="px-5 py-2 font-bold text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
                            >
                                {isGeneratingLink ? 'Generando enlace...' : 'Ir al Panel de Stripe'}
                            </button>

                            {/* Botón Eliminar Vinculación (ahora abre modal) */}
                            <button
                                onClick={() => setIsConfirmDisconnectModalOpen(true)} // <-- Cambiado para abrir modal
                                disabled={isDisconnecting || isGeneratingLink} // Deshabilita si se está haciendo otra acción
                                className="px-5 py-2 font-bold text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50"
                            >
                                {/* Texto cambiado */}
                                Eliminar Vinculación Stripe 
                            </button>
                        </div>
                            <p className="text-xs text-gray-500 mt-3">
                            Usa el Panel de Stripe para ver tus saldos, gestionar transferencias bancarias y actualizar tu información fiscal.
                            </p>
                    </div>
                )}

                {/* --- CASO 3: PAYPAL CONECTADO --- */}
                {userProfile?.donations?.provider === 'paypal' && userProfile?.donations?.paypalOnboardingStatus === 'COMPLETED' && (
                    <div>
                        <p className="text-green-400 mb-2">
                            ¡Tu cuenta está correctamente vinculada con PayPal! 🥳
                        </p>
                        <p className="text-gray-500 mb-4 text-sm">
                            {userProfile.donations.paypalEmail ? 
                                `Email de vendedor: ${userProfile.donations.paypalEmail}` :
                                `ID de Vendedor vinculado: ${userProfile.donations.paypalMerchantId || '(No disponible)'}`
                            }
                        </p>
                        <div className="flex space-x-4">
                            <button 
                                onClick={() => setIsDonationConfigModalOpen(true)}
                                className="px-5 py-2 font-bold text-white bg-indigo-600 rounded-md hover:bg-indigo-700"
                            >
                                Configurar Widget
                            </button>
                            <button 
                                onClick={handlePayPalDisconnect}
                                disabled={isDisconnectingPayPal}
                                className="px-5 py-2 font-bold text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50"
                            >
                                {isDisconnectingPayPal ? 'Desvinculando...' : 'Desvincular PayPal'}
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Sección PRO */}
            <div className="bg-gradient-to-r from-indigo-900 to-purple-900 p-6 rounded-lg shadow-lg mb-8 border border-indigo-500/50">
                <div className="flex justify-between items-center">
                    <div>
                        <h2 className="text-2xl font-bold text-white mb-2">Typefiction PRO 👑</h2>
                        <p className="text-indigo-200">Desbloquea estadísticas, borradores ilimitados y programación de estrenos.</p>
                    </div>
                    
                    {!userProfile.isPro ? (
                        <button 
                            onClick={handleSubscribePro}
                            disabled={isLoadingPro}
                            className="px-6 py-3 bg-white text-indigo-900 font-bold rounded-full hover:bg-gray-100 transition-colors shadow-lg transform hover:scale-105"
                        >
                            {isLoadingPro ? 'Cargando...' : 'Suscribirse Ahora'}
                        </button>
                    ) : (
                        <div className="text-right">
                            <span className="block text-green-400 font-bold text-lg">PLAN ACTIVO ✅</span>
                            <Link to="/dashboard" className="text-sm text-white underline hover:text-gray-200">Ir al Dashboard</Link>
                        </div>
                    )}
                </div>
            </div>

            {/* Sección Eliminar Cuenta */}
            <div className="p-6 rounded-lg shadow-lg settings_div_peligro border border-red-500/50">
                <h2 className="text-2xl font-semibold text-red-400 mb-4">Zona de Peligro</h2>
                <p className="text-gray-400 mb-4">
                    Eliminar tu cuenta es una acción permanente e irreversible. Todo tu contenido, incluyendo historias, capítulos y comentarios, será borrado para siempre.
                </p>
                <button onClick={handleDeleteAccount} className="px-5 py-2 font-bold text-white bg-red-600 rounded-md hover:bg-red-700">
                    Eliminar mi cuenta permanentemente
                </button>
            </div>

            <DonationWidgetConfigModal 
                isOpen={isDonationConfigModalOpen}
                onClose={() => setIsDonationConfigModalOpen(false)}
                userProfile={userProfile}
            />

            <ConfirmStripeDisconnectModal 
                isOpen={isConfirmDisconnectModalOpen}
                onConfirm={handleStripeDisconnect} // Llama a la función que ya cierra el modal
                onCancel={() => setIsConfirmDisconnectModalOpen(false)}
                isDisconnecting={isDisconnecting} // Pasa el estado de carga
            />
            
        </div>
    );
};

// ==================================================================================
// 5. ONBOARDING & ROUTE PROTECTION
// ==================================================================================

const OnboardingStepProfile = ({ userProfile, onProfileSaved }) => {
    const [displayName, setDisplayName] = useState(userProfile.displayName || '');
    const [username, setUsername] = useState(userProfile.username || '');
    const [avatarFile, setAvatarFile] = useState(null);
    const [avatarPreview, setAvatarPreview] = useState(userProfile.avatarURL || '');
    const [isSaving, setIsSaving] = useState(false);
    const [usernameStatus, setUsernameStatus] = useState('idle'); // idle, checking, available, taken, invalid
    const [error, setError] = useState('');
    const usernameCheckTimeout = useRef(null);

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file && file.type.startsWith("image/")) {
            setAvatarFile(file);
            setAvatarPreview(URL.createObjectURL(file));
        }
    };

    const checkUsername = React.useCallback(async (value) => {
        if (value.length < 3) {
            setUsernameStatus('invalid');
            return;
        }
        if (value === userProfile.username) {
            setUsernameStatus('idle');
            return;
        }
        setUsernameStatus('checking');
        const usernameRef = doc(db, 'usernames', value);
        const usernameSnap = await getDoc(usernameRef);
        setUsernameStatus(usernameSnap.exists() ? 'taken' : 'available');
    }, [userProfile.username]);

    useEffect(() => {
        if (usernameCheckTimeout.current) {
            clearTimeout(usernameCheckTimeout.current);
        }
        if(username) {
            usernameCheckTimeout.current = setTimeout(() => {
                checkUsername(username);
            }, 500); // Debounce time
        }
    }, [username, checkUsername]);

    const handleSave = async () => {
        setError('');
        if (!displayName || !username) {
            setError('El nombre de perfil y de usuario son obligatorios.');
            return;
        }
        if (usernameStatus === 'taken' || usernameStatus === 'invalid') {
            setError('El nombre de usuario no es válido o ya está en uso.');
            return;
        }
        setIsSaving(true);
        let avatarURL = userProfile.avatarURL;
        if (avatarFile) {
            try {
                const avatarRef = ref(storage, `avatars/${userProfile.uid}/${Date.now()}_${avatarFile.name}`);
                const snapshot = await uploadBytes(avatarRef, avatarFile);
                avatarURL = await getDownloadURL(snapshot.ref);
            } catch (uploadError) {
                console.error("Error uploading avatar:", uploadError);
                setError("No se pudo subir la imagen. Inténtalo de nuevo.");
                setIsSaving(false);
                return;
            }
        }
        
        const batch = writeBatch(db);
        const userDocRef = doc(db, "users", userProfile.uid);
        batch.update(userDocRef, { displayName, username, avatarURL });
        
        if(username !== userProfile.username) {
            const oldUsernameRef = doc(db, "usernames", userProfile.username);
            const newUsernameRef = doc(db, "usernames", username);
            if (userProfile.username) batch.delete(oldUsernameRef);
            batch.set(newUsernameRef, { userId: userProfile.uid });
        }

        try {
            await batch.commit();
            onProfileSaved();
        } catch(e) {
            console.error(e);
            setError("No se pudo guardar el perfil. Inténtalo de nuevo.");
            setIsSaving(false);
        }
    };

    const getUsernameFeedback = () => {
        switch(usernameStatus) {
            case 'checking': return <p className="text-xs text-yellow-400 mt-1">Comprobando...</p>;
            case 'available': return <p className="text-xs text-green-400 mt-1">¡Disponible!</p>;
            case 'taken': return <p className="text-xs text-red-400 mt-1">Este nombre ya está en uso.</p>;
            case 'invalid': return <p className="text-xs text-red-400 mt-1">Debe tener al menos 3 caracteres.</p>;
            default: return null;
        }
    }

    return (
        <div className="w-full max-w-md p-8 space-y-6 bg-gray-800 rounded-xl shadow-lg CONTAINER_LOGIN">
            <h2 className="text-3xl font-bold text-center text-white">Crea tu Perfil</h2>
            <p className="text-center text-gray-400">Elige cómo te verán los demás.</p>
            <div className="flex flex-col items-center space-y-4">
                <div className="relative">
                    <img src={avatarPreview || `https://placehold.co/128x128/374151/E5E7EB?text=${displayName ? displayName.charAt(0) : '?'}`} alt="Avatar preview" className="w-32 h-32 rounded-full object-cover border-4 border-gray-700"/>
                    <label htmlFor="avatar-upload" className="absolute bottom-0 right-0 bg-indigo-600 rounded-full p-2 cursor-pointer hover:bg-indigo-700">
                         <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.5L14.732 3.732z"></path></svg>
                        <input id="avatar-upload" type="file" onChange={handleFileChange} accept="image/png, image/jpeg" className="hidden"/>
                    </label>
                </div>
            </div>
            <div className="space-y-4">
                <div>
                    <label className="block text-sm font-medium text-gray-300">Nombre de Perfil</label>
                    <input type="text" value={displayName} onChange={(e) => setDisplayName(e.target.value)} className="mt-1 w-full px-4 py-2 text-white bg-gray-700 border border-gray-600 rounded-md"/>
                </div>
                 <div>
                    <label className="block text-sm font-medium text-gray-300">Nombre de Usuario (@)</label>
                    <input type="text" value={username} onChange={(e) => setUsername(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))} className="mt-1 w-full px-4 py-2 text-white bg-gray-700 border border-gray-600 rounded-md"/>
                    {getUsernameFeedback()}
                </div>
            </div>
            {error && <p className="text-red-500 text-sm text-center">{error}</p>}
            <button onClick={handleSave} disabled={isSaving || usernameStatus === 'checking' || usernameStatus === 'taken'} className="w-full px-4 py-2 font-bold text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed">
                {isSaving ? 'Guardando...' : 'Siguiente'}
            </button>
        </div>
    );
};

const OnboardingStepInterests = ({ userProfile, onInterestsSaved }) => {
    const PREDEFINED_INTERESTS = ['Romance', 'Fantasía', 'Ciencia Ficción', 'Misterio', 'Terror', 'Aventura', 'Fanfiction', 'Poesía', 'Humor', 'Drama', 'Acción', 'Slice of Life'];
    const [selectedInterests, setSelectedInterests] = useState([]);
    const [isSaving, setIsSaving] = useState(false);

    const handleToggleInterest = (interest) => {
        setSelectedInterests(prev => 
            prev.includes(interest) ? prev.filter(i => i !== interest) : [...prev, interest]
        );
    };

    const handleFinish = async () => {
        if (selectedInterests.length < 3) {
            alert("Por favor, selecciona al menos 3 intereses.");
            return;
        }
        setIsSaving(true);
        const userDocRef = doc(db, "users", userProfile.uid);
        try {
            await updateDoc(userDocRef, {
                interests: selectedInterests,
            });
            onInterestsSaved(); 
        } catch(e) {
            console.error("Error saving interests:", e);
            alert("No se pudieron guardar tus intereses. Inténtalo de nuevo.");
            setIsSaving(false);
        }
    };
    
    return(
        <div className="w-full max-w-2xl p-8 space-y-6 bg-gray-800 rounded-xl shadow-lg CONTAINER_LOGIN">
             <h2 className="text-3xl font-bold text-center text-white">¿Qué te gusta leer?</h2>
             <p className="text-center text-gray-400">Selecciona al menos 3. Esto nos ayudará a recomendarte historias.</p>
             <div className="grid grid-cols-2 md:grid-cols-4 gap-4 py-4 ">
                {PREDEFINED_INTERESTS.map(interest => {
                    const isSelected = selectedInterests.includes(interest);
                    return (
                        <button 
                            key={interest} 
                            onClick={() => handleToggleInterest(interest)}
                            className={`p-4  text-center font-semibold transition-all duration-200 MINI_CARDS ${
                                isSelected 
                                ? 'bg-blue text-white ring-2 ring-indigo-700' 
                                : ' hover:bg-gray-600'
                            }`}
                        >
                            {interest}
                        </button>
                    )
                })}
             </div>
             <button onClick={handleFinish} disabled={isSaving || selectedInterests.length < 3} className="w-full px-4 py-3 font-bold text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed">
                {isSaving ? 'Guardando...' : 'Siguiente'}
            </button>
        </div>
    );
};

const OnboardingStepFinal = ({ userProfile }) => {
    const [isCompleting, setIsCompleting] = useState(false);
    const navigate = useNavigate();

    const handleComplete = async () => {
        setIsCompleting(true);
        const userDocRef = doc(db, "users", userProfile.uid);
        try {
            await updateDoc(userDocRef, { onboardingComplete: true });
            navigate('/');
        } catch (e) {
            console.error("Error completing onboarding:", e);
            alert("Hubo un error al finalizar. Por favor, intenta recargar la página.");
            setIsCompleting(false);
        }
    };
    
    return(
        <div className="w-full max-w-lg p-8 space-y-6 bg-gray-800 rounded-xl shadow-lg text-center CONTAINER_LOGIN">
             <h2 className="text-3xl font-bold text-white">¡Todo listo!</h2>
             <p className="text-gray-300">Hemos enviado un correo a <span className="font-bold text-indigo-400">{userProfile.email}</span>. <br/>Verifica tu cuenta cuando puedas para mantenerla segura.</p>
             <p className="text-gray-400 text-sm pt-4">Mientras tanto, ya puedes empezar a explorar.</p>
             <button onClick={handleComplete} disabled={isCompleting} className="w-full px-4 py-3 font-bold text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-50">
                {isCompleting ? 'Redirigiendo...' : 'Empezar a Explorar'}
            </button>
        </div>
    );
}

const OnboardingPage = ({ userProfile }) => {
    const [step, setStep] = useState(1);
    if (!userProfile) {
        return <div className="flex items-center justify-center min-h-screen bg-gray-900 text-white">Cargando perfil...</div>;
    }
    return (
        <div className="flex items-center justify-center min-h-screen bg-black text-white p-4">
           {step === 1 && <OnboardingStepProfile userProfile={userProfile} onProfileSaved={() => setStep(2)} />}
           {step === 2 && <OnboardingStepInterests userProfile={userProfile} onInterestsSaved={() => setStep(3)} />}
           {step === 3 && <OnboardingStepFinal userProfile={userProfile} />}
        </div>
    );
};

const ProtectedRouteLayout = ({ user, userProfile }) => {
    // If auth state is not yet determined, show nothing or a loader
    if (user === undefined) {
        return <div className="flex items-center justify-center min-h-screen bg-gray-900 text-white">Cargando...</div>;
    }

    // If there is no user, redirect to login
    if (!user) {
        return <Navigate to="/login" />;
    }

    // If user object exists, but we are still fetching their profile from firestore
    if (!userProfile) {
        return <div className="flex items-center justify-center min-h-screen bg-gray-900 text-white">Cargando perfil...</div>;
    }
    
    const hasCompletedOnboarding = userProfile.onboardingComplete !== false;

    if (!hasCompletedOnboarding) {
        return <Navigate to="/onboarding" />;
    }

    // All checks passed, render the requested child route
    return <Outlet />;
};

// --- SEGURIDAD: RUTA SOLO PARA EL ADMIN ---
const AdminRoute = ({ userProfile, children }) => {
    // ⚠️ REEMPLAZA ESTO CON TU UID REAL DE FIREBASE (Lo encuentras en Authentication)
    const ADMIN_UID = "uHdM30L3hTZpTMEz2tLpcb8yo4K3"; 
    
    // Verificación doble: UID específico O campo isAdmin en base de datos
    const isAuthorized = userProfile && (userProfile.uid === ADMIN_UID || userProfile.isAdmin === true);

    if (!isAuthorized) {
        return <Navigate to="/" replace />;
    }
    return children;
};

// ==================================================================================
// 6. ADMIN MODERATION COMPONENT (FASE 4 - ACTUALIZADO)
// ==================================================================================
const AdminModerationPage = ({ userProfile }) => {
    // --- ESTADOS DE PESTAÑAS ---
    const [activeTab, setActiveTab] = useState('pending'); // 'pending' | 'search' | 'logs'
    
    // --- ESTADOS DE DATOS ---
    const [pendingChars, setPendingChars] = useState([]);
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isProcessing, setIsProcessing] = useState(false); // Spinner global para acciones

    // --- ESTADOS DE FUSIÓN (MERGE) ---
    const [activeMergeCharId, setActiveMergeCharId] = useState(null); // ID del char que estamos intentando fusionar
    const [mergeSearchInput, setMergeSearchInput] = useState(''); // Lo que escribes en el input de fusión
    const [mergeSuggestions, setMergeSuggestions] = useState([]); // Resultados de Typesense para fusión
    const [mergeTarget, setMergeTarget] = useState(null); // El personaje DESTINO seleccionado

    // --- ESTADOS DE BUSCADOR GLOBAL ---
    const [globalQuery, setGlobalQuery] = useState('');
    const [globalResults, setGlobalResults] = useState([]);
    const [isSearchingGlobal, setIsSearchingGlobal] = useState(false);

    // --- ESTADOS DE EDICIÓN (MODAL) ---
    const [editingChar, setEditingChar] = useState(null); 

    // 1. CARGA INICIAL
    useEffect(() => {
        const fetchInitialData = async () => {
            // Pendientes
            const qPending = query(collection(db, 'characters'), where('status', '==', 'pending'));
            const snapPending = await getDocs(qPending);
            setPendingChars(snapPending.docs.map(d => ({ id: d.id, ...d.data() })));

            // Logs Recientes
            const qLogs = query(collection(db, 'moderationLogs'), orderBy('timestamp', 'desc'), limit(20));
            const snapLogs = await getDocs(qLogs);
            setLogs(snapLogs.docs.map(d => ({ id: d.id, ...d.data() })));
            
            setLoading(false);
        };
        fetchInitialData();
    }, [userProfile]);

    // --- LOGICA DE FUSIÓN (MERGE SEARCH) ---
    // Busca candidatos para fusionar mientras escribes
    useEffect(() => {
        if (!mergeSearchInput || !activeMergeCharId) {
            setMergeSuggestions([]);
            return;
        }
        const timer = setTimeout(async () => {
            try {
                // Buscamos solo aprobados preferiblemente, pero buscamos en todo
                const res = await typesenseClient.collections('characters').documents().search({
                    q: mergeSearchInput, 
                    query_by: 'name', 
                    // filter_by: 'status:=approved', // Opcional: solo permitir fusionar hacia aprobados
                    per_page: 5
                });
                if (res.hits) setMergeSuggestions(res.hits.map(h => h.document));
            } catch (e) { console.error(e); }
        }, 300);
        return () => clearTimeout(timer);
    }, [mergeSearchInput, activeMergeCharId]);

    // --- HELPER: LOGS ---
    const addLog = async (action, charName, details = '') => {
        const logData = {
            action, charName, details, timestamp: serverTimestamp(), adminId: userProfile.uid
        };
        await addDoc(collection(db, 'moderationLogs'), logData);
        setLogs(prev => [{ ...logData, id: Date.now(), timestamp: { seconds: Date.now()/1000 } }, ...prev]);
    };

    // --- ACCIONES PRINCIPALES ---

    const handleApprove = async (char) => {
        setIsProcessing(true);
        try {
            await updateDoc(doc(db, 'characters', char.id), { status: 'approved' });
            await addLog('approved', char.name);
            setPendingChars(prev => prev.filter(c => c.id !== char.id));
        } catch (e) { alert("Error: " + e.message); }
        setIsProcessing(false);
    };

    const handleReject = async (char) => {
        if (!window.confirm(`¿RECHAZAR y ELIMINAR "${char.name}" de todas las historias?`)) return;
        setIsProcessing(true);
        try {
            const batch = writeBatch(db);
            // 1. Buscar historias y quitar el tag
            const qStories = query(collection(db, 'stories'), where('mainCharacters', 'array-contains', char.name));
            const snapStories = await getDocs(qStories);
            snapStories.forEach(s => batch.update(s.ref, { mainCharacters: arrayRemove(char.name) }));
            // 2. Borrar char
            batch.delete(doc(db, 'characters', char.id));
            
            await batch.commit();
            await addLog('rejected', char.name, `Removido de ${snapStories.size} historias.`);
            setPendingChars(prev => prev.filter(c => c.id !== char.id));
            setGlobalResults(prev => prev.filter(c => c.id !== char.id));
        } catch (e) { alert("Error: " + e.message); }
        setIsProcessing(false);
    };

    const handleConfirmMerge = async (badChar) => {
        if (!mergeTarget) return alert("Selecciona un destino para fusionar.");
        if (badChar.id === mergeTarget.id) return alert("No puedes fusionar un personaje consigo mismo.");
        
        setIsProcessing(true);
        try {
            const batch = writeBatch(db);
            const badName = badChar.name;
            const goodName = mergeTarget.name;

            // 1. Actualizar historias: Cambiar BadName por GoodName
            const qStories = query(collection(db, 'stories'), where('mainCharacters', 'array-contains', badName));
            const snapStories = await getDocs(qStories);

            snapStories.forEach(sDoc => {
                const data = sDoc.data();
                // Reemplazar y asegurarnos de que sea único (Set)
                const updatedChars = [...new Set(data.mainCharacters.map(c => c === badName ? goodName : c))];
                batch.update(sDoc.ref, { mainCharacters: updatedChars });
            });

            // 2. Sumar el usageCount al personaje destino (Opcional, pero bueno para stats)
            // batch.update(doc(db, 'characters', mergeTarget.id), { usageCount: increment(badChar.usageCount || 0) });

            // 3. Borrar el personaje malo
            batch.delete(doc(db, 'characters', badChar.id));

            await batch.commit();
            await addLog('merged', badName, `Fusionado hacia -> ${goodName}. Historias afectadas: ${snapStories.size}`);

            // Limpieza UI
            setPendingChars(prev => prev.filter(c => c.id !== badChar.id));
            setGlobalResults(prev => prev.filter(c => c.id !== badChar.id));
            setActiveMergeCharId(null);
            setMergeTarget(null);
            setMergeSearchInput('');
            alert("Fusión completada con éxito.");

        } catch (e) { alert("Error al fusionar: " + e.message); }
        setIsProcessing(false);
    };

    // --- ACCIONES EDICIÓN (MODAL) ---
    const handleSaveEdit = async (newName, newFile) => {
        if (!editingChar) return;
        setIsProcessing(true);
        try {
            const charRef = doc(db, 'characters', editingChar.id);
            const oldName = editingChar.name;
            let finalName = newName.trim();
            let finalAvatar = editingChar.avatarURL;

            // 1. Subir imagen si existe
            if (newFile) {
                const storageRef = ref(storage, `characters/${editingChar.id}/${Date.now()}_${newFile.name}`);
                const snap = await uploadBytes(storageRef, newFile);
                finalAvatar = await getDownloadURL(snap.ref);
            }

            // 2. Si cambia el nombre, actualizar historias (Batch)
            if (finalName !== oldName) {
                const batch = writeBatch(db);
                batch.update(charRef, { name: finalName, avatarURL: finalAvatar });
                
                const qStories = query(collection(db, 'stories'), where('mainCharacters', 'array-contains', oldName));
                const snapStories = await getDocs(qStories);
                snapStories.forEach(s => {
                    const newArr = s.data().mainCharacters.map(c => c === oldName ? finalName : c);
                    batch.update(s.ref, { mainCharacters: newArr });
                });
                await batch.commit();
                await addLog('edited', oldName, `Renombrado a ${finalName}`);
            } else {
                // Solo update simple
                await updateDoc(charRef, { avatarURL: finalAvatar });
                await addLog('edited', oldName, 'Foto/Datos actualizados');
            }

            setEditingChar(null);
            // Actualizar listas locales
            const updater = list => list.map(c => c.id === editingChar.id ? { ...c, name: finalName, avatarURL: finalAvatar } : c);
            setPendingChars(updater);
            setGlobalResults(updater);

        } catch (e) { alert("Error: " + e.message); }
        setIsProcessing(false);
    };

    // --- BUSCADOR GLOBAL ---
    useEffect(() => {
        if (!globalQuery) { setGlobalResults([]); return; }
        const timer = setTimeout(async () => {
            setIsSearchingGlobal(true);
            try {
                const res = await typesenseClient.collections('characters').documents().search({
                    q: globalQuery, query_by: 'name', per_page: 12
                });
                if(res.hits) setGlobalResults(res.hits.map(h => h.document));
            } catch(e) { console.error(e); }
            setIsSearchingGlobal(false);
        }, 500);
        return () => clearTimeout(timer);
    }, [globalQuery]);


    if (loading) return <div className="p-10 text-white text-center">Cargando panel de administración...</div>;

    return (
        <div className="min-h-screen bg-gray-900 text-white p-4 md:p-8 font-sans">
            <div className="max-w-6xl mx-auto">
                
                {/* HEADER & TABS */}
                <div className="flex flex-col md:flex-row justify-between items-center mb-8 border-b border-gray-700 pb-6 gap-4">
                    <div>
                        <h1 className="text-3xl font-bold text-yellow-400 flex items-center gap-2">🛡️ Panel de Moderación</h1>
                        <p className="text-gray-400 text-sm">Gestiona personajes y etiquetas para mantener la calidad.</p>
                    </div>
                    <div className="flex bg-gray-800 p-1 rounded-lg">
                        {['pending', 'search', 'logs'].map(tab => (
                            <button 
                                key={tab}
                                onClick={() => setActiveTab(tab)}
                                className={`px-4 py-2 rounded-md text-sm font-bold capitalize transition-colors ${activeTab === tab ? 'bg-indigo-600 text-white' : 'text-gray-400 hover:text-white'}`}
                            >
                                {tab === 'pending' ? `Pendientes (${pendingChars.length})` : tab === 'search' ? 'Buscador' : 'Historial'}
                            </button>
                        ))}
                    </div>
                </div>

                {/* --- TAB 1: PENDIENTES --- */}
                {activeTab === 'pending' && (
                    <div className="animate-fadeIn">
                        {pendingChars.length === 0 ? (
                            <div className="text-center py-12 bg-gray-800/50 rounded-xl border border-gray-700 border-dashed">
                                <p className="text-gray-500 text-lg">✨ Todo limpio. No hay solicitudes pendientes.</p>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 gap-4">
                                {pendingChars.map(char => (
                                    <div key={char.id} className="bg-gray-800 p-4 rounded-xl border border-gray-700 flex flex-col md:flex-row items-center justify-between gap-4 shadow-lg">
                                        
                                        {/* Info Personaje */}
                                        <div className="flex items-center gap-4 w-full md:w-auto">
                                            <img src={char.avatarURL || `https://placehold.co/100x100/333/FFF?text=${char.name.charAt(0)}`} className="w-16 h-16 rounded-full object-cover bg-gray-900 border border-gray-600" alt=""/>
                                            <div>
                                                <h3 className="font-bold text-xl text-white">{char.name}</h3>
                                                <div className="flex items-center gap-2 text-xs text-gray-400 mt-1">
                                                    <span className="bg-yellow-900/40 text-yellow-200 px-2 py-0.5 rounded border border-yellow-500/30">NUEVO</span>
                                                    <span>Usado en {char.usageCount} historias</span>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Acciones */}
                                        <div className="flex flex-col md:flex-row items-center gap-3 w-full md:w-auto">
                                            
                                            {/* ZONA DE MERGE (Desplegable) */}
                                            {activeMergeCharId === char.id ? (
                                                <div className="flex items-center gap-2 bg-gray-900 p-2 rounded-lg border border-indigo-500/50 w-full md:w-auto animate-fadeIn">
                                                    {mergeTarget ? (
                                                        // Paso 2: Confirmar Destino
                                                        <div className="flex items-center gap-3">
                                                            <div className="text-xs text-right">
                                                                <span className="block text-gray-500">Fusionar en:</span>
                                                                <span className="font-bold text-green-400">{mergeTarget.name}</span>
                                                            </div>
                                                            <button onClick={() => handleConfirmMerge(char)} disabled={isProcessing} className="bg-green-600 hover:bg-green-500 text-white px-3 py-1 rounded text-xs font-bold">CONFIRMAR</button>
                                                            <button onClick={() => setMergeTarget(null)} className="text-gray-400 hover:text-white px-2">✕</button>
                                                        </div>
                                                    ) : (
                                                        // Paso 1: Buscar Destino
                                                        <div className="relative">
                                                            <input 
                                                                autoFocus
                                                                type="text" 
                                                                placeholder="Buscar personaje correcto..." 
                                                                className="bg-transparent text-sm text-white focus:outline-none w-48 placeholder-gray-600"
                                                                value={mergeSearchInput}
                                                                onChange={(e) => setMergeSearchInput(e.target.value)}
                                                            />
                                                            {mergeSuggestions.length > 0 && (
                                                                <div className="absolute bottom-full left-0 w-64 bg-gray-800 border border-gray-600 rounded-md shadow-2xl mb-2 overflow-hidden z-50">
                                                                    {mergeSuggestions.map(s => (
                                                                        <button key={s.id} onClick={() => setMergeTarget(s)} className="w-full text-left px-3 py-2 text-xs hover:bg-indigo-600 text-gray-300 hover:text-white border-b border-gray-700 last:border-0 flex justify-between">
                                                                            <span>{s.name}</span>
                                                                            {s.status === 'approved' && <span>✓</span>}
                                                                        </button>
                                                                    ))}
                                                                </div>
                                                            )}
                                                            <button onClick={() => { setActiveMergeCharId(null); setMergeSearchInput(''); }} className="ml-2 text-gray-500 hover:text-white text-xs">Cancelar</button>
                                                        </div>
                                                    )}
                                                </div>
                                            ) : (
                                                <button onClick={() => setActiveMergeCharId(char.id)} disabled={isProcessing} className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-gray-200 rounded font-bold text-sm">
                                                    Fusionar...
                                                </button>
                                            )}

                                            <button onClick={() => setEditingChar(char)} disabled={isProcessing} className="px-4 py-2 bg-gray-700 hover:bg-white hover:text-black text-white rounded font-bold text-sm transition-colors">
                                                Editar / Foto
                                            </button>
                                            <button onClick={() => handleApprove(char)} disabled={isProcessing} className="px-4 py-2 bg-green-600 hover:bg-green-500 text-white rounded font-bold text-sm shadow-lg shadow-green-900/20">
                                                Aprobar
                                            </button>
                                            <button onClick={() => handleReject(char)} disabled={isProcessing} className="px-4 py-2 text-red-500 hover:bg-red-900/20 rounded font-bold text-sm">
                                                Rechazar
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {/* --- TAB 2: BUSCADOR GLOBAL (Para Editar/Borrar cualquier cosa) --- */}
                {activeTab === 'search' && (
                    <div className="animate-fadeIn">
                        <div className="relative mb-6">
                            <input 
                                type="text" 
                                value={globalQuery}
                                onChange={(e) => setGlobalQuery(e.target.value)}
                                placeholder="Buscar cualquier personaje para editar o eliminar..."
                                className="w-full bg-gray-800 border border-gray-600 rounded-xl px-6 py-4 text-white text-lg focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 placeholder-gray-500"
                            />
                            {isSearchingGlobal && <div className="absolute right-6 top-5 animate-spin h-5 w-5 border-2 border-indigo-500 border-t-transparent rounded-full"></div>}
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {globalResults.map(char => (
                                <div key={char.id} className="bg-gray-800 p-4 rounded-lg border border-gray-700/50 flex items-center justify-between hover:bg-gray-750 transition-colors">
                                    <div className="flex items-center gap-3">
                                        <img src={char.avatarURL || `https://placehold.co/100x100/333/FFF?text=${char.name.charAt(0)}`} className="w-10 h-10 rounded-full object-cover" alt=""/>
                                        <div>
                                            <p className="font-bold text-white">{char.name}</p>
                                            <div className="flex gap-2 text-xs">
                                                <span className={`px-1.5 rounded ${char.status === 'approved' ? 'bg-green-900 text-green-300' : 'bg-yellow-900 text-yellow-300'}`}>
                                                    {char.status === 'approved' ? 'Aprobado' : 'Pendiente'}
                                                </span>
                                                <span className="text-gray-500">Usos: {char.usageCount || 0}</span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex gap-2">
                                        <button onClick={() => setEditingChar(char)} className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded transition-colors" title="Editar">✏️</button>
                                        <button onClick={() => handleReject(char)} className="p-2 text-red-500 hover:text-red-400 hover:bg-red-900/20 rounded transition-colors" title="Eliminar Globalmente">🗑️</button>
                                    </div>
                                </div>
                            ))}
                        </div>
                        {globalQuery && globalResults.length === 0 && !isSearchingGlobal && <p className="text-center text-gray-500 mt-8">No se encontraron personajes.</p>}
                    </div>
                )}

                {/* --- TAB 3: LOGS --- */}
                {activeTab === 'logs' && (
                    <div className="animate-fadeIn space-y-2">
                        {logs.map(log => (
                            <div key={log.id} className="flex items-center gap-4 p-3 bg-gray-800 rounded border-l-4 border-gray-700"
                                style={{ borderColor: log.action === 'approved' ? '#10B981' : log.action === 'rejected' ? '#EF4444' : log.action === 'merged' ? '#6366F1' : '#F59E0B' }}
                            >
                                <div className="text-xl">
                                    {log.action === 'approved' && '✅'}
                                    {log.action === 'rejected' && '🗑️'}
                                    {log.action === 'merged' && 'twisted_rightwards_arrows'}
                                    {log.action === 'edited' && '✏️'}
                                </div>
                                <div className="flex-grow">
                                    <p className="text-sm text-gray-300">
                                        <span className="font-bold uppercase text-xs opacity-70 mr-2">{log.action}</span>
                                        {log.charName}
                                    </p>
                                    {log.details && <p className="text-xs text-gray-500">{log.details}</p>}
                                </div>
                                <span className="text-xs text-gray-600 font-mono whitespace-nowrap">
                                    {log.timestamp?.seconds ? new Date(log.timestamp.seconds * 1000).toLocaleTimeString() : ''}
                                </span>
                            </div>
                        ))}
                    </div>
                )}

            </div>

            {/* --- MODAL DE EDICIÓN (Global) --- */}
            {editingChar && (
                <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[200] p-4">
                    <div className="bg-gray-800 rounded-xl shadow-2xl w-full max-w-md p-6 border border-gray-600 relative">
                        <button onClick={() => setEditingChar(null)} className="absolute top-4 right-4 text-gray-500 hover:text-white">✕</button>
                        <h3 className="text-xl font-bold mb-6 text-white">Editar Personaje</h3>
                        
                        <div className="flex flex-col items-center mb-6">
                            <div className="w-24 h-24 bg-gray-900 rounded-full overflow-hidden border-2 border-indigo-500 relative group cursor-pointer">
                                <img id="previewEditImg" src={editingChar.avatarURL || `https://placehold.co/100x100/333/FFF?text=${editingChar.name.charAt(0)}`} className="w-full h-full object-cover" alt=""/>
                                <label className="absolute inset-0 bg-black/50 flex flex-col items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity text-xs font-bold">
                                    <span>CAMBIAR</span>
                                    <input type="file" accept="image/*" className="hidden" 
                                        onChange={(e) => {
                                            const file = e.target.files[0];
                                            if(file) {
                                                editingChar.newFile = file;
                                                document.getElementById('previewEditImg').src = URL.createObjectURL(file);
                                            }
                                        }} 
                                    />
                                </label>
                            </div>
                            <p className="text-xs text-gray-500 mt-2">Click en la imagen para subir nueva</p>
                        </div>

                        <div className="mb-6">
                            <label className="block text-xs font-bold text-gray-400 uppercase mb-1">Nombre (Corrección)</label>
                            <input type="text" id="editNameInput" defaultValue={editingChar.name} className="w-full bg-gray-900 border border-gray-600 rounded p-3 text-white focus:border-indigo-500 focus:outline-none"/>
                            <p className="text-xs text-yellow-500/80 mt-2 flex gap-1">
                                <span>⚠️</span> Si cambias el nombre, se actualizarán todas las historias asociadas.
                            </p>
                        </div>

                        <div className="flex justify-end gap-3 pt-4 border-t border-gray-700">
                            <button onClick={() => setEditingChar(null)} className="px-4 py-2 text-gray-300 hover:text-white font-medium">Cancelar</button>
                            <button 
                                onClick={() => {
                                    const val = document.getElementById('editNameInput').value;
                                    handleSaveEdit(val, editingChar.newFile);
                                }}
                                disabled={isProcessing}
                                className="px-6 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded font-bold shadow-lg"
                            >
                                {isProcessing ? 'Guardando...' : 'Guardar Cambios'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
};

// ==================================================================================
// 5. APP COMPONENT (Main Application Logic)
// ==================================================================================

function App() {
  const [user, setUser] = useState(undefined);
  const [userProfile, setUserProfile] = useState(null);
  const [isProfileLoading, setIsProfileLoading] = useState(true);

  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      if (!currentUser) {
          setIsProfileLoading(false); // Si no hay usuario, dejamos de cargar
      }
    });
    return () => unsubscribeAuth();
  }, []);

  useEffect(() => {
    if (user === null) { 
      setUserProfile(null);
      return;
    }
    if (user) { 
      const userDocRef = doc(db, "users", user.uid);
      const unsubscribeProfile = onSnapshot(userDocRef, (doc) => {
        if (doc.exists()) {
          // Aquí entra isPro automáticamente gracias al spread ...doc.data()
          setUserProfile({ uid: user.uid, ...doc.data() });
        } else {
          setUserProfile(null); 
        }
        setIsProfileLoading(false); // <--- ¡IMPORTANTE! Ya cargamos
      });
      return () => unsubscribeProfile();
    }
  }, [user]);

  return (
<PayPalScriptProvider options={initialPayPalOptions}>
    <BrowserRouter>
      <div className="text-white font-sans min-h-screen bg-gray-900 FONDITO">
            <Routes>
            {/* Rutas que no usan el layout principal */}
            <Route path="/login" element={user ? <Navigate to="/" /> : <AuthPage />} />
            <Route path="/onboarding" element={user ? <OnboardingPage userProfile={userProfile} /> : <Navigate to="/login" />} />
            
            {/* Rutas PÚBLICAS que usan la barra de navegación */}
            <Route element={<AppLayout userProfile={userProfile} />}>
                
                <Route path="/profile/:username" element={<ProfilePage loggedInUserProfile={userProfile} />} />
                <Route path="/notifications" element={<NotificationsPage userProfile={userProfile} />} />
                <Route path="/search" element={<SearchPage loggedInUserProfile={userProfile} />} />
                <Route path="/story/:storyId" element={<StoryPage loggedInUserProfile={userProfile}/>} />
                <Route path="/collection/:collectionId" element={<CollectionPage loggedInUserProfile={userProfile} />} />
                <Route path="/pro" element={<ProLandingPage userProfile={userProfile} />} />
            </Route>

            {/* 🔥 RUTAS PRO (EL NUEVO BLOQUE) 🔥 */}
          <Route element={<ProtectedRouteLayout user={user} userProfile={userProfile} />}>
              <Route element={<AppLayout userProfile={userProfile} />}>
                  
                  {/* Aquí envolvemos con ProRoute */}
                  <Route element={<ProRoute userProfile={userProfile} isLoading={isProfileLoading} />}>
                      <Route path="/dashboard" element={<DashboardPro userProfile={userProfile} />} />
                      {/* Futuras rutas PRO irán aquí (ej. /analytics, /scheduler) */}
                  </Route>

              </Route>
          </Route>

            {/* Ruta PÚBLICA de lectura (no usa el layout principal) */}
            <Route path="/story/:storyId/chapter/:chapterId" element={<ReadingPage loggedInUserProfile={userProfile} />} />
            
            {/* Rutas que SÍ necesitan protección */}
            <Route element={<ProtectedRouteLayout user={user} userProfile={userProfile} />}>
                <Route path="/write" element={<WritingPage userProfile={userProfile} />} />
                <Route path="/write/edit/:chapterId" element={<WritingPage userProfile={userProfile} />} />
            </Route>

            {/* Rutas que SÍ necesitan protección y tienen NAVBAR*/}
            <Route element={<ProtectedRouteLayout user={user} userProfile={userProfile} />}>
                <Route element={<AppLayout userProfile={userProfile} />}>
                        <Route path="/" element={<Dashboard userProfile={userProfile} />} />
                        <Route path="/settings" element={<AccountSettingsPage userProfile={userProfile} />} />
                        {/* --- RUTA SEGURA DE ADMIN --- */}
                        <Route 
                            path="/admin/moderation" 
                            element={
                                <AdminRoute userProfile={userProfile}>
                                    <AdminModerationPage userProfile={userProfile} />
                                </AdminRoute>
                            } 
                        />
                </Route>
            </Route>
            
            {/* Fallback Route */}
            <Route path="*" element={<Navigate to="/" />} />
            </Routes>
      </div>
    </BrowserRouter>
</PayPalScriptProvider>
  );
}


export default App;
